const en = {
  errors: {
    missingPermission:
      "You don't have permission for this action ({{permission}}).",
  },
  common: {
    scrollLeft: "Scroll left",
    scrollRight: "Scroll right",
    unsavedTitle: "Unsaved changes",
    unsavedDesc: "You have unsaved changes. Close without saving?",
    unsavedLeave: "Close",
    unsavedStay: "Stay",
    appName: "UnrealSoft",
    installBannerKicker: "PWA",
    installBannerTitle: "Install UnrealSoft",
    installBannerDesc: "Add the app to your home screen for quick access.",
    installBannerCta: "Install the app",
    restaurantSystem: "Restaurant management system",
    logout: "Logout",
    fullscreen: "Fullscreen",
    exitFullscreen: "Exit fullscreen",
    language: "Language",
    chooseLanguage: "Choose language",
    theme: "Theme",
    chooseTheme: "Choose theme",
    themeLight: "Light",
    themeDark: "Dark",
    themes: {
      orange: "Orange",
      green: "Green",
      sky: "Sky blue",
      cream: "Cream",
      violet: "Violet",
      silver: "Silver",
      red: "Red",
    },
    newVersionTitle: "New version available",
    newVersionMessage: "Please reload the app to get it.",
    newVersionReload: "Reload",
    moduleLoadFailed: "The module could not be loaded.",
    qrTitle: "QR code",
    qrSubtitle: "Scan to open the restaurant",
    backoffice: "Backoffice",
    cancel: "Cancel",
    confirm: "Confirm",
    back: "Back",
    close: "Close",
    save: "Save",
    delete: "Delete",
    edit: "Edit",
    add: "Add",
    search: "Search",
    loading: "Loading...",
    error: "Error",
    forbidden: "You do not have permission for this operation",
    success: "Success",
    warning: "Warning",
    info: "Info",
    yes: "Yes",
    no: "No",
    ok: "OK",
    total: "Total",
    date: "Date",
    refresh: "Refresh",
    apply: "Apply",
    clear: "Clear",
    filters: "Filters",
    noData: "No data",
    product: "Product",
    today: "Today",
    yesterday: "Yesterday",
    print: "Print",
    options: "Options",
    quantity: "Quantity",
    price: "Price",
    amount: "Amount",
    status: "Status",
    comment: "Comment",
    details: "Details",
    view: "View",
    min: "min",
    selected: "Selected",
    select: "Select",
    max: "Max",
    totalRecords: "Total {{count}} records",
    totalReports: "Total {{count}} reports",
    securedJwt: "Secured with JWT • Anti-brute-force active",
    callPhone: "Call {{phone}}",
  },

  login: {
    welcomeKicker: "WELCOME",
    eyebrow: "Restaurant platform",
    heroHeadline: "Run your restaurant from a single calm place.",
    feature1: "Orders, bills and sales in real time",
    feature2: "Revenue reports and analytics",
    feature3: "Menu, tables and operator configuration",
    title: "Sign in",
    subtitle: "Please enter your credentials.",
    usernameLabel: "Username",
    usernamePlaceholder: "admin",
    usernameRequired: "Enter username",
    usernameMin: "Minimum 3 characters",
    passwordLabel: "Password",
    passwordPlaceholder: "••••••••",
    passwordRequired: "Enter password",
    passwordMin: "Password must be at least 3 characters",
    loginButton: "Sign in",
    loginSuccess: "Login successful",
    loginSuccessDesc: "Welcome back!",
    loginFailed: "Login failed",
    loginFailedDesc: "Check username and password.",
    heroDescription:
      "Manage all processes in your restaurant easily and quickly.",
    activeSession: "Active session",
    activeSessionDesc:
      "There is an active session with this user. Are you sure you want to terminate it?",
    terminateAndLogin: "Terminate & Login",
    insufficientLicenses: "Insufficient licenses",
    selectUserToLogout: "Please select a user to log out:",
    forceLogout: "Logout & Login",
    lastActivity: "Last activity:",
  },

  // Expiring license — the "Got it" modal right after sign-in (src/lib/licenseNotice.ts)
  licenseNotice: {
    modal: {
      titleCountdown: "Access will stop in",
      titleBlocked: "Access suspended",
      expiresAt: "The license expires on {{date}} at {{time}}.",
      expiredAt: "The license expired on {{date}} at {{time}}.",
      blockedOn: "The license expired on {{date}} and sign-in is suspended.",
      contact: "Contact UnrealSoft to renew the license without interruption.",
      contactBlocked: "Contact UnrealSoft to renew the license.",
      titleReadOnly: "View only",
      readOnly:
        "You can browse Reports and Inventory. Changes, actions and the other modules are unavailable until the license is renewed — contact UnrealSoft.",
      readOnlyOn: "The license expired on {{date}} — view only.",
      acknowledge: "Got it",
    },
  },

  dashboard: {
    greeting: "Hello, {{name}}",
    subtitle:
      "Select a module and continue to the corresponding functionality.",
  },

  modules: {
    pageTitle: "Modules",
    welcomeKicker: "Welcome",
    ordering: "Ordering",
    orderingDesc:
      "Create orders, mark products by table and manage bills in real time",
    configuration: "Configuration",
    configurationDesc:
      "Configure monitors, fiscal printers and system settings for your establishment",
    monitors: "Monitors",
    monitorsDesc:
      "Monitor active orders by station and manage their status in real time",
    reports: "Reports",
    reportsDesc:
      "View bill history, personal and establishment reports, cancellations and split bills",
    reservations: "Reservations",
    reservationsDesc:
      "Plan and manage table reservations, schedules and availability by date",
    inventory: "Inventory",
    inventoryDesc:
      "Track stock levels, manage deliveries and control the movement of goods",
    discounts: "Discounts",
    discountsDesc:
      "Create and manage discounts, promotions and customer loyalty programs",
    delivery: "Delivery",
    deliveryDesc:
      "Organize deliveries, track couriers and manage delivery orders",
    monitoring: "Monitoring",
    monitoringDesc:
      "Action audit log and fiscal issues — service access for the administrator",
    aiIntegrations: "AI integrations",
    aiIntegrationsDesc:
      "Artificial intelligence that forecasts, analyzes and takes routine work off your hands",
    aiModal: {
      title: "AI integrations",
      subtitle:
        "Artificial intelligence is coming to every module — less manual work and more time for your guests.",
      available: "Available now",
      footnote: "We are working on them — we will let you know as soon as they are available.",
      features: {
        voice: {
          title: "Voice orders",
          desc: "The waiter speaks the order — the system enters it on its own.",
        },
        forecast: {
          title: "Revenue forecast",
          desc: "Predicts the busy days and hours so you can plan shifts and restocking.",
        },
        restock: {
          title: "Smart restocking",
          desc: "Suggests what and how much to order based on sales and stock levels.",
        },
        menu: {
          title: "Menu analysis",
          desc: "Shows which dishes bring profit and which are due for a change.",
        },
        reservations: {
          title: "AI reservation assistant",
          desc: "Takes reservations by phone and chat — around the clock.",
        },
        invoices: {
          title: "Deliveries from a photo",
          desc: "Snap the supplier invoice and the delivery goes into stock.",
        },
      },
    },
    accessModal: {
      title: "Test mode",
      subtitle: "Enter the test mode code",
      clear: "Clear",
      backspace: "Backspace",
      submit: "Log in",
      close: "Close",
    },
    noRoles: "No roles assigned",
    noRolesDesc:
      "To access modules, you need to be assigned roles/permissions. Please contact an administrator.",
    comingSoonKicker: "Coming soon",
    comingSoonTitle: "{{module}} is still in the works",
    comingSoonDesc:
      "We're building this module. Stay tuned — we'll let you know as soon as it ships.",
    comingSoonCta: "Got it",
    accessOn: "Test mode",
    revokeAccess: "Exit test mode",
    rolesLabel: "Roles",
  },

  navbar: {
    reportsAndAnalysis: "Reports & Analysis",
    configurationSubtitle: "System configuration",
    monitorsSubtitle: "Order monitoring",
    deliverySubtitle: "Delivery management",
    discountsSubtitle: "Discounts management",
    inventorySubtitle: "Inventory management",
    reservationsSubtitle: "Reservations management",
  },

  sidebar: {
    dashboard: "Dashboard",
    sales: "Sales",
    settings: "Settings",
    expandMenu: "Expand menu",
    collapseMenu: "Collapse menu",
  },

  ordering: {
    depositLoad: "Deposit",
    // A new table with no internet (`NewTableModal`): the text ALSO serves as
    // the marker — the line about what a manager can do is shown under it.
    tableCheckOffline:
      "No connection to the cloud — the table was not created. Check the internet and try again.",
    tableCheckOfflineHint:
      "If the internet is down, a manager can switch the venue to working against the till.",
    tableCheckSkipped:
      "The name was not checked ({{reason}}) — the table was created anyway; a taken name would have been refused.",
    // “New table” with a suggested number (the “Suggest a new table number”
    // setting): the line under the field while the number is still the suggestion.
    newTableSuggestedHint:
      "The first free number — OK accepts it, type another to replace it",
    // "New table" with the permission "Opening and closing other operators'
    // bills": first whose table it is, then the number.
    newTableWaiter: {
      subtitle: "Whose table is it?",
      you: "You",
      openTables_one: "{{count}} open table",
      openTables_other: "{{count}} open tables",
      noOpenTables: "no open tables",
      dash: "Dash",
      offlineBlocked:
        "A table for another waiter can't be opened without internet. Wait for the connection and press \"Order\" again.",
    },
    // Renaming a table (`RenameTableModal`): the name check failed. This is
    // NOT a refusal — "Save" stays enabled.
    renameCheckOffline:
      "No connection to the cloud — the name was not checked. \"Move\" still works: a taken table is refused by the move itself.",
    renameCheckFailed:
      "The name check did not go through: {{reason}}. \"Move\" still works.",
    // Quick close with no cloud: the new table number comes from the tables on
    // THIS screen, not from the backend.
    nextTableNameOffline:
      "No connection to the cloud — the number was picked from the tables on this screen: {{name}}. Check with your colleagues before announcing it.",
    nextTableNameLocal:
      "The number was picked from the tables on this screen ({{name}}): {{reason}}",
    // An order sent to a cloud that never answered. It is not retried
    // automatically — hence the text sends the person to look at the table.
    orderCloudUnreachable:
      "No connection to the cloud. It is not certain the order arrived — check the table before sending it again.",
    tableKicker: "Table",
    loadingMenu: "Loading menu...",
    searchProduct: "Search product...",
    noProductsFound: "No products found",
    searchTable: "Search table, bill or waiter...",
    cardNoReceiptTag: "Card — no receipt",
    tableClosing: "Closing",
    // The bill facts on the table card in "List"
    // (tables/cardInfo/tableCardFacts.ts): the row text and the tooltips
    cardInfo: {
      billDiscount: "on the whole bill",
      billDiscountTitle: "Whole-bill discount: {{amount}}",
      lineDiscount: "on items",
      lineDiscountTitle: "Item discounts: {{percent}} on average",
      lineSurchargeTitle: "Item surcharges: {{percent}} on average",
      clientTitle: "Client: {{name}} ({{percent}})",
      depositNumber: "No. {{id}}",
      depositTitle: "{{title}}: {{amount}}",
      guestsTitle: "Guests: {{count}}",
      moreTitle: "More:\n{{list}}",
    },
    // The handles that size the tables screen (desktop, "List")
    tablesLayout: {
      resizeRail: "Drag to resize the tables. Double-click to reset.",
      resizeCardWidth: "Drag to set the cards per row. Double-click to reset.",
      resizeCardHeight: "Drag to set the card height. Double-click to reset.",
      resizeCard: "Drag to resize the cards. Double-click to reset.",
    },
    // Tooltip of the seated reservation on the table card
    seatedReservationTag: "Seated reservation — {{name}}",
    noTablesFound: "No bills found",
    noTablesFoundHint: "Try a different keyword or clear the search.",
    noOpenTables: "No open bills yet",
    noOpenTablesHint:
      "Create a new table from the button below to start an order.",
    info: "Info",
    // "Info" in warehouse mode: the recipe's ingredients (one level)
    infoIngredients: "Ingredients",
    infoSubRecipe: "Recipe",
    modify: "Modify",
    order: "Order",
    newTable: "New table",
    guestCount: {
      title: "Guests",
      quickPick: "Quick pick",
      keyboardHint: "Type the count and press Enter, ↑ ↓ to change.",
      confirm_one: "Confirm · {{count}} guest",
      confirm_other: "Confirm · {{count}} guests",
      saveError: "The guest count was not saved",
      saved: "The guest count was saved",
    },
    closeTable: "Close",
    cancelItem: "Cancel",
    split: "Split & move",
    clients: "Clients",
    billDiscountClient: "Client",
    billDiscountBase: "Base",
    billDiscountLabel: "Discount",
    billSurchargeLabel: "Surcharge",
    discounts: "Discounts",
    lockedPriceNoDiscount:
      "This product has a locked price — discounts cannot be applied.",
    customPrice: "Change price",
    preliminaryBill: "Preliminary bill",
    preliminaryBillPrinting: "Printing preliminary bill…",
    preliminaryBillSuccess: "The preliminary bill has been printed.",
    preliminaryBillError: "Could not print the preliminary bill.",
    report: "Report",
    interimReport: "Interim report",
    interimReportCode: "Enter your code",
    operations: "Fiscal operations",
    invoicing: "Invoicing",
    restore: "Restore",
    selectTableToView:
      "Create a new table from the button below to start a new order.",
    noTableSelected: "No table selected",
    noTableSelectedHint:
      "Pick a table on the left to see its orders, or start a new one from the button below.",
    noOrdersForTable: "No orders for this table",
    noOrdersForTableHint:
      "Add products from the menu to start an order for this table.",
    tableNumber: "Table number",
    tableCreatedSuccess: 'Table "{{name}}" created and products added',
    tableJoinedSuccess: 'Table "{{name}}" opened and products added',
    productsAddedSuccess: "Products added to the table",
    selectProductsWarning: "Please select products to order",
    home: "Home",
    modifiers: "Modifiers:",
    addonRequired: "select",
    addonRecommended: "recommended",
    addonOptional: "optional",
    itemComment: "Comment",
    itemCommentPlaceholder: "Extra note for the product",
    orderPreview: "Order preview",
    summarized: "Summarized",
    orderComment: "Order comment:",
    commentPlaceholder: "Enter comment...",
    reservationNotice: {
      blockedTitle: "The table belongs to a seated reservation",
      linkedTable: "Table {{table}} belongs to {{guest}}'s seated reservation — the bill is on table {{billTable}}. Order there.",
      understood: "Got it",
      title: "The table has a reservation",
      question: "{{notice}}. Do you want to continue?",
      continue: "Continue",
      seated: "Table {{table}} is taken by {{guest}}'s reservation (seated {{time}})",
      upcoming_one: "Table {{table}} is reserved for {{time}} — {{guest}}, {{count}} guest",
      upcoming_other: "Table {{table}} is reserved for {{time}} — {{guest}}, {{count}} guests",
      // "Cancel" on quick open → offer the next free number
      openInstead: "Table {{reserved}} is reserved. Open table {{free}} instead?",
      openTable: "Open table {{name}}",
      noFreeTable: "No free table without a reservation — open one manually",
      searchingFreeTable: "Looking for a free table without a reservation…",
    },
    tableAlreadyExists: 'Table "{{name}}" already exists',
    pleaseChooseDifferentName: "Please choose a different table name",
    add: "Add",
    options: "Options",
    // Влачене между „Опции" и лентата под масите (TableFooterBar)
    optionsDragHint: "Press and hold a button, then drag it anywhere on the bar below — even between the main buttons. Drag it back the same way. \"New table\", \"Add\", \"Close\" and \"Options\" can be moved along the bar too.",
    optionsDropHere: "Drop here to move the button back to Options",
    optionsEmpty: "All buttons are on the bar. Drag one here to move it back.",
    footerLayoutSaveError: "The bar layout was not saved",
    // Гласова поръчка (микрофонът до търсачката на масите)
    voiceOrder: "Voice order",
    voiceListening: "Speak…",
    voiceHold: "Hold and speak",
    voiceListeningHint:
      "For example: “Open table 1 and add 2 caesar salads with 2 colas”",
    voiceTranscribing: "Recognizing speech…",
    voiceProcessing: "Understanding the order…",
    voiceNotSupported:
      "This browser does not give access to the microphone. Open the app over https:// in an up-to-date browser.",
    voiceNoSpeech: "I did not hear anything. Please try again.",
    voiceMicDenied:
      "Microphone access denied — allow it from the browser settings.",
    voiceMicUnavailable:
      "The microphone is not available — check that another app is not using it.",
    voiceErrAudioTooLarge: "The recording is too long — try a shorter order.",
    voiceErrUnsupportedAudio:
      "The recording format is not supported by this browser.",
    voiceParseError: "Could not understand the order. Please try again.",
    // Codes come from the BE (502 → detail.code); an unknown code falls back
    // to voiceParseError. See cloudTS-restaurant-be/docs/cross-repo-contract.md.
    voiceErrTooLong: "The order is too long — split it into shorter ones.",
    voiceErrUnreadable:
      "The model returned an unreadable answer. Please try again.",
    voiceErrEmpty: "The model returned an empty answer. Please try again.",
    voiceErrRefused: "The model refused the request. Please try again.",
    voiceErrUpstream:
      "The recognition service is unavailable right now. Please try again.",
    voiceErrNotConfigured:
      "Voice orders are not configured. Please contact an administrator.",
    voiceNoItems: "No products were recognized.",
    // "Seat the reservation of …" by voice (voice/useVoiceSeat.tsx).
    voiceSeatNotFound: "No reservation to seat under the name “{{name}}”",
    voiceSeatNotAllowed: "You are not allowed to seat reservations",
    voiceRetry: "Try again",
    voiceContinue: "Continue",
    voiceChooseHint:
      "What you said matches several options — pick the one you meant.",
    voiceNeedsConfig: "Pick the required add-ons",
    voiceTable: "Table",
    voiceTableNew: "new table",
    voiceTableExisting: "adding to the open table",
    voiceTablePlaceholder: "type a table…",
    voiceNoMatch: "no match in the menu",
    voiceConfirm: "Order",
    voiceCancel: "Cancel",
    voiceSuccess: "The order for table “{{name}}” was sent.",
    itemServed: "Item marked as served",
    itemUnserved: "Serving undone",
    itemServedError: "Error marking item as served",
    servedStatus: "Served",
    serveAfterStatus: "Can be served after status “{{status}}”",
    statusLegend: "Status legend",
    statusNotStarted: "Not started",
    statusInProgress: "In progress",
    statusCooked: "Cooked",
    statusIssued: "Issued",
    closeTableSuccess: "Bill closed successfully.",
    closeTableError: "Error closing the bill.",
    renameTable: "Change table",
    renameTableTitle: "Rename table",
    renameTableSubtitle: "New table name",
    renameTableSuccess: 'Table renamed to "{{name}}"',
    renameTableError: "Error renaming the table",
    // "Change table" (manage/RenameTableModal.tsx): from → to.
    moveTableSubtitle: "Table {{name}} → to another table",
    moveTableButton: "Move",
    moveTableTaken: "Table {{name}} is taken",
    moveTableTakenBy: "Table {{name}} is taken by {{waiter}}",
    // "To another table" from "Split" (NewTableModal with `moveFrom`).
    moveToTableTitle: "To another table",
    moveToTableButton: "Move to table {{name}}",
    // The check while typing failed — a warning only.
    tableCheckLiveFailed: "No connection — not checked whether the table is free.",
    transferBill: "Change waiter",
    transferBillTitle: "Transfer bill to another waiter",
    transferBillSubtitle: "Waiters: {{count}}",
    transferBillConfirm: "Transfer",
    transferBillSuccess: "Bill transferred successfully",
    transferBillError: "Error transferring the bill",
    transferAllBills: "Transfer all my {{count}} tables",
    transferAllBillsSuccess: "{{count}} tables transferred",
    transferAllBillsError: "Error transferring the tables",
    discardItemsConfirmTitle: "Leave order",
    discardItemsConfirmContent:
      "Are you sure? The selected products will be removed.",
    productsShortCount: "{{count}} items",
    itemsShortCount: "{{count}} items",
    orderSheetTitle: "Order",
    modifyDone: "Add · {{price}}",
    modifyOrder: "Order · {{price}}",
    reorderUnavailable: "This product is not available in the menu",
    allCategories: "All categories",
    noGroup: "No group",
    emptyOrder: "Select products on the left to start an order.",
    emptyOrderShort: "No products yet.",
    allRows: "All",
    sortName: "ABC",
    sortPlu: "CODE",
    sortIndex: "SORT INDEX",
    allStations: "All stations",
    viewTiles: "Tiles",
    viewTable: "Table",
    // Tables as a list or on the floor plan — the switch in the top bar
    tablesViewList: "List",
    tablesViewSchema: "Floor plan",
    // The floor plan mode of the tables screen (components/tables/schema/)
    schema: {
      // The label above the table name in the panel (uppercased by CSS)
      tableKicker: "Table",
      temporaryTables: "Temporary tables",
      temporaryTablesHint: "Opened outside the floor plan",
      // The grey strip under „Temporary tables": closed without a receipt
      nonFiscalTables: "Non-fiscalized tables",
      nonFiscalTablesHint: "Closed without a receipt",
      // The field at the top right: whose tables are shown
      waiterFilter: "Waiter's tables",
      unknownWaiter: "Unknown waiter",
      freeTableHint:
        "Free table. Double-click it on the floor plan to open it and take an order.",
      noTableSelectedHint:
        "Pick a table on the floor plan to see what was ordered or to open a free table.",
      noSchemaHint:
        "Tables are opened from “List” until the areas and tables are drawn in Reservations → Floor plan.",
      offlineHint:
        "The till has no copy of the floor plan — tables are opened from “List” until the internet is back.",
    },
    keyboard: "Keyboard",
    hideKeyboard: "Hide",
    space: "Space",
    clearSearch: "Clear",
    backspace: "Delete character",
    searchKicker: "Search",
    searchHint: "type a name or code",
    kbBds: "BDS",
    kbPhonetic: "Phonetic",
    kbLatin: "ABC",
    previewStations: "Stations",
    previewGroups: "Groups",
    // "Hold order" (docs/ordering.md §Поток: задържане на поръчка)
    kitchenHold: {
      held: "On hold",
      release: "Release now",
      change: "Change",
      releaseIn: "in {{count}} min",
      heldFor: "for {{count}} min",
      heldJustNow: "just now",
      manualHint: "Manual — until someone presses “Release now”",
      changeError: "The time was not changed",
      now: "Now",
      minutesShort: "{{count}}′",
      manualShort: "Manual",
      mixed: "mixed",
      allRowsLabel: "One time for all items",
      sameForAll: "Same time for all items",
      rowTime: "Kitchen time",
      summaryNow: "{{count}} now",
      summaryMinutes: "{{count}} in {{minutes}}′",
      summaryManual: "{{count}} ∞",
      entering: "Going in…",
      serveBlocked: "The order is on hold — release it first",
      released: "The order went to the kitchen",
      releaseError: "The order was not released",
      offlineDropped:
        "Orders can't be held without internet — the times were set back to \"Now\" and the next \"Order\" sends everything right away.",
      confirmTitle: "Orders on hold",
      confirmMessage_one: "{{count}} order hasn't reached the kitchen yet.",
      confirmMessage_other: "{{count}} orders haven't reached the kitchen yet.",
      confirmQuestion_one: "If you continue, it goes in right away.",
      confirmQuestion_other: "If you continue, they go in right away.",
      confirmContinue: "Release and continue",
    },
  },

  codeKeypad: {
    title: "Enter code",
    enterCode: "Enter code",
    invalidCode: "Invalid code",
    codeVerified: "Code verified successfully",
    submit: "Enter",
    kicker: "Sign in with personal code",
    clear: "Clear",
    venue: "Restaurant",
    online: "Online",
    hint: "You can also type on the keyboard",
  },

  logoutConfirm: {
    title: "Logout confirmation",
    subtitle: "Please enter your password to confirm",
    passwordPlaceholder: "Password",
    invalidPassword: "Invalid password",
    verificationError: "Error verifying password",
  },

  closeTable: {
    // The attached deposit in Close (payment/useCloseDeposit.ts) and the
    // "Use a deposit" picker (tables/deposits/DepositPickerModal.tsx)
    deposit: {
      chosen: "Deposit No. {{id}} · {{name}}",
      // The deposit number (its id) — shown only, never edited
      number: "Deposit No. {{id}}",
      // The deposit's fiscal receipt number (Receipt 1) next to it — shown only
      receiptNumber: "Receipt No. {{number}}",
      // The "Bill − Deposit = Left to pay" breakdown and what will happen
      // (payment/DepositPaymentSummary.tsx)
      billAmount: "Bill",
      due: "Left to pay",
      ofTotal: "of {{amount}} EUR",
      outcomeTitle: "On close",
      outcomeCovers: "The bill is paid in full with the deposit — the guest pays nothing more.",
      outcomeUsedUp: "The deposit is used up in full.",
      outcomePartial: "The deposit covers {{amount}} EUR of the bill.",
      outcomeRemainder: "The remaining {{amount}} EUR is paid by “{{method}}”.",
      pickRemainderMethod: "Choose how the remainder is paid.",
      outcomeRetry: "Retry — the receipt is issued with the recorded deposit and method.",
      remainderMethodTitle: "Pay the remaining {{amount}} EUR by:",
      burned: "The unused {{amount}} EUR of the deposit is forfeited — not refunded to the guest.",
      offline:
        "Offline, the till does not deduct the deposit — the bill is paid in full and the deposit stays free.",
      zeroBill:
        "The bill is 0.00 — remove the deposit via “Deposit” → “Use a deposit” (or attach it to the table with the orders).",
      changed: "The bill's deposit was changed on another terminal — check and confirm again.",
      pickerSubtitle: "Free deposits: {{count}}",
      searchPlaceholder: "Search by name, description, deposit or receipt No.",
      noMatches: "No deposit matches the search",
      noDeposits: "No active deposits",
      validUntil: "For {{date}}",
      noExpiry: "No expiry",
      takenOn: "Taken {{date}} · {{method}}",
      closeCovered: "Close with deposit",
    },
    table: "Table:",
    operator: "Operator:",
    paymentMethod: "Payment method:",
    selectMethod: "Select method:",
    notSelected: "Not selected",
    noMethodsTitle: "No payment methods available",
    noMethodsDescription:
      "Add and activate methods in Configuration → Payment methods.",
    selectPaymentWarning: "Please select a payment method",
    sumMismatch: "Amount must be {{total}} Entered: {{entered}}.",
    totalChanged:
      "The bill has changed — the new total is {{total}}. Check and confirm again.",
    closeSuccess: "Bill closed successfully",
    closeError: "Error closing bill",
    alreadyClosed: "The bill is already closed",
    printerError: "Bill closed, but the printer returned an error",
    printerTimeout: "Bill closed, but the printer did not respond in time",
    closeButton: "Close bill",
    paymentFailed: "Payment failed — table {{table}}: {{text}}",
    stillClosing: "This table is still closing — wait for the receipt.",
    fiscalInvoiceOffline:
      "A fiscal invoice is issued only online — the bill is not closed and nothing was printed. Close it again when the connection is back.",
    billNoLongerOpen:
      "Table {{table}} is no longer open (closed or transferred on another terminal) — Close was cancelled.",
    tableNotice: "Table {{table}} — {{text}}",
    paymentDistribution: "Payment distribution:",
    cash: "Cash",
    card: "Card",
    mixedTotal: "Total: {{entered}} / {{total}}",
    totalToPay: "Total to pay:",
    banknotes: "Banknotes:",
    received: "Received:",
    remaining: "Remaining to pay",
    amountBeforeDiscount: "Amount before discount",
    amountBeforeSurcharge: "Amount before surcharge",
    clientDiscount: "Client discount ({{percent}})",
    clientSurcharge: "Client surcharge ({{percent}})",
    change: "Change due",
    // The "Close" design (payment/CashTipPanel, GivenAmountField): the amounts on top,
    // the "Amount given by the customer" field (also for the tip), the banknotes below.
    changeShort: "Change:",
    remainingShort: "Remaining:",
    givenByClient: "Amount given by the customer",
    clearReceived: "Clear",
    banknotesTitle: "Banknotes",
    banknotesCash: "Cash banknotes",
    // "Mixed": the field filled up to the bill by the other one (payment/MixedTipPanel).
    remainder: "remainder",
    exactAmount: "Exact",
    cardTerminalHint: "Payment via card terminal.",
    // Tip in „Close" and „Split & pay" (payment/TipEntrySection.tsx);
    // errors.* mirror the BE catalog (app/features/tips/rules.py) 1:1.
    tip: {
      title: "Tip",
      ordered: "Ordered after discounts",
      totalWithTipHint:
        "Enter the amount the customer wants to pay for the order and the tip. The difference from the ordered amount is the tip.",
      methodMixedBillHint:
        "The bill is paid mixed — choose one method for the whole tip.",
      someMethodsWrongRoute:
        "Inactive methods go a different way than the bill payment (with or without a fiscal receipt).",
      confirm: "I confirm a tip of {{amount}} €",
      reviewed: "Reviewed",
      dropped: "The entered tip was dropped — tips cannot be entered right now.",
      recorded: "Tip recorded at closing",
      recordedHint: "{{method}} · a retry does not change the tip.",
      recordedWrongPath:
        "The tip was recorded with „{{method}}“ and cannot go the same way as the chosen bill payment method (with or without a fiscal receipt). Choose another method for the bill or restore the bill.",
      breakdownTip: "Tip ({{method}})",
      breakdownTotalWithTip: "Total with tip",
      // The "Closing — all combinations" design: the white "Tip" card
      chooseMethod: "Choose",
      // Hidden behind the "Add a tip" button; opened — "Amount given by the customer"
      // and the on-screen keypad (expanded/collapsed — remembered on the device).
      addTip: "Add a tip",
      removeTip: "Remove",
      underBill: "The amount is less than the bill ({{amount}} EUR)",
      // <num> — the amount in the numbers' mono font, <tip> — the tip in orange (payment/tipDue.ts).
      dueWithTip: "Bill <num>{{ordered}}</num> + tip <tip>{{tip}}</tip>",
      dueSeparateTip: "Tip <tip>{{tip}}</tip> EUR separately · {{method}}",
      recordedShort: "Recorded at closing · {{method}}",
      errors: {
        tip_disabled: "Tips are not enabled.",
        tip_negative:
          "The total cannot be less than the ordered amount — a tip cannot be negative.",
        tip_method_required: "Choose how the tip is paid.",
        tip_method_mixed:
          "A tip is paid in full with one method — „Mixed“ is not allowed.",
        tip_method_not_allowed: "This payment method is not allowed for tips.",
        tip_over_max:
          "The tip {{amount}} € is above the maximum of {{max}} € per bill.",
        tip_confirmation_required:
          "The tip {{amount}} € is above {{threshold}} € — confirm the amount explicitly.",
        tip_method_wrong_path:
          "A tip paid with „{{method}}“ cannot go the same way as the bill payment (with or without a fiscal receipt). Choose another tip method.",
        tip_method_not_on_printer:
          "„{{method}}“ is not set up on the fiscal device — the tip cannot be put on the receipt.",
        tip_on_account_not_allowed:
          "The bill's customer cannot pay „On account“.",
        tip_requires_items: "A tip can only be added to a bill with ordered items.",
        bill_total_changed:
          "The bill's ordered amount changed ({{old}} € → {{new}} €). Review the total with tip again.",
        tip_change_on_retry:
          "The tip was recorded at closing and cannot be changed on a retry.",
      },
    },
  },

  // Configuration → Payment types → the „Tip" section
  // (configuration/components/payment-methods/tip/). errors.* are the BE texts
  // of the 422 on PUT /payment-methods/tip-settings (mirrored check).
  tipSettings: {
    // The section on the page (the summary) and the „Configure" modal — the
    // „Tip – settings" design.
    title: "Tip",
    configure: "Configure",
    statusOn: "On",
    statusOff: "Off",
    statusLineOn: "On — a tip can be entered when closing a bill.",
    statusLineOff:
      "Off — no tip can be entered when closing a bill. Recorded bills, invoices and reports keep their data.",
    loadError: "The tip settings could not be loaded. Please try again later.",
    unsupported: "The server does not support tip settings yet.",
    columnReceipt: "Receipt line",
    columnPayment: "Payment and tax group",
    columnLimits: "Split and limits",
    summaryName: "Name",
    summaryNote: "Text under the line",
    summaryNoNote: "No text",
    summaryNoMethods: "None selected",
    summaryExpense: "Expense share",
    summaryExpenseStaff: "{{percent}}% for the staff",
    summaryDeduct: "Card tips when reporting",
    summaryDeductOn: "Deducted",
    summaryDeductOff: "Not deducted",
    summaryWarn: "Warning above",
    summaryMax: "Maximum per bill",
    modalSubtitle:
      "The receipt line, the payment methods and the limits — on one screen.",
    close: "Close",
    enabledName: "Allow tips",
    enabledHint: "Waiters can add a tip when closing a bill.",
    disabledHint: "No new tip can be entered; recorded data stays.",
    receiptNameBgLabel: "Name — BG",
    receiptNameBgHint: "Printed as a separate line on the fiscal receipt.",
    receiptNameEnLabel: "Name — EN",
    receiptNameEnPlaceholder: "empty — same as BG",
    receiptNoteBgLabel: "Additional text under the line",
    receiptNoteBgHint: "Text only — adds no amount.",
    optional: "optional",
    charsCount: "{{count}}/{{max}}",
    previewName: "Receipt preview",
    previewHint: "example with 10.00 €",
    methodsName: "Payment methods",
    methodsSelected: "{{count}} of {{total}}",
    methodsSearch: "Search payment method",
    methodsAll: "Select all",
    methodsNone: "Clear",
    methodsShownOf: "Shown {{shown}} of {{total}}",
    methodsNoMatch: "No matches.",
    methodSeparateTag: "separate",
    methodSeparateTitle:
      "Reported separately: in the reports, tips paid with this method are not part of the cash/card offset.",
    methodsHint:
      "When closing, the tip is shown only for one of these methods and is paid with it. „Cash“ is change only, „Mixed“ is not allowed. Custom methods and „On account“ are reported separately.",
    noMethods: "No payment types.",
    taxGroupName: "Tax group",
    taxGroupLetter: "Group {{letter}}",
    taxGroupNoVat: "no VAT",
    taxGroupVat: "VAT {{rate}}%",
    taxGroupNoVatHint: "Group {{letter}} — the tip carries no VAT.",
    taxGroupTaxableHint: "Group {{letter}} — the tip carries {{rate}}.",
    taxGroupDeviceHint:
      "Used for the receipt line and must match the device programming.",
    expenseName: "Expense share",
    expenseExample:
      "Example: of a {{tip}} € tip, {{expense}} € stays for expenses, {{staff}} € — for the staff.",
    deductName: "Deduct card tips when reporting",
    deductHint: "The staff share of card tips reduces the cash to hand over.",
    warnName: "Warn for a tip above",
    warnHint: "Asks for confirmation. Empty — none.",
    maxName: "Maximum tip",
    maxHint: "Per bill — a larger one is not recorded.",
    footerStatus: "Status",
    footerMethods: "Methods",
    footerTaxGroup: "Group",
    unsaved: "Unsaved changes",
    cancel: "Cancel",
    save: "Save",
    saveSuccess: "Tip settings saved",
    saveError: "Error saving the tip settings",
    fixErrors: "Fix the marked fields to save.",
    errors: {
      tip_tax_group_invalid: "Choose tax group А, Б or В.",
      tip_name_required: "Enter the name of the receipt line.",
      tip_text_too_long: "The text is longer than {{max}} characters.",
      tip_text_control_chars:
        "The text contains control characters (tab, new line and similar) — remove them.",
      tip_text_unprintable:
        "The text contains characters the fiscal device cannot print. Use Cyrillic, Latin, digits and the usual punctuation.",
      tip_method_mixed:
        "A tip is paid in full with one method — „Mixed“ is not allowed.",
      tip_method_unknown: "Payment method {{id}} does not exist.",
      tip_methods_required: "Choose at least one payment method for tips.",
      tip_percent_range:
        "The expense share must be from 0 to 100% with up to two decimals.",
      tip_max_range:
        "The maximum tip must be above 0 € and up to 99999.99 € with up to two decimals.",
      tip_warn_range:
        "The warning threshold must be above 0 € and not more than the maximum tip, with up to two decimals.",
    },
  },

  cancelItem: {
    titleAssortments: "Void assortments",
    titleRecipes: "Void recipes",
    subtitle: "Table {{name}} · Bill #{{id}} · total {{total}} €",
    ofQuantity: "of {{max}}",
    confirmButton: "Void",
    defective: "Defective",
    selectAll: "Select all",
    reasonLabel: "Reason",
    reasonWrongMark: "Marked by mistake",
    reasonCustomerRefused: "Customer refused",
    reasonOrderChanged: "Order changed",
    reasonOther: "Other",
    reasonPlaceholder: "Describe the reason...",
    // A failed void: the dialog STAYS open with the selection.
    failed:
      "The void did not go through. The dialog stays open — check the bill and try again.",
    failedReason:
      "The void did not go through: {{reason}}. Check the bill — the credit note may still have printed.",
  },

  splitBill: {
    title: "Split and move",
    subtitle: "Table {{name}} · {{total}} €",
    sumLabel: "Amount:",
    modeByProducts: "By products",
    modeEqual: "Equally",
    modifiersLabel: "Modifiers:",
    addonsLabel: "Addons:",
    selectedProducts: "Selected products: {{count}}",
    selectAll: "Select all",
    splitButton: "To another table",
    cancelButton: "Cancel",
    splitError: "Error splitting bill",
    transferWaiterTitle: "Transfer to waiter",
    transferWaiterSubtitle: "Waiters: {{count}}",
    transferWaiterSearch: "Search waiter...",
    transferWaiterNone: "No waiters found",
    transferWaiterSkip: "Skip",
    transferWaiterConfirm: "Transfer",
    transferWaiterCurrent: "Current",
    transferWaiterResponsibility: "Responsibility for {{total}} EUR is transferred",
    transferWaiterFrom: "From",
    transferWaiterTo: "To",
    transferWaiterNoTarget: "choose a waiter",
    transferWaiterTablesOne: "1 table",
    transferWaiterTables: "{{count}} tables",
    transferWaiterConfirmTo: "Transfer to {{name}}",
    splitAndPayButton: "Pay selected",
    // An attached deposit is deducted from the whole bill at Close
    depositBlocksPay:
      "The bill has a deposit — “Pay selected” is locked. Pay the whole bill via Close, or move some items “To another table” and pay them there.",
    // The whole-bill discount stays on this bill
    billDiscountBlocksPay:
      "The bill has a whole-bill discount — “Pay selected” is locked. Pay the whole bill via Close, or move some items “To another table” (the discount stays on this bill).",
    splitAndPayTitle: "Split & pay",
    splitAndPaySubtitle: "From bill #{{name}}",
    splitAndPaySuccess: "Bill split and paid successfully",
    splitAndPayError: "Error splitting and paying",
    billChanged: "The bill has changed — review the selected items and try again.",
  },

  confirmSplit: {
    title: "Table is occupied",
    transferQuestion:
      "Would you like to transfer the selected products to the already open table?",
    transferButton: "Transfer",
    occupiedByOtherWaiter: "Table “{{name}}” is already occupied by another waiter.",
    noTransferPermission:
      "You don't have permission to transfer split products to another waiter.",
    understoodButton: "Got it",
  },
  duplicateTableName: {
    title: "The table will be duplicated",
    transferMessage:
      "The waiter you are transferring to already has an open table “{{name}}”.",
    restoreMessage: "There is already an open table “{{name}}”.",
    transferAllMessage:
      "The waiter you are transferring to already has open tables with some of your names.",
    transferAllQuestion:
      "If you continue, there will be two open tables with the same name.",
    question: "If you continue, there will be two open tables named “{{name}}”.",
    confirm: "Continue",
  },

  itemDiscounts: {
    title: "Discounts",
    subtitle: "Table {{name}} · {{total}} €",
    emptyBill: "This bill has no products.",
    generalTitle: "General discount",
    generalHint: "Applies to the selected rows without their own discount",
    ownPercentCount_one: "{{count}} row has its own discount and stays unchanged",
    ownPercentCount_other: "{{count}} rows have their own discount and stay unchanged",
    none: "None",
    free: "Free",
    selectAll: "Select all",
    discountedOfTotal: "{{discounted}} of {{total}} discounted",
    removeAll: "Remove all",
    forThisRow: "For this row",
    followGeneral: "General · {{percent}}%",
    ownSuffix: "row",
    currentPercentTitle: "Now: −{{percent}}%",
    discountLabel: "Discount",
    customPriceHint: "custom price",
    applySuccess: "Discounts saved",
    // "Whole bill" — an amount in € (BE `apply-amount-discount`)
    modeItems: "By product",
    modeBill: "Whole bill",
    billTitle: "Whole-bill discount",
    billHint: "Amount in €, taken off the bill · up to {{max}} €",
    billRowsTotal: "Bill",
    billToPay: "To pay",
    billReceiptNote:
      "Printed on the fiscal receipt as one \"Discount\" line under the subtotal. Product discounts stay.",
    billKeptNote: "Whole-bill discount −{{amount}} € — taken off after these",
    billApplySuccess: "Bill discount saved",
    billRemoveSuccess: "Bill discount removed",
    billCloseOffline:
      "This bill has a whole-bill discount — close it when the internet is back",
  },

  customPrice: {
    title: "Change price",
    subtitle: "Tap the pencil next to a product or addition and enter a new price",
    emptyBill: "This bill has no products.",
    changedCount: "Changed prices: {{count}}",
    invalidRows: "Invalid price on {{count}} row(s) — empty or 0.",
    restore: "Restore menu price",
    applySuccess: "Prices updated",
  },

  clients: {
    title: "Discount clients",
    total: "Total: {{count}}",
    noClients: "No clients found",
    onAccountBadge: "On account",
    select: "Select",
    searchPlaceholder: "Search by name or card...",
    // The chips under the search and the footer summary (ClientsModal).
    filterAll: "All",
    filterDiscount: "With discount",
    filterSurcharge: "With surcharge",
    summaryAfterDiscount: "Table {{name}} after discount",
    summaryAfterSurcharge: "Table {{name}} after surcharge",
    summaryWithout: "Table {{name}} without adjustment",
    applyPercent: "Apply {{percent}}",
    removeButton: "Remove",
    applySuccess: "Discount applied successfully",
    applySurchargeSuccess: "Surcharge applied successfully",
    removeDiscount: "Remove applied discount ({{discount}})",
    removeDiscountNamed: "Remove discount - {{name}} ({{discount}})",
    removeSurcharge: "Remove applied surcharge ({{discount}})",
    removeSurchargeNamed: "Remove surcharge - {{name}} ({{discount}})",
    removeSuccess: "Discount removed successfully",
    removeSurchargeSuccess: "Surcharge removed successfully",
    active: "Active",
    inactive: "Inactive",
    from: "From",
    to: "To",
    monday: "Mon",
    tuesday: "Tue",
    wednesday: "Wed",
    thursday: "Thu",
    friday: "Fri",
    saturday: "Sat",
    sunday: "Sun",
    // Attached-customer banner (tables + close modal)
    bannerDiscountAssortments: "{{percent}} discount on all assortments",
    bannerDiscountRecipes: "{{percent}} discount on all recipes",
    bannerSurchargeAssortments: "{{percent}} surcharge on all assortments",
    bannerSurchargeRecipes: "{{percent}} surcharge on all recipes",
    // Some rows carry a different percent (row discount / custom price)
    bannerDiscountPartialAssortments:
      "{{percent}} discount on {{count}} of {{total}} assortments",
    bannerDiscountPartialRecipes:
      "{{percent}} discount on {{count}} of {{total}} recipes",
    bannerSurchargePartialAssortments:
      "{{percent}} surcharge on {{count}} of {{total}} assortments",
    bannerSurchargePartialRecipes:
      "{{percent}} surcharge on {{count}} of {{total}} recipes",
    bannerNoDiscount: "no discount",
    bannerSaved: "Saved",
    bannerSurcharged: "Surcharge",
  },

  invoicing: {
    selectBillTitle: "Select bill",
    selectBillSubtitle:
      "Uninvoiced closed bills not yet in a personal report. A bill from a report already run is invoiced from “Reports → Bills report”. Pick one or more to continue.",
    selectBillSubtitleWindow:
      "Uninvoiced closed bills from the last {{days}} days. Pick one or more to continue.",
    searchPlaceholder: "Search by table, waiter or payment",
    selectAll: "Select all",
    selectRow: "Select bill",
    colId: "Time",
    colTable: "Table",
    colWaiter: "Waiter",
    colPayment: "Payment",
    colTotal: "Total",
    confirm: "Select",
    confirmWithCount: "Select ({{count}})",
    selectedCount: "Selected bills: {{count}}",
    emptyAll: "No uninvoiced closed bills.",
    emptyFiltered: "No bills match your search.",
    loadError: "Failed to load bills",
    needDefaultCompany:
      "Set a current company first in Configuration → Companies.",
    warnAlreadyInvoiced: "The invoice has already been issued",
    warnOnlyClosed: "Only closed bills can be invoiced",
    warnCashNoReceipt:
      "The bill was closed in cash without a receipt and cannot be invoiced",
    warnWindowExpired: "Invoicing window expired ({{days}} days)",
  },

  freeInvoice: {
    title: "Free invoice",
    subtitle: "No bill — the lines are entered by hand",
    paymentHint: "“On account” only",
    issue: "Issue invoice",
    issued: "The invoice is issued",
  },

  invoicesReport: {
    title: "Invoices report",
    listKicker: "Invoices list",
    resultOne: "{{count}} invoice",
    results: "{{count}} invoices",
    searchPlaceholder: "Search by invoice №, customer, EIK or receipt",
    kindInvoice: "Invoice",
    kindSimplified: "Simplified invoice",
    kindUnused: "Unused number",
    kindFree: "Free invoice",
    creditNoteOf: "for № {{number}}",
    sourceFiscal: "FD",
    sourceSoftware: "Software",
    statusCredited: "Credited",
    statusUnused: "Not a document",
    creditedBy: "CN № {{number}}",
    createdAt: "created {{moment}}",
    kpiInvoices: "Invoices",
    kpiInvoicesSub: "issued, excluding cancelled",
    kpiGrandSub: "incl. VAT · after credit notes",
    kpiCreditNotes: "Credit notes",
    kpiCreditNotesSub: "{{amount}} · subtracted from the totals",
    kpiOutside: "Outside the totals",
    kpiCancelledPart: "{{count}} cancelled",
    kpiUnusedPart: "{{count}} unused numbers",
    clearFilters: "Clear filters",
    cancelledOnly: "Cancelled only",
    empty: "No invoices issued in the selected period.",
    view: "View",
    colNumber: "Invoice №",
    colDate: "Date",
    colTime: "Time",
    colDateTime: "Date and time",
    colSource: "Source",
    colDevice: "FD",
    colReceipt: "Receipt №",
    colCreditNoteOf: "For invoice №",
    colCreditedBy: "Credit note №",
    colType: "Type",
    colCustomer: "Customer",
    colEik: "EIK",
    colTaxable: "Taxable",
    colNonTaxable: "Non-taxable",
    colVatRate: "VAT %",
    colVat: "VAT",
    colGrand: "Grand total",
    colView: "Actions",
    kpiCount: "Invoices",
    kpiTaxable: "Taxable",
    kpiNonTaxable: "Non-taxable",
    kpiVat: "VAT",
    kpiGrand: "Grand total",
    cardRevenueTitle: "Invoiced revenue",
    cardRevenueCountLabel: "Count",
    cardRevenueAvgLabel: "Average",
    cardVatTitle: "Taxable / VAT / non-taxable split",
    excelTotal: "Total (documents in force)",
  },

  invoicePreview: {
    title: "Invoice № {{number}}",
    subtitle: "Issued on {{date}}",
    editCustomer: "Edit recipient",
    editCustomerSubtitle:
      "Recipient details only — totals, lines, number and dates stay unchanged.",
    editCustomerSuccess: "Recipient updated",
    editCustomerError: "Failed to update the recipient",
    recipient: "Recipient",
    supplier: "Supplier",
    titleOriginal: "ORIGINAL INVOICE",
    titleDuplicate: "DUPLICATE INVOICE",
    titleSimplified: "SIMPLIFIED INVOICE",
    docNumber: "Number",
    docDate: "Date",
    supplyDate: "Supply date",
    supplyPlace: "Supply place",
    colDescription: "Goods / service",
    colVatRate: "VAT %",
    colQty: "Qty",
    colUnitPrice: "Unit price",
    colTotal: "Total",
    totalWords: "Grand total in words",
    sumTotal: "Subtotal",
    sumTaxable: "Tax base",
    sumNonTaxable: "Non-taxable",
    sumVat: "VAT",
    sumGrand: "Grand total (EUR)",
    goodsReceived: "Goods / service received:",
    receivedAt: "Date",
    receivedBy: "By",
    paymentMethod: "Payment method",
    noteLabel: "Note",
    preparedBy: "Prepared by",
    print: "Print",
    printPos: "Print (POS)",
    originalPrintedTitle: "Was the original printed?",
    originalPrintedDescription: "If not, the next print from “Reports → Invoices” will be the original again.",
    originalPrintedSaveError: "The original print was not saved — try again",
    printPosSuccess: "Invoice sent to the POS printer",
    printPosError: "Error printing on the POS printer",
    cancel: "Cancel invoice",
    cancelConfirmTitle: "Cancel invoice № {{number}}",
    cancelConfirmStays:
      "The invoice stays in “Invoices” marked “CANCELLED”, and its number is never reused.",
    cancelConfirmBills_one: "Its bill becomes uninvoiced — you can invoice it again.",
    cancelConfirmBills_other: "Its {{count}} bills become uninvoiced — you can invoice them again.",
    cancelConfirmNoPrint: "The fiscal receipt is not touched and nothing is printed.",
    cancelConfirmFinal: "The cancellation cannot be undone.",
    cancelConfirmContent: "Are you sure you want to cancel the invoice?",
    cancelSuccess: "Invoice cancelled",
    cancelError: "Error cancelling invoice",
    cancelledBadge: "CANCELLED",
  },

  invoiceCreate: {
    title: "Issue invoice",
    subtitle: "Document over {{count}} selected bills",
    secDocument: "Document",
    secCustomer: "Customer",
    secItems: "Items",
    fieldNumber: "Number",
    numberHint: "auto +1 from last issued",
    fieldType: "Type",
    typeTax: "Tax invoice",
    typeSimplified: "Simplified invoice",
    fieldIssueDate: "Issue date",
    fieldSupplyDate: "Supply date",
    fieldSupplyPlace: "Supply place",
    fieldPaymentMethod: "Payment method",
    paymentFromBills: "from the bill's receipt",
    optional: "optional",
    fieldName: "Company name",
    fieldVat: "VAT №",
    fieldCity: "City",
    fieldAddress: "Address",
    fieldMol: "Accountable person",
    fieldPickCompany: "Search previous invoices",
    fieldPickCompanyPh: "Name or EIK from a previous invoice",
    fieldCustomerEik: "EIK №",
    phCustomerEik: "9 or 13 digits",
    phCustomerCity: "e.g. Sofia",
    phCustomerName: 'e.g. "Test Ltd"',
    phCustomerAddress: "Registered address",
    phCustomerMol: "Full name",
    noCustomers: "No invoices issued yet — fill in the details below",
    noCustomersForQuery: "No such company in previous invoices — fill in the details below",
    noDefaultCompany: "No current company set",
    lookupTitle: "Fetch data from the Bulgarian Commercial Registry",
    lookupInvalidEik: "Enter a valid EIK first",
    lookupSuccess: "Data filled from the Commercial Registry",
    lookupFailed: "Lookup failed. Try again or fill manually.",
    fieldReceivedBy: "Received by",
    fieldReceivedByPh: "Full name",
    fieldReceivedAt: "Receipt date",
    colName: "Name",
    colUnit: "Unit",
    colVat: "VAT %",
    colQty: "Qty",
    colUnitPrice: "Unit price",
    // The field in “New line” / “Edit line” — the price is entered WITH VAT (the
    // table column is the net one, `InvoiceLineRow`).
    unitPriceGross: "Unit price incl. VAT",
    colVatAmount: "VAT amount",
    colTotal: "Net amount",
    colGross: "Total incl. VAT",
    noLines: "No items.",
    totalSum: "Subtotal",
    totalTaxable: "Tax base",
    totalNonTaxable: "Non-taxable",
    totalVat: "VAT",
    totalGrand: "Grand total",
    totalWords: "Grand total in words",
    note: "Note",
    actionEdit: "Edit",
    actionDelete: "Delete",
    actionSummarize: "Summarize",
    actionAdd: "New row",
    save: "Save",
    editLineTitle: "Edit line",
    editLineSubtitle:
      "Change name, unit, VAT, quantity and unit price. Total is recalculated.",
    newLineTitle: "New line",
    newLineSubtitle: "Add an item manually. Only the name is required.",
    addLine: "Add",
    summarizeTitle: "Summarize bill",
    summarizeSubtitle: "Choose how items will appear on the final invoice.",
    summarizePerItem: "By name and price",
    summarizePerItemDesc: "Each item on its own row — as it was ordered.",
    summarizeSingle: "All on a single row",
    summarizeSingleDesc:
      'A single "Consumption" row with quantity 1 and the bill total.',
    summarizeApply: "Apply",
    summarizedName: "Consumption",
    deleteConfirmTitle: "Delete row",
    deleteConfirmDesc: 'Are you sure you want to delete "{{name}}"?',
    deleteConfirmOk: "Delete",
    mismatchOneBill: "the bill",
    mismatchManyBills: "the bills",
    mismatchTitle: "Invoice total differs from {{context}} amount",
    mismatchBody: "Do you want to continue saving?",
    mismatchBillsTotal: "Bill(s) total",
    mismatchInvoiceTotal: "Invoice total",
    errInvoiceNumber: "Enter a valid invoice number",
    errDates: "Pick issue and supply dates",
    errPaymentMethod: "Pick a payment method",
    errCustomerEik: "Invalid customer EIK",
    errCustomerVat: "VAT number must be in BG + 9 or 10 digits format",
    errCustomerFields: "Fill in company name, city and address",
    errNoLines: "Add at least one item",
    saveSuccess: "Invoice saved successfully",
    saveError: "Failed to save invoice",
  },

  reportType: {
    selectType: "Select report type",
    personal: "Personal report",
    establishment: "Establishment report",
  },

  operations: {
    selectType: "Select operation type",
    xReport: "X report (no reset)",
    zReport: "Z report (with reset)",
    // Fiscal-memory report for a period — a different document, not an X with dates.
    fmReport: "Reports by date",
    fmReportDetail: "Report type",
    fmReportShort: "Summary",
    fmReportFull: "Detailed",
    fmReportShortHint: "The period totals in one short report.",
    fmReportFullHint:
      "One block per Z report in the period — a long period is metres of paper.",
    fmReportPeriod: "Report period",
    fmPresetLastMonth: "Last month",
    fmPresetThisMonth: "This month",
    fmPresetPeriod: "Period",
    fmPresetLastMonthHint:
      "From {{from}} to {{to}}. The monthly report is due by the {{deadlineDay}}th of the following month.",
    fmPresetThisMonthHint: "From {{from}} until today ({{to}}).",
    fmPeriodFrom: "From",
    fmPeriodTo: "To",
    fmPeriodLongSpanHint:
      "No limit. A detailed report over {{days}} days asks for confirmation before printing.",
    // Long detailed report — a question to the operator, not a ban.
    fmLongPeriodTitle: "Long detailed report",
    fmLongPeriodMessage:
      "The selected period is {{days}} days. The detailed report prints one block per Z report in it — that is metres of paper and several minutes of printing.",
    fmLongPeriodQuestion:
      "Print it anyway? If you only need the period totals, choose “Summary”.",
    fmLongPeriodConfirm: "Print",
    fmReportPeriodRequired: "Select a period",
    fmReportInvalidPeriod: "The end date is before the start date",
    fmReportFuturePeriod: "The period is in the future",
    fmReportSuccess: "The period report has been printed",
    xReportSuccess: "The X report has been printed",
    zReportSuccess: "The Z report has been printed",
    cashIn: "Cash In",
    cashOut: "Cash Out",
    enterAmount: "Enter amount",
    amountRequired: "Please enter an amount greater than 0",
    cashInSuccess: "Cash in completed successfully",
    cashOutSuccess: "Cash out completed successfully",
    error: "Error executing operation",
  },

  generateReport: {
    title: "Generate report",
    establishmentTitle: "Generate establishment report",
    selectUser: "Select user:",
    selectUserWarning: "Please select a user",
    reportDate: "Report date:",
    selectDate: "Select date",
    previewReport: "Preview report",
    // In warehouse mode the sellable is a recipe → same checkbox, different name.
    showSoldAssortments: "Show sold assortments",
    showSoldRecipes: "Show sold recipes",
    loadUsersError: "Error loading users",
    previewError: "Error previewing report",
    establishmentPreviewError: "Error previewing establishment report",
    reportCreated: "Report created successfully",
    establishmentReportCreated: "Establishment report created successfully",
    generateError: "Error generating report",
    establishmentGenerateError: "Error generating establishment report",
    resetReport: "Reset report",
    resetFromReport: "Reset from report?",
    confirmAgain: "Confirm once more?",
    resetButton: "Reset",
    noUsersAvailable: "No users with unreported accounts",
    resetConfirmDescription:
      "This operation will reset all unreported bills and generate a new report. This action cannot be undone.",
    resetConfirmEstablishmentDescription:
      "This operation will generate an establishment report from all personal reports for the selected date. This action cannot be undone.",
    resetConfirmEstablishmentWithPersonalDescription:
      "This operation will generate all personal reports and an establishment report for the selected date. This action cannot be undone.",
    unreportedUsersTitle: "There are unfinished reports for these waiters:",
    unreportedUsersTitleSingle: "There is an unfinished report for this waiter:",
    zReportConfirmTitle: "Reset daily financial report",
    zReportConfirmDescription:
      "Reset the daily financial report (Z-report)? This operation sends a Z-report to the fiscal device.",
    printPosConfirmTitle: "Print report",
    printPosConfirmDescription: "Print report on POS printer?",
  },

  personalReport: {
    title: "Personal report № {{number}} / {{date}}",
    testRestaurant: "Test Restaurant",
    turnoverByPayment: "Turnover by payment types",
    paymentType: "Payment type",
    bills: "Bills",
    alone: "Alone",
    inBrigade: "In brigade",
    cashPayment: "Cash",
    cardPayment: "Card",
    cancelledByStation: "Cancelled items by station",
    bar: "Bar",
    qty: "Qty.",
    firstLastOrder: "First / last order",
    cashDescription: "Cash inventory",
    coins: "cents",
    firstLastBill: "First / last bill",
    amountToReport: "Amount to report:",
    accepted: "Accepted:",
    created: "Created:",
    dateLabel: "Date:",
    printPos: "Print (POS)",
    printPosSuccess: "Report sent to the POS printer",
    printPosError: "Failed to print on the POS printer",
  },

  establishmentReport: {
    title: "Establishment report № {{number}} / {{date}}",
    includedPersonalReports: "Included personal reports: {{count}}",
    turnoverByPayment: "Turnover by payment types",
    paymentType: "Payment type",
    bills: "Bills",
    amount: "Amount",
    cashPayment: "Cash",
    cardPayment: "Card",
    otherPayment: "Other",
    firstLastOrder: "First / last order",
    firstLastBill: "First / last bill",
    stationBreakdown: "Breakdown by stations",
    station: "Station",
    totalBills: "Total bills",
    totalAmount: "Total amount",
    userBreakdown: "Breakdown by users",
    user: "User",
    reports: "Reports",
    cancelledByStation: "Cancelled products by station",
    totalForStation: "Total for {{name}}",
    // In warehouse mode the sellable is a recipe → same section, different name.
    soldAssortments: "Sold assortments",
    soldRecipes: "Sold recipes",
    assortment: "Assortment",
    recipe: "Recipe",
    printPos: "Print (POS)",
    printPosSuccess: "Report sent to the POS printer",
    printPosError: "Failed to print on the POS printer",
    resetWillFinalizePersonalReports:
      "Resetting the <b>Establishment report</b> will finalize all <b>Personal reports</b> for the day",
  },

  // Load deposit (Ordering → Options, ordering/components/tables/deposits/)
  depositLoad: {
    title: "Load a deposit",
    // The title once the deposit is saved (the number is its id)
    titleSaved: "Deposit No. {{id}}",
    name: "Name",
    namePlaceholder: "Guest or event name",
    amount: "Amount",
    description: "Description",
    descriptionPlaceholder: "Occasion, guests, phone…",
    validUntil: "Due date",
    validUntilPlaceholder: "No expiry",
    validUntilHint: "After this date the deposit is no longer valid. Empty — no expiry.",
    invoice: "Invoice",
    invoiceHint: "After the receipt, an invoice for the deposit opens — no new receipt",
    save: "Save deposit",
    saveError: "The deposit was not saved",
    // The deposit's fiscal receipt (deposits/useDepositSave.ts)
    savedWithReceipt: "Deposit No. {{id}} “{{name}}” ({{amount}} EUR) saved, the receipt is printed",
    savedWithoutReceipt:
      "Deposit No. {{id}} “{{name}}” ({{amount}} EUR) saved — no receipt (the method is not fiscalised)",
    receiptUnknown: "Deposit No. {{id}} “{{name}}” is saved, but the printer did not confirm the receipt",
    receiptFailed: "Deposit No. {{id}} “{{name}}” is saved, but the receipt was not printed: {{error}}",
    noAnswer: "The server did not answer — the deposit may already be saved. Press “Check deposit”.",
    checkDeposit: "Check deposit",
    checkReceipt: "Check receipt",
    issueAgain: "Print the receipt again",
    finishReceipt: "Finish the receipt",
    noticeNoAnswer:
      "The server did not answer — the deposit and its receipt may already be saved. “Check deposit” asks for the same record and does not create a second deposit or receipt.",
    noticeUnknown:
      "The deposit is saved, but the printer did not confirm the receipt. Check the paper and press “Check receipt” — the retry does not print a second receipt.",
    noticeFailed:
      "The deposit is saved, but the receipt was not printed ({{error}}). “Print the receipt again” tries once more; the receipt can also be printed later — from “Deposit” → “Use a deposit” or from the “Deposits” report.",
    noticeCardCharged:
      "The card is charged, but the receipt was not printed ({{error}}). Do NOT charge the card again — “Finish the receipt” prints it without a new charge.",
    hintWithoutReceipt: "This payment method is not fiscalised — the deposit is saved without a receipt.",
    hintCard: "By card: the terminal charges the amount first, then the receipt is printed.",
    closedWithoutReceipt:
      "The deposit is saved without a receipt — until then it cannot pay a bill. Print it with “Print receipt” in “Deposit” → “Use a deposit” or in the “Deposits” report.",
    closeConfirmTitle: "Close without confirmation?",
    closeConfirmBody:
      "The server did not confirm the save — the deposit may already be saved and its receipt printed. If you close, entering it again creates a new deposit. Press “Check deposit” first or look it up in the “Deposits” report.",
    closeConfirmOk: "Close",
    closeConfirmStay: "Back",
    errName: "Enter a name",
    errAmount: "Enter an amount above 0",
    errMethod: "Choose a payment method",
  },

  // "Deposit" in Options (tables/deposits/useDepositAction.tsx): "Create a new
  // deposit" / "Use a deposit" (attaches a deposit to the open table), the
  // table banner
  // “Invoicing” — the “Current bill” / “Closed bill” choice
  // (ordering/components/tables/invoicing/InvoicingChoiceModal.tsx)
  invoicingChoice: {
    title: "Invoicing",
    subtitle: "An invoice for the current table, for an already closed bill or a free one",
    subtitleSoftware: "An invoice for an already closed bill or a free one, without a bill",
    currentKicker: "Fiscal invoice on close",
    currentKickerTable: "Table {{table}} · fiscal invoice",
    current: "Current bill",
    currentHint: "{{amount}} EUR — enter the buyer and close; the invoice comes out of the fiscal device.",
    currentHintPrinter: "{{amount}} EUR — enter the buyer and close; the invoice comes out of “{{printer}}”.",
    currentHintNoTable: "An invoice for the selected table — it comes out of the fiscal device when the table is closed.",
    closedKicker: "Software invoice · A4 or POS",
    closed: "Closed bill",
    closedHint: "One or more closed bills — the lines and the payment come from them.",
    freeKicker: "Software invoice · no bill",
    free: "Free invoice",
    freeHint:
      "You enter the lines by hand; “On account” only (bank transfer), no fiscal receipt. Cash or card — an invoice against the bill; an advance — from “Deposits”.",
    unavailable: {
      noBill: "Select an open table",
      billClosed: "The table is closed",
      closing: "The table is being closed right now",
      zeroBill: "The bill is 0.00 — there is nothing to invoice",
      billDiscount:
        "The bill has a whole-bill discount — no fiscal invoice is issued for it. Remove the discount (Discounts → Whole bill) or close the bill without an invoice.",
    },
  },
  depositAction: {
    title: "Deposit",
    subtitle: "An advance payment made beforehand",
    load: "Create a new deposit",
    loadKicker: "The guest pays now",
    loadHint: "Take an advance payment and issue a fiscal receipt. Later the deposit is deducted from a bill.",
    use: "Use a deposit",
    useKicker: "Already paid deposit",
    useHint: "Pick a deposit — its amount is deducted from table {{table}}'s bill at Close.",
    useHintNoTable: "Its amount is deducted from the selected table's bill at Close.",
    useKickerAttached: "Attached to table {{table}}",
    useAttached: "Change or remove the deposit",
    useHintAttached: "{{deposit}} · {{amount}} EUR",
    useTitle: "Use a deposit — table {{table}}",
    unavailable: {
      noBill: "Select an open table",
      billClosed: "The table is closed",
      zeroBill: "The bill is 0.00 — nothing to deduct",
      closing: "The table is closing right now",
    },
    attachedElsewhere: "Attached to table {{table}}",
    attached: "Deposit No. {{id}} “{{name}}” is attached to table {{table}} — deducted at Close",
    attachError: "The deposit was not attached",
    remove: "Remove the deposit",
    removed: "Deposit No. {{id}} was removed from table {{table}} — it is free again",
    removeError: "The deposit was not removed",
  },

  // “Print receipt” for a deposit without one — the “Use a deposit” picker and the
  // “Deposits” report (hooks/useIssueDepositReceiptAction.tsx)
  depositReceipt: {
    issue: "Print receipt",
    issued: "The receipt for deposit No. {{id}} “{{name}}” is printed (receipt No. {{number}})",
    notRequired: "Deposit No. {{id}} “{{name}}” has no receipt — the method is not fiscalised",
    unknown:
      "Deposit No. {{id}} “{{name}}” — the printer did not confirm the receipt: {{error}}. Check the paper before trying again.",
    failed: "Deposit No. {{id}} “{{name}}” — the receipt was not printed: {{error}}",
    cardCharged:
      "Deposit No. {{id}} “{{name}}” — the card is charged, but the receipt was not printed: {{error}}. Do not charge the card again — “Print receipt” finishes it.",
    failedGeneric: "The receipt for the deposit was not printed",
    noAnswer:
      "Deposit No. {{id}} “{{name}}” — the server did not answer, the receipt may already be printed. Check the paper before trying again.",
    noDetails: "no details",
    confirmUnknownTitle: "The receipt may already be printed",
    confirmUnknownBody:
      "The last attempt for this deposit's receipt has no confirmed outcome. Check the paper: if there is a receipt, do not print a second one. On the same till the retry asks the device for the same receipt and does not print a second one.",
    notReadyHint: "Print the deposit's receipt first",
    notReadyCardCharged: "The card is charged, the receipt is not — print the receipt first (no new charge)",
  },

  // “Cancel” a deposit in the Deposits report (deposits/useCancelDepositAction.tsx)
  depositCancel: {
    action: "Cancel",
    title: "Cancel deposit No. {{id}}",
    summary: "{{name}} · {{amount}} — the deposit leaves “Use deposit” and can no longer pay a bill.",
    receiptStays:
      "Receipt 1 No. {{number}} stays in the fiscal device — there is no automatic storno. Return the money with a storno of this receipt: from the till portal (“Storno”) or from the device itself.",
    receiptStaysNoNumber:
      "The deposit's receipt stays in the device — there is no automatic storno. Return the money with a storno from the device.",
    noReceipt: "The deposit has no fiscal receipt — return the money without a storno.",
    receiptMaybe:
      "If a fiscal receipt was printed when the deposit was invoiced, it stays in the device — return the money with a storno from the device.",
    invoiceToo: "Invoice No. {{number}} for the deposit is cancelled together with it.",
    reasonLabel: "Reason (required)",
    reasonPlaceholder: "E.g. wrong amount, double deposit, cancelled event",
    confirm: "Cancel deposit",
    done: "Deposit No. {{id}} is cancelled",
    doneStorno:
      "Deposit No. {{id}} is cancelled. Don't forget the storno of Receipt 1 No. {{number}} — it returns the money.",
    failed: "The deposit was not cancelled",
    blockedAttached: "Attached to a table — remove the deposit from the table first",
    blockedReceipt: "The deposit's receipt has no confirmed outcome — use “Print receipt” first",
  },

  // The question before an irreversible action: what exactly happens (components/ConfirmConsequences.tsx)
  confirmConsequences: {
    intro: "What will happen:",
  },

  // “Void all” / “Restore” in “Operations history”
  // (billOperations/billActionConsequences.ts — mirrors the BE)
  billActionConfirm: {
    stornoTitle: "Void the whole bill “{{name}}”",
    stornoClosed: "Every item is voided — the bill stays closed and its total becomes 0.00.",
    stornoOpen: "Every item is voided — the table stays open and its total becomes 0.00.",
    stornoReceipt: "The fiscal device prints a storno of the whole receipt No. {{receipt}}, paid “In cash”.",
    creditNote:
      "The fiscal device prints a credit note to fiscal invoice No. {{number}} for the whole bill, paid “In cash”.",
    noReceipt: "The bill has no fiscal receipt — nothing is printed.",
    noReceiptOpen: "Nothing is printed — the table has no fiscal receipt yet.",
    cardRefundRrn:
      "Paid by card (RRN {{rrn}}): give the guest the money back in cash from the till, not through the terminal.",
    cardRefund: "Paid by card: give the guest the money back in cash from the till, not through the terminal.",
    stornoInvoice: "Invoice No. {{number}} is cancelled — if it also covers other bills, they become uninvoiced too.",
    depositReleased:
      "The deposit ({{amount}}) is not refunded by the storno — it is released and can pay a bill again.",
    tipVoided: "The tip ({{amount}}) is voided as well.",
    stornoFinal: "The storno cannot be undone.",
    stornoQuestion: "Are you sure you want to void the whole bill?",
    stornoOk: "Void all",
    restoreTitle: "Restore bill “{{name}}”",
    restoreReopens:
      "The bill reopens in “Ordering” with the same items — then you close it again with the right payment.",
    restoreInvoice: "Invoice No. {{number}} is cancelled — you can issue a new one after the new closing.",
    depositKept: "The deposit ({{amount}}) stays attached to the bill and is deducted again at closing.",
    tipRemoved: "The tip ({{amount}}) is removed — enter it again at closing.",
    restoreReports: "The bill leaves the reports it was in (they are recalculated).",
    restoreQuestion: "Are you sure you want to restore the bill?",
    restoreOk: "Restore",
  },

  // Invoice for a deposit (invoicing/InvoiceCreateModal.tsx with `deposit`)
  depositInvoice: {
    subtitle: "For the receipt of deposit No. {{id}}",
    notice: "Invoice for the deposit's existing receipt — no new receipt is printed.",
    errTotal: "The total of a deposit invoice must be the deposit amount — {{amount}} EUR",
    lockedDeduction:
      "The part paid with a deposit is deducted on its own line — the advance has its own receipt; the line cannot be changed or deleted.",
  },

  // Fiscal invoice on close — the extended fiscal receipt numbered from the
  // site's SHARED series (lib/fiscalInvoice.ts, docs/invoicing.md)
  fiscalInvoice: {
    // “Configuration → Companies” (companies/InvoiceIssuingSection.tsx, InvoiceDevicePin.tsx)
    company: {
      section: "Where invoices are issued",
      modes: {
        software: {
          title: "Software (A4 / POS printer)",
          hint: "As before — from “Invoicing”, on A4 or on the POS printer.",
        },
        fiscal: {
          title: "Fiscal device",
          hint: "Fiscal only — an invoice receipt when the bill is closed. No invoice for a bill already closed (only through “Restore”), for a payment through the card terminal or for an advance (deposit). On an FP-2000, “Both” gives the same fiscal invoices plus software ones after the receipt.",
        },
        both: {
          title: "Both (shared series)",
          hint: "Fiscal and software invoices — in one series.",
        },
      },
      devices:
        "The cloud gives the numbers — one series, without gaps. Datecs FP-2000 (and FP-800, FP-650, FP-700, FMP-10, SK1-21F, SK1-31F) — all three modes; FP-700X and the rest of the X/MX series — “Fiscal device” only. DP-2015, Tremol and Eltrade — no fiscal invoices.",
      // “Last invoice number” on the shared series (NumberingSection.tsx)
      numberBySeries:
        "The cloud gives the numbers. Here the number can only be raised — when the fiscal device has already issued invoices up to a higher number.",
      numberLockedByDevice:
        "The pinned device holds the next number — to change it, first “Release the device” under “Where invoices are issued”.",
      numberOnlyUp: "Upwards only — the series is at {{number}}.",
      // The confirmation when the counter is raised (CompanyModal → “Save”)
      skipTitle: "Skipping invoice numbers",
      skipOne:
        "Number {{number}} will count as issued — the fiscal device must have issued it already. The cloud will not hand it out. Continue?",
      skipMany:
        "You will skip {{count}} numbers ({{from}}–{{to}}) — the fiscal device must have issued them already. The cloud will not hand them out and the counter does not go back. Continue?",
      skipOk: "Save",
      // The device the invoices are pinned to (an X/MX series device)
      device: {
        title: "Invoice device",
        label: "FD {{serial}} (“{{name}}”)",
        labelNoSerial: "“{{name}}”",
        hintFiscal:
          "The company's invoices are issued from it only — an X/MX series device keeps the series itself.",
        hintOtherMode: "Matters only with “Fiscal device”.",
        release: "Release the device",
        confirm:
          "Release it only when the device is replaced. After saving, the next fiscal invoice pins the device that issues it. Do not issue more invoices from the old one — it holds one number of the series.",
        confirmOk: "Release",
        released: "Will be released on “Save”.",
        undo: "Undo",
      },
    },
    // The company is on “Fiscal device” — no software invoice (the text of the
    // BE refusal): “Invoicing”, “New deposit”, “Reports”
    mode: {
      softwareOff:
        "The company issues invoices from the fiscal device only (“Companies” → “Where invoices are issued”).",
      // “Closed bill” under “Fiscal device”: the way that exists.
      closedBillInFiscal:
        "The company issues invoices from the fiscal device only. An invoice for a bill already closed: “Restore” it and close it again from “Invoicing → Current bill” with the buyer.",
      selectSubtitle: "Invoices are issued by the fiscal device when the bill is closed.",
    },
    // Issuing a software invoice while the shared series is on
    software: {
      numberAuto: "assigned automatically",
    },
    // “Invoicing” → “Current bill” — the device availability (invoicing/useInvoicingFlow.tsx)
    picker: {
      unavailable: "A fiscal invoice on close is not possible.",
      loading: "Checking whether the fiscal device can issue the invoice…",
      loadError:
        "Could not check whether the fiscal device can issue the invoice. Close and reopen “Invoicing”.",
    },
    // The buyer (invoicing/FiscalInvoiceBuyerModal.tsx)
    buyer: {
      title: "Fiscal invoice · table {{table}}",
      subtitle: "Amount {{amount}} EUR — enter the buyer, then choose the payment",
      numberNext:
        "The number is given by the fiscal device on close — the next one is No. {{number}}.",
      numberFromDevice: "The number is given by the fiscal device on close.",
      toPayment: "To payment",
      // The till drives a card terminal (`card_terminal` of fiscal-availability) —
      // “Card” and “Mixed” are not offered in “Close”; here and in the “Close” strip.
      cardTerminal: "A fiscal invoice is not issued yet for a card payment through the terminal — cash only.",
      // The same when the company also issues software invoices (“Both”) — the way for a card.
      cardTerminalSoftware:
        "A fiscal invoice is not issued yet for a card payment through the terminal — cash only. Paying by card? Close without an invoice and issue a software invoice for the receipt (“Invoicing” → “Closed bill”).",
    },
    // “Close” with a fiscal invoice (payment/FiscalInvoiceBuyerStrip.tsx,
    // payment/backgroundBillClose.ts)
    close: {
      buyer: "Invoice to {{name}}",
      eik: "EIK {{eik}}",
      vat: "VAT No. {{vat}}",
      numberFromDevice: "Invoice No. will come from the device",
      success: "Table {{table}} — invoice No. {{invoice}} · receipt No. {{receipt}}",
      successNoReceipt: "Table {{table}} — invoice No. {{invoice}}",
      recordFailed:
        "The fiscal device issued the invoice, but it was not recorded in the cloud — see “Fiscal issues”.",
    },
    // “Invoices” report (reports/components/invoices/)
    report: {
      typeCreditNote: "Credit note",
    },
    // The preview (invoicing/FiscalInvoicePreviewNotice.tsx)
    preview: {
      titleCreditNote: "CREDIT NOTE",
      fiscalInvoice:
        "Fiscal invoice — the original is the receipt from the fiscal device. It is not printed or re-addressed here. It is not cancelled — it is credited with a credit note for the whole bill (“Credit note” in “Reports → Invoices”).",
      creditNote:
        "Credit note to invoice No. {{number}}, issued by the fiscal device — the original is the receipt. It is not printed or cancelled here.",
      deviceSerial: "FD {{serial}}",
      receipt: "Receipt No. {{receipt}}",
    },
    // Refund of a bill with a fiscal invoice (Reports → “Operations history”,
    // “Restore”)
    storno: {
      action: "Credit note",
      confirmTitle: "Credit note to invoice No. {{number}}",
      restoreContent:
        "The bill has fiscal invoice No. {{number}}: restoring it issues a credit note for the whole bill and reopens the bill.",
      changePaymentNote:
        "“Change payment method” closes it again with a plain receipt — issue a new fiscal invoice from “Invoicing”.",
      issued: "Credit note No. {{number}} · receipt No. {{receipt}}",
      issuedNoReceipt: "Credit note No. {{number}}",
      recordFailed:
        "The fiscal device issued the credit note, but it was not recorded in the cloud — see “Fiscal issues”.",
    },
    // “Credit note” in the fiscal invoice preview (Reports → “Invoices”,
    // invoicing/FiscalInvoiceCreditAction.tsx) — the choice between the two paths.
    credit: {
      subtitle:
        "A fiscal invoice is not cancelled — it is credited. The fiscal device issues the credit note for the whole bill; the money is refunded in cash.",
      reissueKicker: "Wrong buyer or details",
      reissue: "New invoice",
      reissueHint: "The bill is reopened (“Restore”) — close it with an invoice to the right buyer.",
      refundKicker: "The sale is undone",
      refund: "Refund",
      refundHint: "The whole bill is reversed (“Storno all”) and leaves the turnover.",
      loading: "Loading the bill…",
      billMissing: "The bill of this invoice was not found.",
      otherInvoice: "The bill already has another invoice No. {{number}}.",
      notClosed: "The bill is not closed — it has already been reopened.",
      alreadyReversed: "The bill is already reversed.",
      noStornoPermission: "You do not have the “Tables history” permission.",
    },
    // “Fiscal issues” (monitoring/fiscalIssues/FiscalInvoiceIssuesCard.tsx)
    issues: {
      title: "Fiscal device documents not recorded in “Invoices”",
      subtitle:
        "The fiscal invoice or credit note is on paper but not recorded in the cloud. Do not issue it again — check the receipt and contact support.",
      colDocument: "Document",
      colNumber: "Invoice No.",
      colBill: "Bill",
      colWhen: "When",
      colReason: "Reason",
      kindInvoice: "Fiscal invoice",
      kindCreditNote: "Credit note",
      // A receipt row whose retry prints a fiscal invoice
      retryHeld: "Fiscal invoice No. {{number}}",
      retryNew: "Fiscal invoice — with a new number",
    },
  },

  // The “Deposits” section of the daily reports (reports/ReportDepositsSection.tsx)
  reportDeposits: {
    section: "Deposits",
    colMethod: "Type",
    colCount: "Count",
    colAmount: "Amount",
    taken: "Deposit — {{method}}",
    total: "Total deposits",
    cashInAmountToReport: "Cash deposits (in the amount to hand over)",
    paidWithDeposit: "Paid with deposit (used)",
  },

  // Tips in the daily reports (paper, lists, Excel) — the numbers are BE
  // fields, the FE never computes "To hand over" (docs/reports.md §Бакшиш).
  reportTips: {
    section: "Tips",
    colKind: "Type",
    colAmount: "Amount",
    // The card: the amount, then the "expense share" and the "staff share"
    // (the settings' words); the BE paper labels match. There are no cash tips.
    card: "Card tips",
    cardExpense: "Card tips — expense share",
    cardDeducted: "Card tips — staff share (deducted)",
    cardSeparate: "Card tips — staff share (separate)",
    other: "Tips — {{method}}",
    otherShort: "Tips — other",
    otherUnlisted: "Tips — other method",
    total: "Total tips",
    amountToReport: "To hand over",
    staffTopUp: "Top-up to staff",
    totalPaid: "Total paid incl. tips",
    cardPaid: "Card total (terminal reconciliation)",
    methodPaid: "{{method}} total (reconciliation)",
    formulaHint: "cash − card-tip staff share",
    shareExpense: "expense share {{amount}}",
    shareStaffDeducted: "staff share (deducted) {{amount}}",
    shareStaffSeparate: "staff share (separate) {{amount}}",
    subCard: "card {{amount}}",
    subOther: "other {{amount}}",
  },

  // The tip of a bill in "Bills history" and "Operations history".
  billTips: {
    column: "Tip",
    method: "Tip method",
    line: "Tip {{amount}} · {{method}}",
    includesTip: "incl. tip {{amount}}",
  },

  // The tip line of an invoice (issuing, A4 preview, "Invoices" report).
  invoiceTips: {
    vatNote: "No VAT charged — voluntary tip.",
    totalTip: "Tip (no VAT)",
    locked:
      "The tip was recorded when the bill was closed and cannot be edited or deleted.",
    summarizeKeepsTip: "The tip stays on a separate line — it is never merged with the products.",
    pickerIncludesTip: "incl. tip {{amount}}",
    reportColumn: "Tip (no VAT)",
  },

  reports: {
    billsHistory: "Bills report",
    splitBills: "Split bills",
    cancellations: "Cancellations",
    personalReports: "Personal reports",
    establishmentReports: "Establishment reports",
  },

  billsHistory: {
    title: "Bills history",
    table: "Table",
    statusCol: "Status",
    open: "Open",
    closed: "Closed",
    openedAt: "Opened",
    closedAt: "Closed",
    duration: "Duration",
    hours: "h",
    minutes: "min",
    orders: "Orders",
    cancelledItems: "Cancelled",
    cancelledLabel: "Cancelled: {{count}}",
    payment: "Payment",
    cashLabel: "Cash: {{amount}}",
    cardLabel: "Card: {{amount}}",
    tables: "Tables",
    totalAmount: "Total amount",
    statusFilter: "Status",
    openFilter: "Open",
    closedFilter: "Closed",
    waiterFilter: "Waiter",
    paymentMethodFilter: "Payment method",
    fromDate: "From date",
    toDate: "To date",
    noData: "No bills data",
  },

  billsSplits: {
    title: "Split bills",
    dateCol: "Date",
    fromTable: "From table",
    toTable: "To table",
    products: "Products",
    totalSplits: "Total splits",
    transferredProducts: "Transferred products",
    fromTableFilter: "From table (ID)",
    toTableFilter: "To table (ID)",
    noData: "No split bills data",
  },

  cancellationsReport: {
    title: "Cancelled products report",
    bill: "Bill",
    product: "Product",
    qtyCol: "Qty",
    unitPrice: "Unit price",
    reason: "Reason",
    statusCol: "Status",
    defective: "✓ Defective",
    cancelled: "Cancelled",
    totalCancelled: "Total cancelled",
    defectiveItems: "Defective items",
    billIdFilter: "Bill ID",
    selectUsers: "Select users",
    defectiveOnly: "Defective products only",
    noData: "No cancelled products",
  },

  personalReportsPage: {
    title: "Personal reports",
    allUsers: "All users",
    userCol: "User",
    billsCol: "Bills",
    cashCol: "Cash",
    cardCol: "Card",
    totalCol: "Total amount",
    noData: "No reports found",
  },

  establishmentReportsPage: {
    title: "Establishment reports",
    personalReportsCol: "Personal reports",
    billsCol: "Bills",
    cashCol: "Cash",
    cardCol: "Card",
    totalCol: "Total amount",
    noData: "No reports found",
  },

  billDetails: {
    tableTitle: "Table {{name}}",
    openedAt: "Opened:",
    closedAt: "Closed:",
    noOrders: "No orders",
    fromTable: "from table {{name}}",
    split: "Split",
    commentLabel: "Comment:",
    totalOrders: "Total orders: {{orders}} | Products: {{items}}",
    cancelledCount: "Cancelled: {{count}}",
  },

  configuration: {
    title: "Configuration",
    monitors: "Monitors",
    devices: "Devices",
  },

  // The shared side menu of modules with per-permission pages (Configuration, Reservations)
  moduleSidebar: {
    expandMenu: "Expand menu",
    collapseMenu: "Collapse menu",
    collapse: "Collapse",
    backToModules: "Back to modules",
  },

  configSidebar: {
    monitors: "Monitors",
    devices: "Devices",
    ordering: "Ordering",
    paymentMethods: "Payment methods",
    companies: "Companies",
    workingHours: "Working hours",
    configurationLabel: "Configuration module",
    sectionMain: "Settings",
  },

  workingHours: {
    sectionKicker: "Week",
    sectionTitle: "Working hours by day",
    days: {
      monday: "Monday",
      tuesday: "Tuesday",
      wednesday: "Wednesday",
      thursday: "Thursday",
      friday: "Friday",
      saturday: "Saturday",
      sunday: "Sunday",
    },
    open: "Open",
    closed: "Day off",
    closedDay: "Day off",
    opensAt: "Opens",
    closesAt: "Closes",
    shiftLabel: "Shift {{number}}",
    addShift: "Add shift",
    removeShift: "Remove shift {{number}}",
    hintClosed: "Day off — no reservations",
    hintAfterMidnight: "Closes at {{time}} the next day",
    errors: {
      sameTime: "Opening and closing cannot be at the same time",
      sameTimeShift: "Shift {{shift}}: opening and closing cannot be at the same time",
      shiftsOverlap: "Shifts must follow one another without overlapping and fit within 24 hours",
      overlapsNextDay: "Closing after midnight must be before the next day opens",
    },
    save: "Save",
    discard: "Discard changes",
    saveSuccess: "Working hours saved",
    saveError: "Failed to save working hours",
    guide: {
      link: "How does it work?",
      text: "Working hours set the default hours in reports and the hours in which reservations are accepted.",
      modalTitle: "How working hours work",
      modalSubtitle: "Examples with three days of one week",
      tab: {
        reports: "Reports",
        reservations: "Reservations",
      },
      lead: {
        reports: "The report for one day runs from the opening to the closing of that day.",
        reservations: "A reservation can start from opening until 15 minutes before closing.",
      },
      columnDay: "Day",
      columnHours: "Working hours",
      columnResult: {
        reports: "Report period",
        reservations: "Reservation time",
      },
      afterMidnight: "closes after midnight",
      result: {
        reports: {
          tuesday: "Tue 10:00 → Tue 22:00",
          friday: "Fri 12:00 → Sat 02:00",
          monday: "Mon 00:00 → Mon 23:59",
        },
        reservations: {
          tuesday: "from 10:00 to 21:45",
          friday: "from 12:00 to 01:45",
          monday: "Not accepted",
        },
      },
      goodToKnow: "Good to know",
      notes: {
        reports: {
          afterMidnight: {
            title: "A bill after midnight",
            text: "Friday is open until 02:00. A bill paid on Saturday at 01:30 goes into the Friday report, not Saturday.",
          },
          shifts: {
            title: "A day with several shifts",
            text: "Thursday has two shifts: 11:00–15:00 and 18:00–23:00. The Thursday report runs from 11:00 to 23:00 — including the break between the shifts.",
          },
          closedAfterMidnight: {
            title: "A day off after a day that works late",
            text: "Sunday is open until 03:00 and Monday is a day off. The Monday report runs from 03:00 to 23:59 — bills until 03:00 stay in Sunday.",
          },
          notSet: {
            title: "No working hours saved",
            text: "Until working hours are saved, the day in reports runs from 06:00 to 05:59 the next day.",
          },
        },
        reservations: {
          lastSlot: {
            title: "Last time",
            text: "Wednesday is open 10:00–22:00. Reservation times are every 15 minutes and the last one is 21:45.",
          },
          shifts: {
            title: "A break between shifts",
            text: "Thursday has two shifts: 11:00–15:00 and 18:00–23:00. Reservations are accepted from 11:00 to 14:45 and from 18:00 to 22:45, but not during the break.",
          },
          afterMidnight: {
            title: "A day that works past midnight",
            text: "Friday is open until 02:00. When you pick Friday, you see all hours from 12 to 01 at once. If you pick a time after midnight (for example 01:15), the date at the top switches to Saturday by itself — the reservation is for Saturday at 01:15.",
          },
          closed: {
            title: "Day off",
            text: "Monday is a day off. The form shows “Day off — no reservations” and does not save.",
          },
          schedule: {
            title: "“Reservations table”",
            text: "The table for the day shows the hours from opening to closing. Wednesday 10:00–22:00 → from 10:00 to 22:00. Friday 12:00–02:00 → from 12:00 to 02:00 on one screen, without switching the day.",
          },
          notSet: {
            title: "No working hours saved",
            text: "Until working hours are saved, reservations are accepted from 11:00 to 22:45 every day.",
          },
        },
      },
      close: "Close",
    },
  },

  reservationsSidebar: {
    reservations: "Reservations",
    table: "Reservations table",
    restaurantSchema: "Floor plan",
    tablesConfig: "Table setup",
    moduleLabel: "Reservations module",
  },

  nonFiscalDisplay: {
    // Database optimization
    cleanupTitle: "Database optimization",
    cleanupRun: "Run",
    cleanupConfirmTitle: "Are you sure?",
    cleanupConfirmOk: "Yes",
    cleanupConfirmCancel: "No",
    cleanupNoBills: "No bills to delete",
    cleanupSuccess: "Deleted bills: {{count}}",
    cleanupError: "Error cleaning up the database",
    // Codes come from the BE (`detail`); the texts live only here.
    cleanupErrors: {
      cleanup_failed:
        "Cleanup did not run. Nothing was deleted — check the server log.",
      cleanup_requires_fiscal_restaurant:
        "Cleanup is available only for a restaurant with a fiscal printer.",
      cleanup_no_restaurant_in_token:
        "No restaurant identifier found — please sign in again.",
      cleanup_requires_access:
        "The optimization is only available in test mode.",
      cleanup_no_session: "No session token found — please sign in again.",
      cleanup_no_tills:
        "Not every till is reporting in. The optimization needs a connection to each one — nothing was deleted.",
      cleanup_till_unreachable:
        "A till refused or did not answer. It may be in local mode or holding an unfinished payment — check it and try again.",
      cleanup_renumber_failed:
        "The bills were deleted and the tills cleared, but the numbers were not rearranged. Run the optimization again.",
      // "Download database" (POST /cleanup/database-download)
      database_download_wrong_code:
        "Wrong code. Nothing was deleted and nothing was downloaded.",
      database_download_not_consecutive:
        "The optimization ran, but the bill numbers are not consecutive or an establishment report was generated meanwhile. The database was not downloaded — try again.",
      database_download_pg_dump_missing:
        "The server cannot download the database (the tool is missing or cannot reach it). Nothing was deleted — contact support.",
      database_download_failed:
        "The optimization ran, but the database could not be downloaded. Try again.",
    },
    // Local mode: the operation goes through the cloud and through every till.
    cleanupOfflineHint:
      "This terminal is running in local mode. The optimization is only possible with a connection to the cloud.",
    cleanupResultTitle: "Optimization result",
    cleanupResultClose: "Close",
    cleanupDeletedBills: "Deleted bills",
    cleanupDeletedReports: "Deleted reports",
    cleanupRecalculated: "Recalculated reports",
    cleanupRemaining: "Remaining bills",
    cleanupNewMaxBillId: "Last bill number",
    cleanupMessage: "Message",
    // Download database — in an archived restaurant the same optimization
    // first, then the file (no description); otherwise just the file (…Plain)
    downloadTitle: "Download database",
    downloadDescPlain: "Downloads a copy of the database as a file",
    downloadRun: "Download",
    downloadHintPlain:
      "Downloads right away.",
    downloadOfflineHint:
      "The terminal is in local mode. Downloading the database is only possible with a connection to the cloud.",
    downloadOptimizing: "Optimizing…",
    downloadDownloading: "Downloading…",
    downloadSuccess: "Database downloaded: {{filename}}",
    downloadError: "Error downloading the database",
  },

  orderingSettings: {
    sectionTablesKicker: "Tables",
    sectionTablesTitle: "Numbering, order and visibility of tables",
    sectionOrdersKicker: "Orders",
    sectionOrdersTitle: "Behaviour after “Order”",
    sectionNotificationsKicker: "Notifications",
    sectionNotificationsTitle: "Messages in Ordering",
    sectionCatalogKicker: "Catalog",
    sectionCatalogTitle: "Product view and order",
    sectionCodeLoginKicker: "Sign-in and sign-out",
    sectionCodeLoginTitle: "Staff sign-out to code login",
    sectionMaintenanceKicker: "Maintenance",
    sectionMaintenanceTitle: "Database",
    sharedNumberingName: "Shared table numbering",
    sharedNumberingDesc: "Whether table numbers are unique for the whole restaurant",
    optionSharedNumberingOff: "Off",
    hintSharedNumberingOff:
      "Each waiter has their own numbering — two waiters can both have table “1” at the same time",
    optionSharedNumberingOn: "On",
    hintSharedNumberingOn:
      "Numbers are unique for the whole restaurant — an occupied table “1” errors for other waiters too",
    tableOrderName: "Order of tables in the list",
    tableOrderDesc: "How the open tables are sorted",
    optionTableOrderChronological: "Chronological",
    hintTableOrderChronological:
      "By opening time — the oldest table comes first, a new one goes to the bottom",
    optionTableOrderAlphanumeric: "By number and name",
    hintTableOrderAlphanumeric:
      "Numbered tables first, by number (1, 2, 10, 33), then named tables alphabetically",
    nonFiscalName: "Unfinalized tables",
    nonFiscalDesc: "When bills that are not yet fiscalized are shown",
    optionNonFiscalNever: "No",
    hintNonFiscalNever: "Bills disappear after closing",
    optionNonFiscalAlways: "Always",
    hintNonFiscalAlways: "Bills remain until fiscalized",
    optionNonFiscalOnDemand: "When needed",
    hintNonFiscalOnDemand: "Dynamic show/hide with ALT + F1 command",
    quickCloseName: "Close immediately after ordering",
    quickCloseDesc: "What happens to the table after “Order”",
    optionQuickCloseOff: "Off",
    hintQuickCloseOff:
      "New table asks for a number, and the order is saved without closing",
    optionQuickCloseOn: "On",
    hintQuickCloseOn:
      "New table auto-picks the smallest free number, and after “Order” the “Close” dialog opens",
    successNotificationsName: "Success messages",
    successNotificationsDesc: "The green “Success…” messages after every action",
    optionSuccessNotificationsOff: "Off",
    hintSuccessNotificationsOff:
      "The green “Success…” messages in Ordering are not shown — errors and warnings still are",
    optionSuccessNotificationsOn: "On",
    hintSuccessNotificationsOn:
      "A short message appears after every successful action (products added, customer saved, table closed…)",
    catalogFilterName: "Bar above the catalog",
    catalogFilterDesc:
      "Which filter sits in the bar above the products; the other goes to the right panel, above the order",
    optionFilterGroups: "Groups",
    hintFilterGroups:
      "Top: groups (Burgers, Salads, Grill…). Right, above the order: stations (Grill, Bar…)",
    optionFilterStations: "Stations",
    hintFilterStations:
      "Top: stations (Grill, Bar…). Right, above the order: groups (Burgers, Salads, Grill…)",
    illustrationGroups: "Groups",
    illustrationStations: "Stations",
    illustrationCatalog: "Catalog",
    illustrationOrder: "Order",
    colorByNameAssortments: "Coloring of assortments",
    colorByNameRecipes: "Coloring of recipes",
    colorByDesc: "Where the color of rows and cards comes from",
    optionColorGroup: "By group",
    hintColorGroupAssortments: "Each assortment carries its group's color",
    hintColorGroupRecipes: "Each recipe carries its group's color",
    optionColorStation: "By station",
    hintColorStationAssortments: "Each assortment carries its station's color",
    hintColorStationRecipes: "Each recipe carries its station's color",
    optionColorNone: "No coloring",
    hintColorNone: "Rows and cards have no colored background",
    searchFieldName: "Search box above the catalog",
    searchFieldDesc: "Shows or hides the search box on desktop; phones and tablets always have it",
    optionSearchFieldOff: "Hidden",
    hintSearchFieldOff:
      "You search straight from the keyboard — what you type pops up over the catalog and fades after a second",
    optionSearchFieldOn: "Shown",
    hintSearchFieldOn:
      "The field sits above the catalog and takes typing; − and + inside it still change the focused product's quantity",
    searchScopeName: "Search with a group open",
    searchScopeDesc:
      "Where the search looks when a group is open in the catalog (e.g. “Drinks”) — the lunch menu editor follows it too. With no group open it always searches the whole menu",
    optionSearchScopeMenu: "Whole menu",
    hintSearchScopeMenu:
      "E.g. in “Drinks” you type “lemon” → “Lemonade” and “Lemon chicken” (Grill) show up",
    optionSearchScopeGroup: "Only the group",
    hintSearchScopeGroup:
      "E.g. in “Drinks” you type “lemon” → only “Lemonade” shows up and you stay in “Drinks”",
    guestCountName: "Guest count",
    guestCountDesc: "Whether and when Ordering asks how many guests are at the table",
    optionGuestCountOff: "No",
    hintGuestCountOff: "The guest count is not entered",
    optionGuestCountNewTable: "On new table",
    hintGuestCountNewTable:
      "A “Guests” dialog before the “New table” dialog. Without a count the table is not opened",
    optionGuestCountClose: "On closing",
    hintGuestCountClose:
      "A “Guests” dialog before the “Close” dialog. Without a count the table is not closed",
    illustrationTableFlow: "Table flow",
    suggestTableNameName: "Suggest a new table number",
    suggestTableNameDesc:
      "Whether the “New table” dialog opens with the first free number filled in. Does not apply with “Close immediately after ordering” — there is no dialog then and the number is picked automatically",
    optionSuggestTableNameOff: "Off",
    hintSuggestTableNameOff:
      "The “New table” dialog opens empty and the number is typed in",
    optionSuggestTableNameOn: "On",
    hintSuggestTableNameOn:
      "The “New table” dialog opens with the first free number — OK accepts it, and the first digit typed replaces it",
    autoCodeLogoutName: "Automatic sign-out to code",
    autoCodeLogoutDesc:
      "If nobody uses the terminal, it returns to the code screen by itself. Applies only to people signed in with a code",
    optionAutoCodeLogoutOff: "Off",
    hintAutoCodeLogoutOff:
      "Staff stay signed in until they sign out themselves",
    optionAutoCodeLogoutOn: "On",
    hintAutoCodeLogoutOn:
      "The terminal returns to the code screen after the set seconds without activity",
    autoCodeLogoutSecondsName: "After how many seconds",
    autoCodeLogoutSecondsDesc:
      "Seconds without a touch, click or key",
    autoCodeLogoutSecondsError: "Enter a number from 10 to 3600",
    autoCodeLogoutSecondsHint: "From 10 to 3600. Saved with Enter or when you leave the field",
    autoCodeLogoutSecondsUnit: "sec",
    sectionCloseKicker: "Closing",
    sectionCloseTitle: "The “Close” dialog",
    cashChangeName: "Change for cash payments",
    cashChangeDesc:
      "Shows “Received” and “Change” when closing with “Cash”. Change applies to cash payments only — it is never shown for card or the other methods",
    optionCashChangeOff: "Off",
    hintCashChangeOff:
      "“Cash” looks like “Card” — only the amount to pay, without banknotes, “Received” and “Change”",
    optionCashChangeOn: "On",
    hintCashChangeOn:
      "With “Cash” you pick a banknote or enter the amount received, and the change due is shown",
    saveSuccess: "Setting saved successfully",
    saveError: "Error saving the setting",
  },

  monitorsConfig: {
    title: "Monitors",
    shpicker: "Expediter",
    shpickerHint: "This user is an expediter — the screen aggregates cooked items from all stations and issues them to waiters.",
    waiterMonitor: "Waiter",
    waiterMonitorHint: "This user is a waiter monitor — the screen shows the dishes of its stations and serves them (\"Complete\"), like serving in Ordering.",
    // "Sees from" (VisibleFromStatusPicker.tsx): the cards are the statuses
    // themselves — "Expediter sees from [Cooked]"; the descriptions are examples.
    stepShpickerVisibleFrom: "Expediter sees from",
    stepWaiterVisibleFrom: "Waiter sees from",
    visibleFromShort: "Sees from",
    stepWaiterServeFrom: "Waiter serves from",
    serveFromShort: "Serves from",
    statusNotStarted: "Not started",
    statusInProgress: "In progress",
    statusCooked: "Cooked",
    statusIssued: "Issued",
    shpickerVisibleFromNotStartedDesc: "Sees every dish right from the order (with its status)",
    shpickerVisibleFromInProgressDesc: "Sees the dish as soon as the cook starts it",
    shpickerVisibleFromCookedDesc: "Sees the dish as soon as the kitchen completes it (as before)",
    waiterVisibleFromNotStartedDesc: "Sees every dish right from the order",
    waiterVisibleFromInProgressDesc: "Sees the dish as soon as the cook starts it",
    waiterVisibleFromCookedDesc: "Sees the dish as soon as the kitchen completes it",
    waiterVisibleFromIssuedDesc: "Sees the dish as soon as the expediter issues it",
    waiterServeFromNotStartedDesc: "Serves right away, without waiting for the kitchen",
    waiterServeFromInProgressDesc: "Serves as soon as the cook starts",
    waiterServeFromCookedDesc: "Serves as soon as the kitchen completes it",
    waiterServeFromIssuedDesc: "Serves as soon as the expediter issues it",
    waiterServeFromNoShpickerReason: "No expediter — dishes never reach \"Issued\"",
    waiterServeFromHint:
      "With an expediter usually \"Issued\" (once the expediter issues it), without one — \"Cooked\". Applies to this monitor's stations — on its screen and in Ordering. It never waits for a status the dish cannot reach: a station with a kitchen monitor but no expediter — served after \"Cooked\"; a station only on the waiter monitor (no kitchen monitor or expediter) — served without checks. A dish from a station without a waiter monitor — served without checks.",
    waiterVisibleFromHint:
      "Usually one status after the expediter: on \"Complete\" the expediter sets \"Issued\" → the waiter sees from \"Issued\". If the restaurant has no expediter, choose \"Cooked\": the waiter sees the dish as soon as the kitchen completes it.",
    stepColorBy: "Coloring",
    colorByShort: "Coloring",
    colorByTable: "By table",
    colorByTableDesc: "Rows of one table share a color",
    colorByWaiter: "By waiter",
    colorByWaiterDesc: "Rows of one waiter share a color",
    colorByStation: "By station",
    colorByStationDesc: "Uses the station colors from the configuration",
    colorByNone: "None",
    colorByNoneDesc: "No color grouping",
    colorByStatus: "By status",
    colorByStatusDesc: "Rows cooked by the kitchen (\"Cooked\") are in a chosen color",
    colorByStatusHint:
      "Rows cooked by the kitchen (\"Cooked\") are in the chosen color — the expediter sees at once what to issue. The others stay uncolored. Most useful when it also sees the earlier statuses (\"Expediter sees from\").",
    readyColorLabel: "Color of ready rows",
    readyColorExample: "Example",
    readyColorExampleReady: "Pizza Margherita",
    readyColorExampleWaiting: "Caesar salad",
    name: "Name",
    stations: "Stations",
    actions: "Actions",
    editMonitor: "Edit monitor",
    deleteMonitor: "Delete monitor",
    deleteConfirm: "Are you sure?",
    assignUsers: "Users",
    loadingError: "Loading error",
    loadingErrorDesc: "Please try again later",
    configuredMonitors: "Configured monitors",
    deleteSuccess: "Monitor configuration deleted successfully",
    deleteError: "Error deleting configuration",
    deleteConfirmTitle: "Confirm deletion",
    deleteConfirmContent:
      'Are you sure you want to delete the monitor configuration for "{{name}}"?',
    editSuccess: "Monitor configuration updated successfully",
    editError: "Error editing configuration",
    editTitle: "Edit monitor",
    editConfirmTitle: "Confirm edit",
    editConfirmContent: "Are you sure you want to save the changes?",
    saveChanges: "Save changes",
    selectMonitorPlaceholder: "Select a monitor",
    noStations: "No available stations",
    monitorConfig: "Monitor configuration",
    selectUserAndStation: "Please select a user and at least one station",
    saveSuccess: "Monitor configuration saved successfully",
    saveError: "Error saving configuration",
    saveConfig: "Save configuration",
    selected: "Selected: {{count}}",
    aggregatedQuantities: "Aggregated quantities",
    // Redesign keys
    configTitle: "Link a user to stations",
    // The three sections by type (monitorTypeSections.ts): panel title = first
    // column title, kicker, button, empty list, modal kicker.
    kitchenSection: "Kitchen",
    listKickerKitchen: "Kitchen monitors list",
    addKitchenMonitor: "Add kitchen monitor",
    noKitchenMonitors: "No kitchen monitors configured",
    configKickerKitchen: "New kitchen monitor",
    shpickerSection: "Expediter",
    listKickerShpickers: "Expediter monitors list",
    addShpicker: "Add expediter monitor",
    noShpickers: "No expediter monitors configured",
    configKickerShpicker: "New expediter monitor",
    waiterSection: "Waiter",
    listKickerWaiters: "Waiter monitors list",
    addWaiterMonitor: "Add waiter monitor",
    noWaiterMonitors: "No waiter monitors configured",
    configKickerWaiter: "New waiter monitor",
    // The empty "User" list — without it antd shows "No data".
    noFreeUsers: "No free users of this type",
    // Numbers are added dynamically in MonitorFormModal (expediter adds a step).
    stepUser: "User",
    stepScreen: "Screen",
    stepStations: "Stations",
    stepView: "View",
    stepOptions: "Options",
    screenSingle: "Single screen",
    screenSingleDesc: "All stations on one shared screen",
    screenSplit: "Split in 2",
    screenSplitDesc: "Left and right halves with their own stations",
    leftHalf: "Left half",
    rightHalf: "Right half",
    leftHalfShort: "L",
    rightHalfShort: "R",
    selectBothHalves: "With a split screen, pick stations for both halves",
    aggregatedShort: "Grouping",
    completeFullQuantity: "Complete full quantity",
    completeFullQuantityShort: "Full quantity",
    viewShort: "View",
    viewGrid: "Grid",
    viewList: "List",
    viewGridDesc: "Products are arranged in columns",
    viewListDesc: "Products are arranged in a single list",
    viewBoard: "Table",
    viewBoardDesc: "A grid of cells per order; the size is chosen on the screen itself",
    // "Box order" — Grid and Table views (BoxOrderStep.tsx, MonitorBoxOrder).
    stepBoxOrder: "Box order",
    boxOrderDown: "Top to bottom",
    boxOrderDownDesc: "Below the previous one",
    boxOrderAcross: "Left to right",
    boxOrderAcrossDesc: "Next to the previous one",
    boxOrderHint:
      "Applies to Grid and Table. Top to bottom (as before) — column by column; left to right — row by row, like reading.",
    boxOrderListLead: "Applies only to Grid and Table",
    boxOrderListReason: "In List the rows are one below another.",
    // Per-monitor sound — the keys live in src/pages/monitors/monitorSounds.ts.
    stepSound: "Sound",
    soundShort: "Sound",
    soundPreview: "Play",
    soundClassic: "Classic",
    soundBell: "Service bell",
    soundDoubleBell: "Double bell",
    soundTriangle: "Triangle",
    soundChime: "Chimes",
    soundKnock: "Knock",
    soundBuzzer: "Buzzer",
    soundBeeper: "Pager",
    soundAlarm: "Alarm",
    soundRing: "Phone",
    stepSelectionAfterAction: "Selection after action",
    selectionAfterActionShort: "Selection",
    selectionStay: "Stays in place",
    selectionStayDesc: "Moves to the row that takes the completed one's place",
    selectionFirst: "First row",
    selectionFirstDesc: "Goes to the first row, top left",
    selectionOrder: "First of the order",
    selectionOrderDesc: "Goes to the first row of the same order",
    selectionNone: "No selection",
    selectionNoneDesc: "Cleared; click or ↓ selects again",
  },

  devices: {
    name: "Name",
    uniqueNumber: "Unique number",
    type: "Type",
    actions: "Actions",
    loadingError: "Loading error",
    loadingErrorDesc: "Please try again later",
    users: "Access & Workpoints",
    connectedClients: "FiscalAPI",
    connectedClientsCount: "{{count}} connections",
    connectedClientsConflict:
      "Two installations print on this device — one receipt will come out twice.",
    connectedClientsUnknown:
      "No data. Older FiscalAPI versions do not announce themselves — update them to see which computer prints here.",
    connectedClientsStale: "not connected",
    fiscalapiDownload: "Download the latest FiscalAPI",
    fiscalapiUpdateButton: "Update to v{{version}}",
    fiscalapiUpdateStarted:
      "Update started — the installation will restart within minutes and the new version will show up here.",
    fiscalapiUpdateFailed: "Update did not start",
    clock: {
      open: "Device clock",
      title: "Device clock",
      close: "Close",
      reading: "Reading the device clock…",
      deviceTime: "Device",
      referenceTime: "Exact time",
      exact: "Exact to the second",
      small: "Off by {{duration}} — within normal",
      ahead: "Ahead by {{duration}}",
      behind: "Behind by {{duration}}",
      hours: "{{count}} h",
      minutes: "{{count}} min",
      seconds: "{{count}} s",
      referenceUnverified:
        "The exact time is not confirmed — the till's computer and the time servers disagree or the till has no internet. Syncing will be refused.",
      readFailed: "The device clock could not be read.",
      readAgain: "Read again",
      sync: "Sync to the exact time",
      syncing: "Syncing…",
      syncFailed: "The sync did not go through.",
      syncedExact: "Synced — the device shows the exact time.",
      syncedFromAhead: "Synced — the device was ahead by {{duration}}.",
      syncedFromBehind: "Synced — the device was behind by {{duration}}.",
      syncUnverified:
        "The time was sent, but the new reading differs — read the clock again in a minute.",
      backwardBlocked:
        "The device does not accept a time before its last receipt. Syncing will be possible after {{time}} on its clock — the safest time is in the morning, before the first receipt. The clock was not changed.",
      referenceUnverifiedRefusal:
        "The exact time cannot be confirmed — the till's computer and the time servers disagree or the till has no internet. The clock was not changed. Check the computer's clock or sync from the till's portal.",
      hint: "Syncs to the till's exact time (the computer, the update server and an internet clock in agreement). The device accepts an earlier time only after its last receipt.",
    },
    deleteSuccess: "Device deleted successfully",
    deleteError: "Error deleting device",
    deleteConfirmTitle: "Confirm deletion",
    deleteConfirmContent: 'Are you sure you want to delete device "{{name}}"?',
    editSuccess: "Device updated successfully",
    editError: "Error editing device",
    editConfirmTitle: "Confirm edit",
    editConfirmContent: "Are you sure you want to save the changes?",
    saveChanges: "Save changes",
    pickerSearch: "Search by name",
    pickerWorkpointsCount_one: "{{count}} workpoint",
    pickerWorkpointsCount_other: "{{count}} workpoints",
    pickerUsersCount_one: "{{count}} user",
    pickerUsersCount_other: "{{count}} users",
    createSuccess: "Device created successfully",
    createError: "Error creating device",
    addDevice: "Add device",
    // Save attempt without a name / an account (DeviceModalShell — the button stays enabled).
    nameRequired: "Enter the device name",
    accountsRequired: "Choose at least one workpoint or user",
    posAssignmentRequired: "Choose at least one workpoint, user or station",
    copySuccess: "Connection string copied",
    typeFiscal: "Fiscal printer",
    typePos: "POS printer",
    typePaymentTerminal: "Payment terminal",
    // Redesign
    // The three page sections — one per device type.
    posSectionKicker: "Order printing",
    fiscalSectionKicker: "Fiscalization",
    terminalSectionKicker: "Card payments",
    posDevices: "POS devices",
    fiscalDevices: "Fiscal printers",
    terminalDevices: "Payment terminals",
    addPosDevice: "Add POS device",
    addFiscalDevice: "Add fiscal printer",
    addTerminalDevice: "Add terminal",
    noPosDevices: "No configured POS devices",
    noFiscalDevices: "No configured fiscal printers",
    noTerminalDevices: "No configured payment terminals",
    deactivateWarning_one: "On save, access will be removed for:",
    deactivateWarning_other: "On save, {{summary}} will be removed:",
    deactivateUndo: "Undo",
    deactivateWarningAnd: " and ",
    orgUrlCopied: "Address copied",
    noStations: "No available stations",
    printLanguage: "Print language",
    printLanguagePlaceholder: "Bulgarian (default)",
    noLanguages: "No available languages",
    summarizeReceipt: "Summarized receipts",
    summarizeReceiptHint:
      "Identical products on the bill are printed on the fiscal receipt as one line with the total quantity.",
    summarizeReceiptOff: "Off",
    summarizeReceiptOn: "On",
    summarizeReceiptExampleWater: "Mineral water",
    summarizeReceiptExampleCola: "Cola",
    summarizeReceiptExampleCoffee: "Coffee",
    summarizeReceiptExampleCoffeeMilk: "Coffee + milk",
    summarizeReceiptActive: "Active",
    summarizeReceiptLines_one: "{{count}} line",
    summarizeReceiptLines_other: "{{count}} lines",
    nonFiscalCopy: "Non-fiscal copy on POS",
    nonFiscalCopyHint:
      "After every successful fiscal receipt, the POS printer of the same till prints a non-fiscal copy — handy for deliveries, to check that everything is packed. No POS printer, no copy.",
    paymentMethods: "Payment methods",
    stations: "Stations",
    stationsCount_one: "{{count}} station",
    stationsCount_other: "{{count}} stations",
    usersCount_one: "{{count}} user",
    usersCount_other: "{{count}} users",
    searchPlaceholder: "Search by name, GUID or user",
    noPaymentMethodsForDevice: "—",
    // The payment terminal modal (create and edit) — TerminalModal.
    terminalModal: {
      kickerEdit: "Edit device · Payment terminal",
      kickerCreate: "New device · Payment terminal",
      accountsTitle: "Card payments",
      accountsDesc:
        "Workpoints and users attached to this terminal. An account can be attached to one terminal only.",
      accountsDescShort: "Accounts attached to this terminal.",
      takenNote_one:
        "{{count}} account is already attached to {{where}} and can't be selected here. To move it, first remove it from the other terminal.",
      takenNote_other:
        "{{count}} accounts are already attached to {{where}} and can't be selected here. To move them, first remove them from the other terminal.",
      takenWhereMany: "other terminals",
      summaryAccounts: "Card payments:",
      summaryCount_one: "{{count}} account",
      summaryCount_other: "{{count}} accounts",
    },
    // The fiscal printer modal (create and edit) — FiscalPrinterModal.
    fiscalModal: {
      kickerEdit: "Edit device · Fiscal printer",
      kickerCreate: "New device · Fiscal printer",
      accountsTitle: "Receipts",
      accountsDesc:
        "Fiscal receipts of the selected workpoints and users are issued by this device. An account fiscalizes on one device only.",
      accountsDescShort: "Receipts of the selected accounts are issued here.",
      inactiveBannerTitle: "The device is off.",
      inactiveBannerText:
        "On save all attached workpoints and users are removed and become free for other fiscal devices.",
      takenNote_one:
        "{{count}} account already fiscalizes on {{where}} and can't be selected here. To move it, first remove it from the other device.",
      takenNote_other:
        "{{count}} accounts already fiscalize on {{where}} and can't be selected here. To move them, first remove them from the other device.",
      takenWhereMany: "other fiscal devices",
      methodsTitle: "Payment methods",
      methodsDesc:
        "Pick a device slot or “БЕЗ ФИСКАЛИЗИРАНЕ” (no fiscalization) for each method. “Not used” = the method isn't used on this device.",
      methodsDescShort: "A device slot or “БЕЗ ФИСКАЛИЗИРАНЕ” (no fiscalization) for each method.",
      methodLabel: "Method",
      slotLabel: "Device slot",
      fiscalizedPill: "Fiscalized: {{count}}",
      notUsed: "Not used",
      hintSlot: "Fiscal · {{slot}}",
      hintNoFiscal: "No fiscal receipt",
      hintNotUsed: "Not used here",
      hintOnAccount: "No fiscal receipt · always",
      onAccountInfo:
        "“On account” appears when closing a bill only if a client with the “On-account payment” right is selected for the bill (Organization → “Configuration” → “Clients”). It never issues a fiscal receipt, so here it is always “БЕЗ ФИСКАЛИЗИРАНЕ”.",
      receiptTitle: "Fiscal receipt",
      receiptDescShort: "Merged lines and a non-fiscal POS copy.",
      tabAccounts: "Accounts",
      tabMethods: "Payment",
      tabReceipt: "Receipt",
      tabOff: "off",
      summaryReceipts: "Receipts:",
      summaryMethods: "Methods:",
      summaryOff: "off",
      summaryAccounts_one: "{{count}} account",
      summaryAccounts_other: "{{count}} accounts",
      summaryFiscalized: "{{count}} fiscalized",
    },
    // The POS printer modal (create and edit) — PosPrinterModal.
    posModal: {
      kickerEdit: "Edit device · POS printer",
      kickerCreate: "New device · POS printer",
      namePlaceholder: "Name, e.g. Bar",
      editName: "Edit name",
      nameAria: "Printer name",
      close: "Close",
      cancel: "Cancel",
      docsTitle: "Till documents",
      docsDesc:
        "Documents of the selected workpoints and users come out on this printer. An account prints on one POS printer only.",
      docsDescShort: "Receipts of the selected accounts print here.",
      kitchenDescShort: "Orders from the selected stations print here as kitchen tickets.",
      tabDocuments: "Documents",
      tabKitchen: "Kitchen",
      tabOff: "off",
      active: "Active",
      inactive: "Inactive",
      inactiveBannerTitle: "Documents are off.",
      inactiveBannerText:
        "On save all attached workpoints and users are removed and become free for other POS printers. Kitchen tickets keep printing.",
      accountsLabel: "Workpoints and users",
      selectedPill_one: "{{count}} selected",
      selectedPill_other: "{{count}} selected",
      allPill: "All · {{count}}",
      allAccounts: "All accounts",
      allAccountsNote: "All <b>{{count}}</b> free workpoints and users print here.",
      searchPlaceholder: "Search workpoint or user",
      filterAll: "All",
      filterWorkpoints: "Workpoints",
      filterUsers: "Users",
      filterSelected: "Selected",
      workpoint: "Workpoint",
      user: "User",
      noAccounts: "No accounts found",
      shownOf: "Showing {{shown}} of {{total}}",
      selectShown: "Select shown",
      clear: "Clear",
      takenNote_one:
        "{{count}} account already prints documents on {{where}} and can't be selected here. To move it, first remove it from the other printer.",
      takenNote_other:
        "{{count}} accounts already print documents on {{where}} and can't be selected here. To move them, first remove them from the other printer.",
      takenWhereMany: "other POS printers",
      printCashNoReceipt: "Print from POS printer",
      printCashNoReceiptHint:
        "On — the slip prints on the POS printer. Off — it does not.",
      kitchenTitle: "Kitchen tickets",
      kitchenDesc:
        "Ordered products from the selected stations come out on this printer as kitchen tickets — no matter which account placed the order.",
      kitchenExampleTitle: "Ticket example",
      examplePrinterNamed: "Printer “{{name}}”",
      exampleThisPrinter: "This printer",
      exampleListens: "{{printer}} listens to <b>{{stations}}</b>.",
      exampleNoStations: "{{printer}} listens to no stations — it prints no kitchen tickets.",
      exampleSplit: "An order with products for both printers is split into two tickets — one on each.",
      exampleSingle: "An order with products from these stations prints here as a kitchen ticket — no matter which account placed it.",
      exampleListAnd: " and ",
      exampleListMore: "{{list}} and {{count}} more",
      ticketTitle: "KITCHEN TICKET",
      ticketMeta: "Table 12 · Maria · 12:41",
      ticketItem1: "Mojito",
      ticketMod1: "+ no sugar",
      ticketItem2: "Pork chop",
      ticketMod2: "+ well done",
      ticketEmpty: "No stations selected — no tickets are printed.",
      stationsLabel: "Stations",
      stationsSelected: "Selected: {{count}}",
      selectAll: "Select all",
      removeAll: "Remove all",
      stationsNote:
        "A station can print on several printers. Stations don't depend on the accounts or their status.",
      languageLabel: "Kitchen ticket language",
      languageNote:
        "Names of products, modifiers and stations on the ticket. Doesn't affect till documents.",
      summaryDocs: "Documents:",
      summaryKitchen: "Kitchen tickets:",
      summaryDocsOff: "off",
      summaryDocsAll: "all free accounts ({{count}})",
      summaryDocsCount_one: "{{count}} account",
      summaryDocsCount_other: "{{count}} accounts",
      summaryNoStations: "no stations",
    },
  },

  devicePaymentMethods: {
    noFiscalOption: "БЕЗ ФИСКАЛИЗИРАНЕ",
    duplicateValuesError:
      "Two different methods cannot share the same value on a device.",
    invalidValueError: "Values cannot be empty.",
    duplicateMethodsError: "A method can only appear once.",
    noPaymentMethods: "No payment methods available",
    viewButton: "View",
    viewTitle: "Device payment methods",
    viewEmpty: "No payment methods configured for this device.",
    colMethod: "Method",
    colSlot: "Value",
  },

  paymentMethodsConfig: {
    title: "Payment methods",
    listKicker: "Payment methods list",
    addMethod: "Add payment method",
    searchPlaceholder: "Search by name",
    colName: "Name",
    colSystem: "Type",
    colSortIndex: "Sort",
    colActions: "Actions",
    badgeSystem: "System",
    badgeCustom: "Custom",
    systemTooltip:
      "System payment methods cannot be deleted, but can be reordered",
    noMethods: "No payment methods yet",
    loadingError: "Loading error",
    loadingErrorDesc: "Please try again later",

    createTitle: "New payment method",
    createSubtitle: "Add a custom payment method",
    editTitle: "Edit payment method",

    fieldNameBg: "Name in Bulgarian",
    fieldNameBgPlaceholder: "Payment method",
    fieldNameEn: "Name in English",
    fieldNameEnPlaceholder: "Payment method",
    fieldSortIndex: "Sort",

    save: "Save",
    create: "Create",

    createSuccess: "Payment method created successfully",
    editSuccess: "Payment method updated successfully",
    deleteSuccess: "Payment method deleted successfully",
    createError: "Failed to create",
    editError: "Failed to update",
    deleteError: "Failed to delete",

    deleteConfirmTitle: "Confirm delete",
    deleteConfirmContent: 'Are you sure you want to delete "{{name}}"?',

    enterNameBgWarning: "Please enter a Bulgarian name",
    enterNameEnWarning: "Please enter an English name",
  },

  restaurantSchema: {
    title: "Floor plan",
    subtitle: "Visual layout of tables and zones by region",
    loadError: "The floor plan could not be loaded",
    loadErrorHint: "Check the connection and reload the page.",
    offlineHint:
      "The floor plan lives in the cloud — editing is possible once the internet is back.",
    empty: {
      title: "Draw your venue",
      description:
        "Start with a region — a hall, terrace or bar. Arrange tables and objects in it, then pick tables straight from the plan when ordering.",
      cta: "Create the first region",
    },
    counts: {
      tables_one: "{{count}} table",
      tables_other: "{{count}} tables",
      seats_one: "{{count}} seat",
      seats_other: "{{count}} seats",
      objects_one: "{{count}} object",
      objects_other: "{{count}} objects",
    },
    kinds: {
      table: "Table",
      bar: "Bar",
      wall: "Wall",
      plant: "Plant",
      entrance: "Entrance",
      stage: "Stage",
      dj: "DJ",
    },
    toolbar: {
      undo: "Undo (⌘Z)",
      redo: "Redo (⇧⌘Z)",
      saving: "Saving…",
      saved: "Saved",
      rotateDrag: "Drag to rotate (Shift — 15° steps)",
      stretchWall: "Drag to stretch the wall",
      duplicate: "Duplicate (⌘D)",
      delete: "Delete (Del)",
      duplicateShort: "Duplicate",
      deleteShort: "Delete",
    },
    canvas: {
      emptyTitle: "This region is empty",
      emptyHint: "Add a table or drag an object from the bar below.",
      zoomOut: "Zoom out",
      zoomIn: "Zoom in",
      fit: "Fit the plan",
      tableTitle: "Table {{name}} · {{seats}}",
      wallLength: "{{length}} cm",
    },
    addBar: {
      dragHint: "Drag onto the plan",
      hint: "Drag an object onto the plan",
    },
    inspector: {
      rotation: "Rotation",
      size: "Size",
      tableSubtitle: "{{shape}} · {{seats}}",
      objectSubtitle: "Size: {{size}}",
      wallSubtitle: "Length: {{length}} cm",
      wallLength: "Length (cm)",
      wallLengthHint: "Or drag the dots at the ends of the wall to stretch it.",
      seatsHint: "The top grows with the seats; the size makes it bigger or smaller.",
    },
    table: {
      title: "Table {{name}}",
      number: "Table number",
      numberPlaceholder: "e.g. 12",
      nameTrailingStar: "The table name cannot end with “*” (reserved for offline mode)",
      nameTaken: "A table with this number already exists",
      seats: "Seats",
      shape: "Shape",
      size: "Table size",
    },
    tableSizes: {
      small: "Small",
      medium: "Medium",
      large: "Large",
      xl: "XL",
    },
    tableShapes: {
      round: "Round",
      square: "Square",
      rectangle: "Rectangular",
    },
    tableCreate: {
      title: "New table",
      subtitle: "Number, seats, shape and size.",
      submit: "Create",
      addAnother: "Keep the window open for the next table",
      addAnotherHint: "After “Create” the number switches to the next free one so you can add another right away.",
      createdSoFar_one: "Created in this run: {{names}}",
      createdSoFar_other: "Created in this run ({{count}}): {{names}}",
      preview: "Preview",
      numberHint: "The next free number. It is also used in Ordering.",
      numberHintTyped: "The number is also used in Ordering.",
      placementHint: "Position and rotation are set directly on the plan.",
    },
    summary: {
      tables: "Tables",
      seats: "Seats",
      hint: "Pick a table on the plan or in the list to change its properties.",
    },
    list: {
      tables: "Tables",
      objects: "Objects",
      empty: "No tables in this region yet.",
    },
    regionTabs: {
      newRegion: "New region",
    },
    region: {
      name: "Region name",
      namePlaceholder: "e.g. Hall, Terrace",
    },
    regionCreate: {
      title: "New region",
      subtitle: "A zone of the venue with its own plan.",
      submit: "Create",
    },
    regionSettings: {
      title: "Edit region",
      button: "Edit region",
      subtitle: "Region name and deletion.",
      deleteTitle: "Delete region",
      deleteBlocked:
        "The region contains {{tables}} and {{objects}} — remove them first to delete it.",
      deleteEmpty: "The region is empty. We will ask for confirmation.",
      delete: "Delete…",
    },
    regionDelete: {
      title: "Delete “{{name}}”",
      description: "The region will be deleted.",
      confirm: "Delete",
    },
    sizes: {
      small: "Small",
      medium: "Medium",
      large: "Large",
      xl: "XL",
    },
    messages: {
      regionCreated: "Region created",
      tableCreated: "Table {{name}} created",
      regionSaved: "Region saved",
      regionDeleted: "Region deleted",
      saveError: "The change could not be saved",
      deleteError: "Delete failed",
      noRoom: "No room — table {{name}} is too close. Move the tables apart first.",
    },
  },
  tablesConfig: {
    title: "Table setup",
    subtitle: "Table numbers and seats by room — no floor plan needed",
    offlineHint:
      "Tables are stored in the cloud — changes are possible once the internet is back.",
    empty: {
      title: "Create a room first",
      description:
        "Tables live in rooms — a hall, terrace or bar. Create the first one and add tables with just a number and seats.",
      cta: "Create room",
    },
    listEmpty: "No tables in this room yet. Add the first one from “New tables”.",
    searchPlaceholder: "Search by number…",
    clearSearch: "Clear search",
    seatsLess: "One seat less",
    seatsMore: "One seat more",
    grid: {
      kicker: "Tables · {{region}}",
      selectAll: "Select all",
      filterAll: "All",
      selectTable: "Select table {{name}}",
      newTable: "New table",
      noMatches: "No tables match the search.",
    },
    newTables: {
      kicker: "New tables",
      title: "Add to “{{region}}”",
      single: "One table",
      series: "Number series",
      number: "Number",
      numbers: "Numbers from – to",
      from: "From",
      to: "To",
      seats: "Seats per table",
      add: "Add table",
      addMany_one: "Add {{count}} table",
      addMany_other: "Add {{count}} tables",
      seriesInvalid: "Enter numbers from – to, with “to” not smaller than “from”",
      seriesTooMany: "At most {{max}} tables at once",
      seriesNothingFree: "All numbers in this range are taken",
      seriesSkippedHint_one: "{{count}} taken number will be skipped",
      seriesSkippedHint_other: "{{count}} taken numbers will be skipped",
      placementHint:
        "The table is placed automatically in the room. Its position can be changed in “<link>Floor plan</link>”.",
    },
    edit: {
      title: "Table {{name}}",
      seats: "Seats",
      region: "Room",
      save: "Save",
      delete: "Delete table",
      close: "Close",
    },
    bulk: {
      title_one: "{{count}} table selected",
      title_other: "{{count}} tables selected",
      seats: "Seats for all",
      apply: "Apply",
      moveTo: "Move to room",
      chooseRegion: "Choose a room…",
      move: "Move",
      delete: "Delete selected",
      cancel: "Clear selection",
      edit: "Edit",
    },
    delete: {
      title: "Delete table {{name}}",
      description:
        "The table disappears from the floor plan and from reservations. A table with a reservation or an open bill cannot be deleted.",
      titleMany: "Delete {{count}} tables",
      descriptionMany:
        "Tables {{names}} disappear from the floor plan and from reservations. Tables with a reservation or an open bill are skipped.",
      confirm: "Delete",
    },
    messages: {
      created: "Table {{name}} added",
      seriesCreated_one: "{{count}} table added",
      seriesCreated_other: "{{count}} tables added",
      seriesSkipped: "Skipped taken numbers: {{names}}",
      seatsApplied_one: "Seats changed for {{count}} table",
      seatsApplied_other: "Seats changed for {{count}} tables",
      moved: "Table {{name}} moved to “{{region}}”",
      movedMany_one: "{{count}} table moved to “{{region}}”",
      movedMany_other: "{{count}} tables moved to “{{region}}”",
      deleted: "Table {{name}} deleted",
      deletedMany_one: "{{count}} table deleted",
      deletedMany_other: "{{count}} tables deleted",
      deleteSkippedInUse: "Not deleted (reservation or open bill): {{names}}",
      notFitted: "The table did not fit in the room — it can be arranged in “Floor plan”",
      saveError: "The change could not be saved",
      deleteError: "Deletion failed",
    },
  },
  companiesConfig: {
    title: "Companies",
    listKicker: "Companies list",
    addCompany: "Add company",
    searchPlaceholder: "Search by name, object or EIK",
    colName: "Name",
    colEik: "EIK",
    colVat: "VAT",
    colActions: "Actions",
    badgeDefault: "Current",
    noCompanies: "No companies yet",
    loadingError: "Loading error",
    loadingErrorDesc: "Please try again later",

    createTitle: "New company",
    createSubtitle: "Enter invoicing details",
    editTitle: "Edit company",
    editSubtitle: "Edit invoicing details",

    secBasic: "Basic details",
    secIdentity: "Identification and contacts",
    secNumbering: "Document numbering",
    secInvoicePrinting: "Invoice printing",
    fieldInvoicePrinter: "POS printer for invoices",
    fieldInvoicePrinterHint:
      "The company's invoices print on this printer, whichever till issues them. “The operator's POS printer” — as before: the printer of the till that prints.",
    invoicePrinterOperator: "The operator's POS printer",
    secBanks: "Bank accounts",

    fieldName: "Company name",
    fieldNamePlaceholder: 'e.g. "Test Ltd"',
    fieldObjectName: "Object / venue name",
    fieldObjectNamePlaceholder: 'e.g. "Test venue"',
    fieldAddress: "Address",
    fieldAddressPlaceholder: "Registered address",
    fieldMol: "Accountable person",
    fieldMolPlaceholder: "MOL",
    fieldEik: "EIK / BULSTAT",
    fieldEikHint: "9 or 13 digits",
    fieldEikValid: "Valid EIK — the checksum matches",
    fieldVatFromEik: "Fill in BG{{eik}}",
    fieldVat: "VAT number",
    fieldVatHint: "Format BG + 9 or 10 digits (optional)",
    fieldPhone: "Phone",
    fieldEmail: "E-mail",

    fieldDefault: "This is the current company",
    fieldDefaultDesc:
      "Documents will be issued with this company's data. Only one can be current.",

    fieldLastInvoice: "Last invoice number",
    fieldLastInvoicePlaceholder: "E.g. 0000000034",
    fieldLastInvoiceHint:
      "The last number ALREADY issued. 0 = no invoice issued yet.",
    fieldLastProforma: "Last proforma",
    fieldLastProformaHint: "Format: P-2026-0001",
    numberingNextKicker: "Next document",
    numberingNextInvoice: "Invoice No",
    numberingNextUnknown:
      "Enter the last issued number to see which number the next invoice starts from.",

    addBank: "Add bank",
    noBanks: "No bank accounts yet",
    bankIndex: "Bank #{{index}}",
    bankName: "Bank name",
    bankAddDraft: "New bank account",
    bankEditDraft: "Edit bank account",
    bankConfirm: "Save bank",
    bankMakeDefault: "Make default",
    draftDirtyBlock:
      "Save or cancel the current bank account before continuing.",

    save: "Save",
    create: "Create",

    createSuccess: "Company created successfully",
    editSuccess: "Company updated successfully",
    deleteSuccess: "Company deleted successfully",
    createError: "Failed to create",
    editError: "Failed to update",
    deleteError: "Failed to delete",

    deleteConfirmTitle: "Confirm delete",
    deleteConfirmContent: 'Are you sure you want to delete company "{{name}}"?',

    errRequired: "This field is required",
    errInvalidEik: "Invalid EIK / BULSTAT",
    errInvalidVat: "Expected format: BG + 9 or 10 digits",
    errInvalidEmail: "Invalid e-mail",
    errInvalidBic: "Invalid BIC",
    errInvalidIban: "Invalid IBAN",
    errInvalidInvoiceNumber: "Invalid number",
    errInvalidProforma: "Expected format: P-2026-0001",
  },

  monitors: {
    title: "Monitors",
    kitchenBadge: "Kitchen",
    shpickerBadge: "Expediter",
    waiterBadge: "Waiter",
    selectStation: "Select station",
    connected: "Connected",
    disconnected: "Disconnected",
    connecting: "Connecting...",
    noOrders: "No orders",
    unfulfilled: "Unfulfilled",
    fulfilled: "Fulfilled",
    noAssignedStations: "No assigned stations",
    contactAdmin: "Contact an administrator to be assigned stations",
    inProgress: "In progress",
    complete: "Complete",
    return: "Return",
    statusNotStarted: "Not started",
    statusInProgress: "In progress",
    statusCooked: "Cooked",
    statusIssued: "Issued",
    statusServed: "Served",
    noActiveOrders: "No active orders",
    newOrdersWillAppear: "New orders will appear automatically",
    noConnection: "No connection",
    noConnectionHint: "This screen is offline — new orders are NOT arriving. Call a manager.",
    refresh: "Refresh",
    soundOn: "Sound on",
    soundOff: "Sound off",
    selected: "Selected: {{count}}",
    minuteShort: "m",
    // Hint of "+6m" — how late the row is against its preparation time
    overdueBy: "Late by {{time}}",
    // Click on a row/checkbox that stays on screen but can't be acted on (lockedRowHint.ts)
    serveAfterStatus: "Can be served after status “{{status}}”",
    issuedLockedHint: "Issued by the expediter — only the expediter can return it",
    navUp: "Up one item",
    navDown: "Down one item",
    navUp2: "Up two items",
    navDown2: "Down two items",
    colStatus: "Status",
    colTable: "Table",
    colProduct: "Item",
    colElapsed: "Time",
    colQty: "Qty",
    colTotal: "Total",
    colOperator: "Operator",
    colOrderTime: "Ordered",
    additionToProduct: "(for {{product}})",
    additionToProductFromStation: "(for {{product}} from {{station}})",
    stationPartDone: "done",
    stationPartInProgress: "in progress",
    stationPartWaiting: "waiting",
    stationPartReady: "ready",
    stationPartNotReady: "not ready",
    mainProductReady: "The dish is ready",
    mainProductNotReady: "The dish is not ready yet",
    serveFailed: "The row was not served — please try again",
    checkboxComplete: "Complete the row",
    checkboxReturn: "Return the row",
    checkboxCompleteOrder: "Complete the order",
    checkboxReturnOrder: "Return the order",
    // Station filter at the top of the monitor (the chips next to the title)
    stationFilterTitle: "Stations",
    stationFilterAll: "All",
    // "Table" view
    // The invisible size grips in the three views (hover hint)
    resize: {
      gridColumnWidth: "Drag for wider or narrower columns. Double-click to reset.",
      gridCardHeight: "Drag for bigger or smaller rows. Double-click to reset.",
      gridCard: "Drag to resize the cards. Double-click to reset.",
      gridRowHeight: "Drag for taller or shorter rows. Double-click to reset.",
      boardColumns: "Drag to set the columns on screen. Double-click to reset.",
      boardRows: "Drag to set the rows on screen. Double-click to reset.",
      boardCells: "Drag to resize the cells. Double-click to reset.",
      boardSlots: "Drag to set the rows per cell. Double-click to reset.",
      listRowHeight: "Drag to set the row height. Double-click for medium.",
    },
    boardNothingSelected: "No order selected",
    boardSelectedOrder: "Selected: #{{table}} · {{waiter}}",
    // Board footer keys — only the allowed actions (BoardSelectionLabel),
    // joined with " · ".
    boardKeysSelect: "↑ ↓ ← → select",
    boardKeysComplete: "+ complete",
    boardKeysReturn: "− return",
    issueWithoutAdditionsTitle: "Complete without the additions?",
    issueWithoutAdditionsLine:
      "You will complete “{{dish}}” from table #{{table}} without the additions: {{additions}}.",
    issueWithoutAdditionsCancel: "Cancel",
  },

  inventoryPage: {
    moduleLabel: "Inventory module",
    backToModules: "Back to modules",
    collapse: "Collapse",
    expandMenu: "Expand menu",
    collapseMenu: "Collapse menu",
    delivery: "Delivery",
    refresh: "Refresh",
    // Deliveries table
    newDelivery: "New delivery",
    colDate: "Date",
    colInvoice: "Invoice №",
    colSupplier: "Supplier",
    colWarehouse: "Warehouse",
    colProducts: "Products",
    colValue: "Value",
    colType: "Type",
    colQty: "Quantity",
    colUnitPrice: "Unit price",
    colBatch: "Batch",
    colExpiry: "Expiry",
    stxRestockLabel: "Restock",
    colProductName: "Product",
    colPackaging: "Packaging",
    colNet: "Total excl. VAT",
    lotSource: "Source",
    lotSource_delivery: "Delivery",
    lotSource_stocktake: "Stocktake",
    lotSource_transfer: "Transfer",
    lotSource_substitution: "Substitution",
    lotSource_writeoff: "Write-off",
    lotSource_scrap: "Scrap",
    lotSource_report: "Report posting",
    colGross: "Total incl. VAT",
    colUnit: "Unit of measure",
    filterApply: "Apply",
    filterReset: "Reset",
    filterSearch: "Search in filters",
    filterSelectAll: "Select all",
    sectionConfig: "Settings",
    settings: "Settings",
    settingsVatSection: "VAT registration",
    settingsWhSection: "Default warehouse",
    woNeedsWarehouseHint: "Choose a default warehouse in Settings — without one there is nothing to write off from.",
    settingsWhHint:
      "The warehouse the module uses by default (for reports and new operations).",
    settingsWhQuestion: "Default warehouse:",
    settingsVatQuestion: "Is the company VAT-registered?",
    settingsVatHint:
      "Determines how the amounts with and without VAT are calculated in the warehouse module and how they are shown: with “Yes” every amount in the warehouse is excluding VAT, with “No” — including VAT, and the delivery price is entered including VAT — the price you pay the supplier.",
    settingsVatYes: "Yes, registered",
    settingsVatNo: "No",
    settingsSaved: "Settings saved",
    fieldScrapReasonPh: "Select a reason",
    kpiDeliveries: "Deliveries",
    kpiValue: "Total value",
    kpiProducts: "Products",
    products: "Products",
    noWarehouse: "Select a warehouse to view deliveries.",
    noDeliveries: "No deliveries recorded for this warehouse yet.",
    noLines: "No products in this delivery.",
    // New delivery modal
    fieldDate: "Delivery date",
    fieldInvoice: "Invoice №",
    fieldInvoicePh: "Enter number",
    fieldSupplier: "Supplier",
    fieldSupplierPh: "Select supplier",
    productAdd: "New product",
    productAddTitle: "New stock product",
    productAddHint: "The product is saved in the organization's shared list.",
    productName: "Name",
    productNamePh: "Product name",
    productNameRequired: "Name is required",
    productExportCode: "Export code",
    productGroup: "Group",
    productGroupPh: "Select group",
    productUnit: "Unit of measure",
    productUnitPh: "Select unit",
    productUnitRequired: "Unit of measure is required",
    productVat: "VAT",
    productVatPh: "Select VAT",
    productVatRequired: "VAT is required",
    productMinQuantity: "Minimum quantity",
    productWastePct: "Waste",
    productPackagings: "Packagings",
    productPackagingsPh: "Select packagings",
    productPackagingsHint:
      "The packagings the product is delivered in (e.g. crate, pack).",
    productTrackBatch: "Track batch",
    productTrackExpiry: "Track expiry date",
    productSaved: "Product saved",
    supplierAdd: "New supplier",
    supplierAddTitle: "New supplier",
    supplierAddHint: "The supplier is saved in the organization's shared list.",
    supplierName: "Name",
    supplierNamePh: "Supplier name",
    supplierNameRequired: "Name is required",
    supplierEik: "Company ID",
    supplierEikPh: "e.g. 123456786",
    supplierEikInvalid: "Invalid company ID (9 or 13 digits with check digit)",
    supplierVatNumber: "VAT number",
    supplierVatNumberPh: "e.g. BG123456786",
    supplierVatNumberInvalid: "Invalid VAT number (BG + 9 or 10 digits)",
    supplierVatNumberHint:
      "Without a VAT number the delivery charges no VAT — the total incl. VAT stays equal to the total excl. VAT.",
    supplierSaved: "Supplier saved",
    fieldTemplate: "From past delivery",
    fieldTemplatePh: "Select a delivery as template",
    templateOptionProducts: "{{count}} items",
    // Delivery worksheet page
    backToDeliveries: "Back to deliveries",
    wsSaveChanges: "Save changes",
    wsSearchPh: "Add a product — just start typing, e.g. “tomatoes”",
    wsClearRowHint: "Remove row from delivery",
    reportLoadFailed: "The report did not load",
    reportLoadFailedText: "The connection to the server dropped or it returned an error. The figures are not shown so they do not look like zeros.",
    reportRetry: "Try again",
    reportStale: "Refreshing failed — showing the data from {{time}}.",
    noMinSet: "No minimum set",
    noPrice: "No price",
    shortagePositions_one: "{{count}} position",
    shortagePositions_other: "{{count}} positions",
    shortageWarehouses_one: "{{count}} warehouse",
    shortageWarehouses_other: "{{count}} warehouses",
    shortageUnpriced_one: "without price: {{count}}",
    shortageUnpriced_other: "without price: {{count}}",
    soldKpiCostCurrent: "Cost at current prices",
    soldRevenueAfterDiscounts: "After discounts",
    scLotsHintAsOf: "Batches as of the end of {{date}} — in write-off order; expiry is judged against that date.",
    payUnpaidToday: "unpaid since today",
    payUnpaidFor_one: "unpaid for {{count}} day",
    payUnpaidFor_other: "unpaid for {{count}} days",
    unknownProductName: "Product #{{id}}",
    wsColUnitPriceNet: "Unit price excl. VAT",
    wsUnitPriceNetPer: "Unit price excl. VAT / {{unit}}",
    // company not VAT-registered: enters what it pays (ENTERED_PRICE_LABELS in vatBasis.ts)
    wsColUnitPriceGross: "Unit price incl. VAT",
    wsUnitPriceGrossPer: "Unit price incl. VAT / {{unit}}",
    wsUnitPriceGrossHint: "The company is not VAT-registered — enter the price you pay (incl. VAT)",
    // the header edge in the warehouse grids (`ColumnHeaderCells`)
    colResizeHint: "Drag to resize the column · double-click — default width",
    // the hover hints on the delivery column headers
    wsUnitPriceGrossDerivedHint: "Calculated from the unit price excl. VAT and the product's VAT rate — not entered",
    wsSumNetHint: "Quantity × unit price excl. VAT — or type the amount from the document",
    wsSumGrossHint: "Quantity × unit price incl. VAT — or type the amount from the document",
    wsStockShort: "Stock",
    wsPriceShort: "Price",
    wsWarehouseForNewRows: "Warehouse for new rows",
    wsWhAskTitle: "Warehouse “{{to}}” — for the entered rows too?",
    wsWhAskText_one: "{{count}} row is already in “{{from}}”.",
    wsWhAskText_other: "{{count}} rows are already in “{{from}}”.",
    wsWhOnlyNew: "Only for new rows",
    wsWhOnlyNewHint: "The entered rows stay in “{{from}}”; new rows go to “{{to}}”.",
    wsWhAlsoExisting_one: "For the existing row too",
    wsWhAlsoExisting_other: "For the existing {{count}} rows too",
    wsWhAlsoExistingHint: "Rows in “{{from}}” move to “{{to}}”. Rows in another warehouse stay where they are.",
    wsAddRow: "Another row for this product",
    wsAddRowHint: "Another row for this product — different warehouse, batch, expiry or price",
    // the "Columns" panel of the inventory tables
    wsColumnWaiting: "This column shows by itself — a row in the sheet uses it or is waiting for a value in it",
    colInternalNumber: "No. (internal number)",
    // 0.0.319: new delivery without a warehouse; product already added; stocktake in progress
    wsPickWarehouseFirst: "Select a warehouse first",
    wsRepeatTitle: "“{{name}}” is already in the delivery",
    wsRepeatTitleMany_one: "{{count}} product is already in the delivery",
    wsRepeatTitleMany_other: "{{count}} products are already in the delivery",
    wsRepeatText_one: "You can add it on a new row — for example with a different quantity or price.",
    wsRepeatText_other: "You can add them on new rows — for example with a different quantity or price.",
    wsRepeatCaption: "Already in the delivery",
    wsRepeatNewRow: "New row for “{{name}}”",
    wsRepeatNewRows: "A new row for each",
    wsRepeatNewRowHint: "Its own quantity, price, warehouse and batch — the same as the other row is fine too.",
    wsRepeatExisting_one: "To the existing row",
    wsRepeatExisting_other: "To the existing rows",
    wsRepeatExistingHint: "The cursor goes to the row that is already in the delivery.",
    wsRepeatExistingCatalogHint_one: "The entered quantity replaces the one in the row.",
    wsRepeatExistingCatalogHint_other: "The entered quantities replace the ones in the rows.",
    wsProductRowOf: "row {{index}} of {{count}}",
    wsProductRowsHint_one: "“{{name}}” is on {{count}} row in the delivery — each one is a separate lot",
    wsProductRowsHint_other: "“{{name}}” is on {{count}} rows in the delivery — each one is a separate lot",
    wsStocktakeToday_one: "A stocktake was started today",
    wsStocktakeToday_other: "{{count}} stocktakes were started today",
    wsStocktakeStarted: "started {{when}}",
    docPickDayFirst: "Select the date first",
    docPastDay_one: "The date is earlier than today — yesterday",
    docPastDay_other: "The date is earlier than today — {{count}} days ago",
    wsClearAll: "Clear",
    wsClearAllTitle: "Clear the delivery?",
    wsClearAllText_one: "{{count}} row leaves the sheet.",
    wsClearAllText_other: "All {{count}} rows leave the sheet.",
    wsClearAllUndo: "Right after that you can bring them back with “Undo”.",
    wsClearAllOk: "Clear the delivery",
    wsClearAllDone_one: "Sheet cleared ({{count}} row)",
    wsClearAllDone_other: "Sheet cleared ({{count}} rows)",
    undo: "Undo",
    tplReplaced: "The sheet was replaced with the delivery from {{date}}",
    tplAskTitle: "The delivery from {{date}} — into the sheet",
    tplAskText: "The sheet already has entered rows.",
    tplAdd: "Add the missing products",
    tplAddHint: "Adds the products from this delivery that are not in the sheet yet. What you entered stays.",
    tplReplace: "Replace the sheet",
    tplReplaceHint: "The sheet becomes this delivery. The previous rows come back with “Undo”.",
    fieldTime: "Time",
    saveMomentLabel: "Date and time",
    conflictSomeone: "another user",
    conflictTitle: "The document was changed by {{who}}",
    conflictText: "While you were editing it, {{who}} saved changes. Your changes are still here — nothing is lost.",
    conflictTextWhen: "{{who}} saved it at {{when}} while you were editing it. Your changes are still here — nothing is lost.",
    conflictView: "View the current document",
    conflictViewHint: "The saved document and its “Change history”. Your sheet stays as it is.",
    conflictOverwrite: "Save my version",
    conflictOverwriteHint: "Saves your version. The changes by {{who}} stay in the “Change history”.",
    conflictDiscard: "Discard my changes",
    conflictDiscardHint: "The sheet reloads from the saved document. Your changes are lost.",
    wsNoMatch: "No match",
    wsLastPrice: "last price",
    wsSoldWithoutStock: "−{{qty}} sold without stock",
    wsSoldWithoutStockHint: "More was sold than was in stock. A delivery dated after the sales comes in whole (the minus is written off); a delivery dated on the day of the sales covers it.",
    wsPricesFromSupplier: "Prices & last delivery from: {{name}}",
    wsResultsCount_one: "{{count}} result",
    wsResultsCount_other: "{{count}} results",
    wsAddedTag: "Added",
    wsNoLastDelivery: "no previous delivery",
    wsNoSupplierDelivery: "no delivery from this supplier",
    wsEmptyTitle: "No products added",
    wsEmptyHint:
      "Find a product with the search above and press Enter — the row is added here and focus jumps straight to “Quantity”.",
    wsEmptyKbdType: "type anywhere",
    wsEmptyKbdEnter: "adds the product",
    wsEmptyKbdEsc: "back to search",
    wsUnitTotals: "Total quantities",
    // Write-off worksheet pages (create/edit)
    backToWriteoffs: "Back to write-offs",
    woSearchPh: "Add a product or recipe — just start typing, e.g. “tomatoes”",
    woEmptyTitle: "No rows added",
    unitPortions: "portions",
    woEmptyHint:
      "Find a product or recipe with the search above and press Enter — the row is added here and focus jumps straight to “Quantity”.",
    draftRestoreTitle: "Unfinished delivery",
    draftRestoreTitleBy: "Unfinished delivery by {{name}}",
    draftRestoreSavedAt: "Saved at",
    draftRestoreTotal: "Total",
    draftRestoreQuestion: "Do you want to continue where you left off?",
    draftRestoreContinue: "Continue",
    draftRestoreStartOver: "Start over",
    fieldWarehouse: "Warehouse",
    fieldWarehousePh: "Select warehouse",
    fieldProduct: "Product",
    fieldProductPh: "Select product",
    productOptStock: "Stock",
    productOptLastDelivery: "Last delivery",
    fieldUnitPrice: "Unit price",
    fieldNote: "Note",
    fieldNotePh: "Optional note for the delivery",
    addRow: "Add row",
    total: "Total",
    // Base + VAT in the "Total" box (only for VAT-registered companies)
    totalBase: "Base",
    totalVat: "VAT",
    colComment: "Comment",
    colUser: "User",
    cancel: "Cancel",
    submit: "Record",
    created: "Delivery recorded",
    errWarehouse: "Select a warehouse",
    errNoWarehouses:
      "No warehouse exists. Create one in the organization before recording movements.",
    errDate: "Select a date",
    errNoLines: "Add at least one valid row",
    errCreate: "Failed to record delivery",
    // Write-offs
    writeoff: "Write-off",
    newWriteoff: "New write-off",
    noWriteoffs: "No write-offs for this warehouse yet.",
    kpiWriteoffs: "Write-offs",
    colReason: "Reason",
    colNote: "Note",
    colAvailable: "Available",
    fieldReason: "Reason",
    fieldReasonPh: "Free text",
    fieldComment: "Comment",
    fieldCommentPh: "Optional comment",
    recipe: "Recipe",
    selectRecipe: "Select recipe",
    splitToProducts: "Split into products",
    revertToRecipe: "Back to recipe",
    confirmContinue: "Continue",
    editDeliveryTitle: "Edit delivery",
    editWriteoffTitle: "Edit write-off",
    editSubmitWriteoff: "Save write-off",
    edit: "Edit",
    delete: "Void",
    confirmDeleteTitle: "Void this record?",
    confirmDeleteBody:
      "The record will be voided and stock recomputed. This cannot be undone.",
    deleted: "Record voided",
    errDelete: "Void error",
    errLoad: "Load error",
    confirmLeaveTitle: "Unsaved changes",
    confirmLeaveBody: "You have unsaved changes. Leave without saving?",
    confirmLeave: "Leave",
    confirmDeleteRowTitle: "Remove this row?",
    confirmDeleteRowBody: "This row will be removed.",
    confirmSplitTitle: "Split the recipe?",
    confirmSplitBody: "The row will be replaced with the recipe's products.",
    confirmRevertTitle: "Back to recipe?",
    confirmRevertBody:
      "The split products will be collapsed back into one recipe row.",
    notePh: "Note",
    portionsPh: "Portions",
    recipeGroupLabel: "Recipe",
    recipeEmpty: "Recipe has no ingredients",
    fifo: "FIFO (first in)",
    fieldWriteoffDate: "Write-off date",
    batchShort: "Batch",
    noBatch: "No batch",
    remainShort: "Left",
    expiryShort: "Exp.",
    noExpiry: "no expiry",
    writeoffSubmit: "Write off",
    writeoffCreated: "Write-off recorded",
    errWriteoff: "Failed to record write-off",
    errStock: "Cannot write off more than available: {{products}}",
    // Scrapping — 1:1 with write-off, different type + scrap reason
    scrap: "Scrapping",
    scrapReasonExpired: "Expired",
    scrapReasonSpoiled: "Spoiled",
    scrapReasonBroken: "Broken/spilled",
    scrapReasonMismatch: "Non-conformity",
    scrapReasonVoidMarking: "Scrap from Ordering",
    docScrapTitle: "Scrapping document",
    docScrapSubtitle: "Scrapping #{{id}}",
    docScrapHeading: "Stock scrapping",
    // Inter-warehouse transfer
    // Transfers — list and worksheet (0.0.155)
    backToTransfers: "Back to transfers",
    trxNotePh: "Optional note",
    trxDirection: "Direction",
    trxRemains: "Remains in {{warehouse}}",
    trxBecomes: "Becomes in {{warehouse}}",
    trxLastPrice: "last price {{price}}",
    trxNoPrice: "no delivery price",
    transfer: "Transfer",
    newTransfer: "New transfer",
    editTransferTitle: "Edit transfer",
    transferSubmit: "Transfer",
    editSubmitTransfer: "Save transfer",
    transferCreated: "Transfer recorded",
    errTransfer: "Failed to record transfer",
    noTransfers: "No transfers for this warehouse yet.",
    kpiTransfers: "Transfers",
    colFromWarehouse: "From warehouse",
    colToWarehouse: "To warehouse",
    fieldFromWarehouse: "From warehouse",
    fieldFromWarehousePh: "Source warehouse",
    fieldToWarehouse: "To warehouse",
    fieldToWarehousePh: "Destination warehouse",
    errFromWarehouse: "Select a source warehouse",
    errToWarehouse: "Select a destination warehouse",
    errSameWarehouse: "Source and destination must be different warehouses",
    docTransferTitle: "Transfer document",
    docTransferSubtitle: "Transfer #{{id}}",
    docTransferHeading: "Inter-warehouse transfer",
    errRecipe: "Failed to load recipe",
    // Split across warehouses + document
    // Hints for what's missing before "Receive" is enabled
    issueQty: "Enter a quantity for: {{products}}",
    issuePrice: "Enter a unit price for: {{products}}",
    issueWarehouse: "Select the row's warehouse for: {{products}}",
    issueBatch: "Enter a batch for “{{product}}” (required)",
    issueExpiry: "Enter an expiry date for “{{product}}” (required)",
    issueBatchFor: "Enter a batch (required) for: {{products}}",
    issueExpiryFor: "Enter an expiry date (required) for: {{products}}",
    issueEmpty: "Add at least one product with quantity, price and warehouse",
    // Stocktake
    stocktake: "Stocktake",
    sectionReports: "Reports",
    stocktakeSubmit: "Confirm stocktake",
    newStocktake: "New stocktake",
    editStocktakeTitle: "Edit stocktake",
    editSubmitStocktake: "Save changes",
    stocktakeCreated: "Stocktake recorded",
    errStocktake: "Stocktake error",
    // 409 `substitution_exceeds_stocktake` — stale substitution (stocktakeSubstitutionConflict.ts)
    substitutionConflictDonor:
      "The substitutions are out of date: “{{product}}” would give {{requested}}, but its counted surplus is {{available}}. The coefficient or the stock has changed — the substitutions were reloaded. Review them and save again; your counts are kept.",
    substitutionConflictRecipient:
      "The substitutions are out of date: {{requested}} of “{{product}}” would be covered, but the shortage is {{available}}. The coefficient or the stock has changed — the substitutions were reloaded. Review them and save again; your counts are kept.",
    errNoCounts: "Enter at least one counted product",
    colSystem: "Expected",
    colCounted: "Found",
    colDiff: "Difference",
    colStatus: "Status",
    statusEqual: "equal",
    statusSurplus: "surplus",
    statusShortage: "shortage",
    statusSubstitute: "substitution",
    kpiSurplus: "Surplus",
    kpiShortage: "Shortage",
    hideZeroStock: "Hide zero stock",
    stockAsOfField: "As of date",
    stockAsOfNow: "As of today",
    stockAsOfNote: "Quantities as of the end of {{date}} · values at current prices",
    stockGrossCurrentPrices: "Value incl. VAT at current prices",
    stockNetCurrentPrices: "Value excl. VAT at current prices",
    showZeroStock: "Show all",
    stocktakeReasonPh: "Note / reason (optional)",
    noProducts: "No products in this warehouse",
    noStocktakes: "No stocktakes for the period",
    colSurplus: "Surplus",
    colShortage: "Shortage",
    colNetValue: "Value excl. VAT",
    colGrossValue: "Value incl. VAT",
    // "Stock on hand" report
    stockReport: "Stock on hand",
    searchStockPh: "Search by product, category, shrinkage…",
    colCategory: "Category",
    colGroup: "Group",
    stockLotsSubtitle: "Movement history",
    stockLotsEmpty: "No movements for this product.",
    colMinQty: "Min. stock",
    colWaste: "Shrinkage",
    colAvgPrice: "Avg price incl. VAT",
    colAvgPriceNet: "Avg price excl. VAT",
    colLastPrice: "Last delivery price",
    colLastPriceShort: "Last delivery price",
    underMin: "Below minimum",
    nearMin: "Up to 20% above minimum",
    overMin: "More than 20% above minimum",
    kpiStocktakes: "Stocktakes",
    // Stocktake — list and worksheet (0.0.152)
    backToStocktakes: "Back to stocktakes",
    groupByWarehouse: "Group by warehouse",
    groupStocktakesCount_one: "{{count}} stocktake",
    groupStocktakesCount_other: "{{count}} stocktakes",
    fieldStocktakeDate: "Date",
    colUnitShort: "Unit",
    fieldStocktakeReason: "Note / reason (optional)",
    stxSearchPh: "Search a product to count…",
    stxCountedTag: "counted",
    stxDraftTitleBy: "Unfinished stocktake by {{name}}",
    // Substitutions (the panel next to the table)
    subPanelTitle: "Substitutions",
    subPanelPairs_one: "{{count}} pair",
    subPanelPairs_other: "{{count}} pairs",
    subCoefficient: "Coef.",
    subApply: "Substitute",
    subRevert: "Undo",
    subChipHint: "Part of a substitution",
    subNoSurplusLeft:
      "This product's surplus is already used by another substitution",
    // Interchangeable products
    interchange: "Interchangeable products",
    newInterchange: "New rule",
    newInterchangeTitle: "Create interchangeable products",
    editInterchangeTitle: "Edit rule",
    backToInterchangeables: "Back to interchangeables",
    interchangeSubmit: "Save rule",
    interchangeGroupProducts: "Products in the group",
    interchangeProductsSubtitle: "All listed products can replace one another",
    interchangeAddProductPh: "Add a product to the group…",
    interchangePairExample:
      "The numbers are relative to the first product; any other pair follows from them — e.g.",
    interchangeHowApplies: "How it applies",
    interchangeCoef: "Coefficient",
    interchangeCoefHelp:
      "Coefficient = how many group base units equal 1 unit of the product (1:1 for equals, e.g. 1 paste = 0.7 juice). Priority = which is used first when covering; empty → the product with the oldest delivery.",
    interchangeSortIndex: "Priority",
    interchangeSortPh: "e.g. 1",
    interchangeNeedTwo: "A group must contain at least 2 products",
    interchangeHintCol: "Comment",
    interchangeHintPh: "Optional comment for the group",
    interchangeHint:
      "When one of the products in the group runs out, the system offers to write off the others by the shown ratios — during report posting and stocktake.",
    interchangeSearchPh: "Product or comment…",
    noInterchange: "No interchangeable product groups yet.",
    interchangeBadCoef: "Coefficient must be a positive number",
    interchangeSaved: "Group saved",
    back: "Back",
    substitutionsReport: "Substituted products",
    noSubstitutions: "No substitutions for the period.",
    subSearchPh: "Search by product or type…",
    // "Voided activities" report (soft delete trail)
    deletedReport: "Voided activities",
    deletedColWhen: "Voided at",
    deletedColDocDate: "Document date",
    deletedColBy: "Voided by",
    deletedKpiCount: "Voided records",
    deletedSearchPh: "Search by type, product or user…",
    noDeleted: "No voided activities for the period.",
    deletedSubtitle: "Voided by {{who}} at {{when}}",
    deletedType_delivery: "Delivery",
    deletedType_writeoff: "Write-off",
    deletedType_scrap: "Scrap",
    deletedType_stocktake: "Stocktake",
    deletedType_transfer: "Transfer",
    deletedType_substitution: "Substitution",
    deletedType_report: "Report posting",
    deletedNetValue: "Value excl. VAT",
    deletedRestore: "Restore",
    deletedRestoreTitle: "Restore this record?",
    deletedRestoreBody:
      "The record will return to the regular lists and its quantities will be re-applied to the stock.",
    deletedRestored: "Record restored",
    errRestore: "Restore failed",
    colSubCount: "Substitutions",
    subSourceStocktake: "At stocktake",
    subColFrom: "From (donor)",
    subColTo: "To (recipient)",
    subWrittenOff: "Written off",
    subCovered: "Covered",
    // "Sales surplus" page (formerly "Shortage" — shortages from posted reports)
    negativeReport: "Sales surplus",
    negSearchPh: "Product, warehouse or category…",
    noNegative: "No products with negative stock.",
    kpiNegCount: "Products in shortage",
    kpiNegNet: "Value excl. VAT",
    kpiNegGross: "Value incl. VAT",
    shortageTotal: "Total uncovered surplus",
    shortageProductsCount_one: "{{count}} product",
    shortageProductsCount_other: "{{count}} products",
    shortageRowDelivery: "Delivery",
    colDeficitQty: "Quantity",
    shortageBreakdownSubtitle: "Accumulated from reports",
    shortageBreakdownHint:
      "A delivery only covers reports AFTER its date. To clear an older shortage, post a back-dated delivery — on/before the report that caused it.",
    compensateBackdated: "Compensate (back-dated)",
    errSave: "Error while saving",
    docStocktakeTitle: "Stocktake document",
    docStocktakeSubtitle: "Stocktake #{{id}}",
    docStocktakeHeading: "Stocktake",
    docTitle: "Delivery document",
    docSubtitle: "Delivery #{{id}}",
    docHeading: "Goods received",
    docWriteoffTitle: "Write-off document",
    docWriteoffSubtitle: "Write-off #{{id}}",
    docWriteoffHeading: "Goods written off",
    // ─── Posting reports ───────────────────────────────────────────────────
    posting: "Posting reports",
    soldProducts: "Sold products",
    soldRecipes: "Sold recipes",
    newPosting: "New posting",
    editPostingTitle: "Edit posting",
    backToPostings: "Back to postings",
    kpiPostings: "Postings",
    kpiPostedProducts: "Written-off products",
    kpiCompensations: "Compensations",
    colReportNo: "Report #",
    colDateTime: "Date & time",
    periodTotal: "Period total",
    postingColSold: "Sold",
    postingSoldRecipes: "recipes",
    postingSoldProducts: "products",
    postingColCompensations: "Compensations",
    postingSubsBadge: "{{count}} sub.",
    postingPickReport: "Select a report",
    postingNoPending: "No reports to post",
    postingReportOption: "Report #{{number}} · {{date}}",
    postingPickSubtitle_one: "{{count}} pending venue report",
    postingPickSubtitle_other: "{{count}} pending venue reports",
    postingPickBills_one: "{{count}} bill",
    postingPickBills_other: "{{count}} bills",
    postingPickTurnover: "Turnover",
    postingProductTag: "Product",
    postingRecipeTag: "Recipe",
    postingColWarehouse: "Warehouse",
    postingWhSourceRecipe: "Warehouse from the recipe (set for the ingredient in the organization portal)",
    postingWhSourceStation: "The warehouse of the recipe's station",
    postingColAvail: "Available",
    postingColToWriteoff: "To write off",
    colQtyShort: "Qty",
    postingColItem: "Item",
    postingTotalWriteoff: "Total write-off",
    postingPerWarehouse: "per warehouse",
    postingCovered: "covered",
    postingRemoveSub: "Remove substitution (returns stock to negative)",
    postingShortageSummary_one: "{{count}} product is short",
    postingShortageSummary_other: "{{count}} products are short",
    postingNoShortage: "All products available",
    postingSplitMismatch:
      "Allocated amount does not match the required quantity",
    postingCompTitle: "Shortage compensation",
    postingCompMarked: "{{applied}} of {{total}} selected",
    postingCompSelectAll: "select all",
    postingCompApplied_one: "{{count}} compensated",
    postingCompApplied_other: "{{count}} compensated",
    postingSoldTitle: "Sold",
    postingSoldPositions_one: "{{count}} item",
    postingSoldPositions_other: "{{count}} items",
    postingSoldTotalQty: "{{qty}} pcs",
    postingSoldShow: "Show what was sold",
    postingSoldHide: "Hide what was sold",
    postingSoldStationWarehouse: "→ warehouse {{name}}",
    postingSoldStationNoWarehouse: "no warehouse",
    postingScopeStation: "Station",
    postingScopeWaiter: "Waiter",
    postingScopeAll: "All",
    postingScopeWhole: "Showing the whole report",
    postingScopeFiltered: "The filter only affects “Sold” · the whole report is posted",
    postingScopeFilteredBoth: "Showing what was sold here and its products · the whole report is posted",
    postingSoldDirectProduct: "Written off directly as a product",
    postingSoldRecipeError: "The recipe failed to load",
    postingSoldRecipeEmpty: "The recipe has no ingredients",
    postingSoldRecipeHint: "Show the recipe and only its products in the write-off",
    postingFocusNote: "Only the products of {{name}}",
    postingTotalShown: "Total of the shown",
    postingFilterNoProducts: "Nothing is written off for what is shown",
    postingTotalsWhole: "for the whole report",
    // The breakdown at the report (under the selected card / „Waiters and stations ▸")
    postingBreakdownStationsCount_one: "{{count}} station",
    postingBreakdownStationsCount_other: "{{count}} stations",
    postingWriteoffTitle: "Warehouse write-off",
    postingWriteoffProducts_one: "{{count}} product",
    postingWriteoffProducts_other: "{{count}} products",
    postingShortBadge_one: "{{count}} shortage",
    postingShortBadge_other: "{{count}} shortages",
    postingSaveEdit: "Save changes",
    postingEditNone: "No posted reports for the selected period",
    postingPosted: "Report posted",
    postingUpdated: "Changes saved",
    postingErr: "Error posting the report",
    postingNoRecipes: "The report has no sold items",
    postingMissingWarehouse: "Select a warehouse for: {{names}}",
    postingPickWarehouse: "Select",
    postingNoWarehouseHint:
      "Select a warehouse — or set the ingredient's warehouse in the recipe (organization portal) or warehouses on the stations (Organization → Menu → Stations)",
    postingConfirmTitle: "Close the day",
    postingConfirmEditTitle: "Save changes",
    postingConfirmBody:
      "Some products are short — they will be written off into negative stock. Continue?",
    postingConfirmBodyOk:
      "Post the report and write the quantities off from the warehouse?",
    postingConfirmOk: "Post the report",
    postingDocTitle: "Posting document",
    postingDocSubtitle: "Posting #{{id}}",
    postingDocHeading: "Report posting",
    // Reports: sold products / recipes
    soldColName: "Name",
    soldColSold: "Sold (pcs)",
    soldColTotalQty: "Total quantity",
    soldColAvgPrice: "Avg price incl. VAT",
    soldColAvgPriceNet: "Avg price excl. VAT",
    soldColIngredients: "Ingredients",
    // The search also matches the recipe ↔ product link (sold/soldSearchModel):
    // next to the name — why the row matched; under the search box — what matched
    soldSearchPhProduct: "Search a product or recipe…",
    soldSearchPhRecipe: "Search a recipe or product…",
    soldViaRecipe: "in {{list}}",
    soldViaIngredient: "contains {{list}}",
    soldMatchedMore: "and {{count}} more",
    soldQuotedName: "“{{name}}”",
    soldMatchedRecipes_one:
      "By recipe {{names}} — showing its products. Next to the name: how much went into the recipe; the columns are totals for the period.",
    soldMatchedRecipes_other:
      "By recipes {{names}} — showing their products. Next to the name: how much went into each; the columns are totals for the period.",
    soldMatchedProducts_one: "By product {{names}} — showing the recipes it is used in.",
    soldMatchedProducts_other: "By products {{names}} — showing the recipes they are used in.",
    // "Last sale" (recipes only): column, card line, panel header, Excel
    soldColLastSold: "Last sale",
    soldLastSoldLine: "last sale {{date}}",
    // Under the search box — what a row click opens
    soldOpenHintRecipe: "Tap a recipe — sales by day and by report",
    soldOpenHintProduct: "Tap a product — its stock card for the period",
    // The permanent notice above the table (sold/SoldPostedOnlyNotice)
    soldPostedOnlyNotice:
      "Only sales from posted reports are counted here. Today's sales appear once the venue report is made and posted.",
    soldPendingReports_one: "Waiting to be posted: {{count}} report",
    soldPendingReports_other: "Waiting to be posted: {{count}} reports",
    soldPostAction: "Post",
    noSold:
      "No posted sales for the selected period (the report shows posted reports only)",
    soldKpiCount: "Count",
    soldKpiQty: "Total quantity",
    soldKpiRevenue: "Revenue",
    close: "Close",
    print: "Print",
    docEdit: "Edit",
    docDelete: "Void",
    navDeliveryPayments: "Delivery payments",
    navPostedReports: "Posted reports",
    navDeliveriesReport: "Deliveries",
    navWriteoffsReport: "Write-offs",
    navStocktakesReport: "Stocktakes",
    navTransfersReport: "Transfers",
    // The menu and the document tabs (Documents → Deliveries · Unpaid · Report…)
    sectionDocuments: "Documents",
    navDeliveries: "Deliveries",
    navWriteoffs: "Write-offs",
    navStocktakes: "Stocktakes",
    navTransfers: "Transfers",
    docTabsLabel: "Document views",
    docTabCurrent: "Current",
    docTabUnpaid: "Unpaid",
    docTabArchive: "Report",
    docTabByWarehouse: "By warehouse",
    docTabToPost: "To post",
    docPageYourDraft: "Your draft",
    docPageDraftAutosaved: "autosaved {{when}}",
    docPageContinue: "Continue",
    docPageDelete: "Delete",
    docPagePendingTitle: "Waiting for action",
    docPagePendingNotesHint: "Delivery notes without an invoice — when the supplier issues the summary invoice, enter it from here.",
    docPageSavedTodayTitle: "Saved today",
    docPageOlderInArchive: "older ones are under Report",
    docPageEmptyDelivery: "You have no draft and no deliveries have been saved today.",
    docPageEmptyWriteoff: "You have no draft and no write-offs have been saved in this warehouse today.",
    docPageEmptyTransfer: "You have no draft and no transfers have been saved today.",
    woNewOptions: "Scrap with a preset reason",
    // Worksheet bottom bar: draft state and "Missing: …"
    draftStateIdle: "Saved automatically",
    draftStateSaving: "Saving…",
    draftStateServer: "Saved automatically {{time}} · visible to colleagues",
    draftStateServerNoTime: "Saved automatically · visible to colleagues",
    draftStateLocal: "Saved in this browser only {{time}} — no connection to the server",
    draftStateFailed: "The draft is not being saved",
    missingTitle: "Missing:",
    missingOnlyProblems: "Problem rows only",
    missingOnlyProblemsClear: "Show all rows",
    missDate: "Date",
    missDateFuture: "Cannot be in the future",
    missInvoiceNumberHint: "Required for an invoice",
    missScrapReasonHint: "Required for scrap",
    missLines: "Products",
    missCounts: "Counted",
    missRecipes: "Recipes",
    missRowsQty_one: "{{count}} row without quantity",
    missRowsQty_other: "{{count}} rows without quantity",
    missRowsPrice_one: "{{count}} row without price",
    missRowsPrice_other: "{{count}} rows without price",
    missRowsBatch_one: "{{count}} row without batch",
    missRowsBatch_other: "{{count}} rows without batch",
    missRowsExpiry_one: "{{count}} row without expiry",
    missRowsExpiry_other: "{{count}} rows without expiry",
    missRowsWarehouse_one: "{{count}} row without warehouse",
    missRowsWarehouse_other: "{{count}} rows without warehouse",
    missRowsShort_one: "{{count}} row short of stock",
    missRowsShort_other: "{{count}} rows short of stock",
    // Worksheet search: the "Already in the document" group
    searchGroupInDocument: "Already in the document",
    searchGroupAdd: "Add",
    searchInDocumentRow: "row {{row}} · {{quantity}}",
    searchCounted: "counted {{quantity}}",
    searchAddRow: "row",
    searchAddRowHint: "New row for the same product (another batch) — Shift+Enter",
    stockLoading: "Loading stock",
    navHintDrafts_one: "{{count}} draft",
    navHintDrafts_other: "{{count}} drafts",
    navHintUnpaid_one: "{{count}} unpaid",
    navHintUnpaid_other: "{{count}} unpaid",
    navHintToPost_one: "{{count}} to post",
    navHintToPost_other: "{{count}} to post",
    navCriticalReports_one: "{{count}} report is overdue",
    navCriticalReports_other: "{{count}} reports are overdue",
    navCriticalStocktakes_one: "{{count}} unfinished stocktake from yesterday or earlier",
    navCriticalStocktakes_other: "{{count}} unfinished stocktakes from yesterday or earlier",
    navCriticalTotal_one: "{{count}} blocking task",
    navCriticalTotal_other: "{{count}} blocking tasks",
    postingArchiveRef: "Posting reports → Report",
    closeMenu: "Close menu",
    openMenu: "Open menu",
    kickerReport: "Report",
    exportExcel: "Export to Excel",
    viewDocument: "View document",
    rowMenu: "More actions",
    showMore: "Show more ({{count}})",
    pdfHint: "In the print dialog choose “Save as PDF”",
    statusPosted: "Posted",
    statusAnnulled: "Voided",
    statusAnnulledStamp: "VOIDED",
    annulledBanner: "Voided on {{date}}",
    annulReason: "Reason",
    annulReasonPh: "e.g. wrong recipe",
    annulledToast: "Document #{{number}} was voided",
    segAll: "All",
    segPosted: "Posted",
    segAnnulled: "Voided",
    colDocument: "Document",
    colReport: "Report",
    colWrittenOff: "Written off",
    colSubstitutions: "Substitutions",
    kpiValueGross: "Total value · incl. VAT",
    kpiValueNet: "Total value · excl. VAT",
    periodTotalActive: "Period total · excluding voided",
    postedNoDocs: "No documents for the selected period.",
    postedSearchPh: "Search by document, report, user…",
    filterPostedBy: "Posted by",
    filterNoMatch: "No matches",
    filterClear: "Clear",
    filterDone: "Done",
    filterClearAll: "Clear all",
    periodCustom: "Period",
    periodApply: "Apply",
    periodPickStart: "Pick a start date",
    period_today: "Today",
    period_yesterday: "Yesterday",
    period_last7: "Last 7 days",
    period_last30: "Last 30 days",
    period_thisMonth: "This month",
    period_lastMonth: "Last month",
    period_thisYear: "This year",
    prevMonth: "Previous month",
    nextMonth: "Next month",
    month0: "January",
    month1: "February",
    month2: "March",
    month3: "April",
    month4: "May",
    month5: "June",
    month6: "July",
    month7: "August",
    month8: "September",
    month9: "October",
    month10: "November",
    month11: "December",
    weekday_mon: "Mo",
    weekday_tue: "Tu",
    weekday_wed: "We",
    weekday_thu: "Th",
    weekday_fri: "Fr",
    weekday_sat: "Sa",
    weekday_sun: "Su",
    postingPendingSubtitle_one: "{{count}} unposted report · oldest first",
    postingPendingSubtitle_other: "{{count}} unposted reports · oldest first",
    postingAllPostedTitle: "All reports are posted",
    postingAllPostedText: "New reports appear here after the day is closed.",
    postingViewPosted: "View posted reports",
    postingPendingList: "Unposted reports",
    postingAgeToday: "Today",
    postingAgeYesterday: "Yesterday",
    postingAgeDays_one: "{{count}} day",
    postingAgeDays_other: "{{count}} days",
    postingAgeLate_one: "Late · {{count}} day",
    postingAgeLate_other: "Late · {{count}} days",
    postingReportTitle: "Report #{{number}}",
    postingCardNumber: "#{{number}}",
    postingMetaClosed: "closed {{time}}",
    postingMetaTurnover: "turnover {{amount}}",
    postingMetaWaiters_one: "{{count}} waiter",
    postingMetaWaiters_other: "{{count}} waiters",
    postingStatusShort_one: "{{count}} product short",
    postingStatusShort_other: "{{count}} products short",
    postingStatusWithSub: "{{count}} with substitute",
    postingStatusAllAvailable: "Everything is in stock",
    postingStatusNoWarehouse_one: "{{count}} product without a warehouse",
    postingStatusNoWarehouse_other: "{{count}} products without a warehouse",
    // the short ones — in the narrow pending-report card (the full ones are in the tooltip)
    postingStatusNoWarehouseShort: "{{count}} no warehouse",
    postingStatusShortShort: "{{count}} short",
    postingStatusAllAvailableShort: "In stock",
    postingStatusReturned: "Returned after voiding #{{number}}",
    postingStatusReturnedShort: "Returned",
    postingCorrectsAnnulled: "Corrects voided #{{number}}",
    postingChipShort_one: "{{count}} product short",
    postingChipShort_other: "{{count}} products short",
    postingChipSubstituted: "{{count}} substituted",
    postingRemaining_one: "{{count}} product will go negative",
    postingRemaining_other: "{{count}} products will go negative",
    postingCostError: "Prices did not load",
    postingCostStale: "Prices did not refresh — showing the last loaded ones",
    postingCostRetry: "Try again",
    postingCostMissing_one: "no price: {{count}} product",
    postingCostMissing_other: "no price: {{count}} products",
    postingCostMissingHint: "No open lots in the selected warehouse for: {{names}}. The total excl. VAT excludes them.",
    postingCostMissingHintGross: "No open lots in the selected warehouse for: {{names}}. The total incl. VAT excludes them.",
    postingPreviewErr: "The report did not load",
    postingAlreadyPosted: "The report is already posted — refreshing the list.",
    postingSubmitReport: "Post report #{{number}}",
    postingShortConfirmTitle: "Posting with shortage",
    postingShortConfirmBody: "These products will be written off into negative stock. Continue?",
    postingShortConfirmOk: "Post the report",
    postingPostedToast: "Report #{{number}} posted",
    // The message after saving (`savedDocument/`): "Open" — the modal in place
    savedOpen: "Open",
    savedAnotherOne: "+ Another one",
    savedAnotherOneNeuter: "+ Another one",
    postingLeaveBody: "The substitutions chosen for this report will be lost.",
    postingSubsTitle: "Substitute with interchangeable products",
    postingSubsHint: "Split the missing quantity between one or more substitutes.",
    // BE policy (service_report_unposting.py): cancelling/restoring a bill does NOT reverse the substitutions
    postingSubsNotReturned:
      "Substitutions are not reversed automatically when a bill from the report is cancelled or restored. If needed, fix them manually: “Correct and post again” or “Void” in Posting reports → Report.",
    postingAvailableShort: "in stock",
    postingMissing: "Missing {{qty}}",
    postingCoveredLabel: "Covered",
    postingSubQty: "Quantity of {{name}}",
    postingInsteadOf: "⇄ instead of {{name}}",
    postingCoveredNote: "⇄ {{qty}} {{unit}} covered by substitute",
    postingManualCorrectionNote: "manually corrected",
    postingManualCorrectionHint:
      "Manual correction (service account): the entered quantity is written off instead of the quantity from sales. Empty field — back to sales.",
    postingManualCorrectionLockedNote: "manually corrected by the service account",
    postingManualCorrectionLockedHint:
      "The quantity was corrected by the service account and is written off instead of the sales. Only that account can change it or return it to the sales; you can change how it is split between warehouses.",
    postingBySales: "by sales: {{qty}}",
    postingResetToSales: "Back to sales",
    postingColAfter: "After",
    postingPositions_one: "{{count}} line",
    postingPositions_other: "{{count}} lines",
    postingPositionsShown_one: "{{shown}} of {{count}} line",
    postingPositionsShown_other: "{{shown}} of {{count}} lines",
    postingDocTitleNo: "Posting document #{{number}}",
    postingDocSoldTitle: "Sold in the report",
    postingDocSoldLoading: "Loading what was sold…",
    postingDocSoldError: "What was sold failed to load",
    postingDocSoldHint: "Show the products it writes off",
    postingDocSoldCurrentRecipe: "by the current recipe — posted before recipe snapshots",
    postingDocSoldNotPosted: "Not written off by this posting",
    postingRepost: "Correct and post again",
    postingRepostHint: "Voids and reopens the report",
    postingRepostTitle: "Correct document #{{number}}",
    postingRepostText: "Written-off quantities return to stock, and report #{{report}} reopens for posting with the previous substitutions.",
    postingRepostConfirm: "Void and correct",
    postingAnnulTitle: "Void document #{{number}}",
    postingAnnulText: "Written-off quantities return to stock, and report #{{report}} returns to “Report posting”.",
    printShort: "Print",
    chipSearch: "Search: “{{q}}”",
    colDocType: "Type",
    colLinks: "Links",
    colNoteNo: "Delivery note #",
    colPayment: "Payment",
    deliveriesNone: "No deliveries match this filter.",
    deliveriesResult_one: "{{count}} delivery",
    deliveriesResult_other: "{{count}} deliveries",
    deliveriesSearchPh: "Search by number, supplier, product, user…",
    deliveriesSumHint: "(delivery notes inside an invoice are not added twice)",
    deliveryAnnulTitle: "Void delivery",
    deliveryAnnulText: "The quantities are taken out of stock. The document stays in the report as voided.",
    deliveryAnnulOwnLines_one: "Its own {{count}} line is taken out of stock",
    deliveryAnnulOwnLines_other: "Its own {{count}} lines are taken out of stock",
    deliveryAnnulNoOwnLines: "It has no lines of its own — nothing is taken out of stock",
    deliveryAnnulNotesStay_one: "{{count}} delivery note stays received and returns to “Notes without invoice”",
    deliveryAnnulNotesStay_other: "{{count}} delivery notes stay received and return to “Notes without invoice”",
    deliveryAnnulStays: "The document stays in the report as voided.",
    deliverySavedToast: "Delivery #{{number}} saved",
    docHeadingNote: "Delivery note",
    docTypeInvoice: "Invoice",
    docTypeNote: "Note",
    draftStatusDraft: "Draft",
    draftStatusHeld: "On hold",
    draftStatusBusy: "Being edited",
    enteredBy: "entered by {{name}}",
    filterEnteredBy: "Entered by",
    homeColleagues: "Colleagues in progress",
    homeContinueDraft: "Continue the unfinished one",
    homeDiscardAndNew: "Delete and start new",
    homeDraftLines_one: "{{count}} product",
    homeDraftLines_other: "{{count}} products",
    homeDraftModalTitle: "You have an unfinished delivery",
    homeDraftModalText: "Continue it or delete it to start a new one.",
    homeDraftUpdated: "last change {{when}}",
    homeEnterInvoice: "Enter invoice",
    homeNoSupplier: "No supplier",
    homeNotesInfo_one: "{{count}} note · {{from}}",
    homeNotesInfo_other: "{{count}} notes · {{from}} – {{to}}",
    homeNotesShowAll: "Show all {{count}}",
    homeNotesShowLess: "Show less",
    homeTodayCount_one: "{{count}} delivery",
    homeTodayCount_other: "{{count}} deliveries",
    homeTodayNone: "No deliveries entered today yet.",
    homeUnpaidCount_one: "{{count}} unpaid document",
    homeUnpaidCount_other: "{{count}} unpaid documents",
    internalNo: "internal #{{id}}",
    invoiceLinksNotes_one: "The invoice summarises {{count}} delivery note:",
    invoiceLinksNotes_other: "The invoice summarises {{count}} delivery notes:",
    kpiUninvoicedNotes: "Notes without invoice",
    kpiUnpaid: "Unpaid",
    payableWithVatHint: "Incl. VAT — the amount owed to the supplier",
    linkInvoice: "Invoice · internal #{{id}}",
    linkWaitingInvoice: "awaiting invoice",
    linkClosedNoInvoice: "closed without invoice",
    filterInvoiceState: "Invoice",
    invoiceState_waiting: "Awaiting invoice",
    invoiceState_closed: "Closed without invoice",
    invoiceState_inInvoice: "In an invoice",
    linkNote: "Note · internal #{{id}}",
    newDeliveryPlus: "+ New delivery",
    noDocNumber: "no number",
    noteInInvoiceBanner: "This note is included in an invoice — it is paid and corrected there.",
    openInvoice: "Open invoice",
    optional: "optional",
    payAllPaid: "All deliveries are paid.",
    payPaid: "Paid",
    payUnpaid: "Unpaid",
    payByInvoice: "By invoice",
    payAnnulled: "Voided",
    payPaidMany: "Paid",
    payUnpaidMany: "Unpaid",
    payAnnulledMany: "Voided",
    payUntracked: "No payment data",
    payMethod_cash: "Cash",
    payMethod_bank: "Bank transfer",
    payMethod_card: "Card",
    payModalTitle: "Payment",
    payDate: "Payment date",
    payDatePick: "Select the payment date",
    payMethod: "Method",
    payReference: "Reference",
    payConfirm: "Confirm payment",
    payDocsCount_one: "{{count}} document",
    payDocsCount_other: "{{count}} documents",
    paySelected_one: "{{count}} document selected",
    paySelected_other: "{{count}} documents selected",
    payMarkPaid: "Mark as paid",
    payPickFirst: "Tick at least one document",
    payDoneToast_one: "{{count}} document marked as paid",
    payDoneToast_other: "{{count}} documents marked as paid",
    payError: "The payment was not saved",
    payNeedsServerTitle: "Payments are not enabled on the server yet",
    payNeedsServerText: "This screen works once the backend is updated to the version with delivery payments.",
    docLabelInvoice: "Invoice {{number}}",
    docLabelNote: "Delivery note {{number}}",
    payPanelPaid: "Paid on {{date}}",
    payPanelReference: "ref. {{reference}}",
    payPanelMarkedBy: "marked by {{name}}",
    payPanelUnpaidToday: "Unpaid · since today",
    payPanelUnpaid_one: "Unpaid · for {{count}} day",
    payPanelUnpaid_other: "Unpaid · for {{count}} days",
    payPanelByInvoice: "Paid through the invoice",
    payPanelPaidSeparately: "paid separately on {{date}}",
    payPanelAnnulled: "The document is voided",
    payChange: "Change",
    payMarkPaidOne: "Mark as paid",
    payMarkUnpaid: "Mark as unpaid",
    payUnpayTitle: "Mark {{doc}} as unpaid?",
    payUnpayText: "{{doc}} becomes unpaid and returns to “Deliveries → Unpaid”.",
    payUnpayTextNotes_one: "{{doc}} becomes unpaid and returns to “Deliveries → Unpaid” together with its {{count}} delivery note — it is paid through the invoice. A note paid separately before the invoice stays paid.",
    payUnpayTextNotes_other: "{{doc}} becomes unpaid and returns to “Deliveries → Unpaid” together with its {{count}} delivery notes — they are paid through the invoice. Notes paid separately before the invoice stay paid.",
    payUnpaidToast: "{{doc}} is marked as unpaid",
    payUnpayError: "The payment was not undone",
    payUndo: "Undo",
    payUndoneToast_one: "The payment of {{count}} document was undone",
    payUndoneToast_other: "The payment of {{count}} documents was undone",
    payNotesPaidSeparately_one: "{{count}} delivery note was paid separately — not in the amount",
    payNotesPaidSeparately_other: "{{count}} delivery notes were paid separately — not in the amount",
    noteDetach: "Take out of the invoice",
    noteDetachTitle: "Take the delivery note out of the invoice?",
    noteDetachText: "{{doc}} leaves the invoice and returns to “Notes without invoice”. Its stock stays received. If the invoice is paid, the note stays paid with the same payment.",
    noteDetachConfirm: "Take out",
    noteDetachedToast: "{{doc}} was taken out of the invoice",
    noteDetachError: "The delivery note was not taken out",
    noteClose: "Close without invoice",
    noteCloseTitle: "Close without invoice",
    noteCloseText: "The delivery note leaves “Notes without invoice” — no invoice will be awaited for it. If one comes after all, “Reopen for invoicing” brings it back.",
    noteCloseReason: "Reason",
    noteCloseReasonPh: "e.g. The supplier does not issue invoices",
    noteCloseMarkPaid_one: "and mark it as paid",
    noteCloseMarkPaid_other: "and mark them as paid",
    noteCloseStaysUnpaid: "Without the tick an unpaid note stays in “Deliveries → Unpaid” until you pay it.",
    noteCloseConfirm: "Close",
    noteClosePick: "Tick at least one delivery note",
    noteClosedToast_one: "{{count}} delivery note was closed without invoice",
    noteClosedToast_other: "{{count}} delivery notes were closed without invoice",
    noteCloseError: "The delivery notes were not closed",
    noteClosedBanner: "Closed without invoice on {{date}}",
    noteReopen: "Reopen for invoicing",
    noteReopenedToast: "{{doc}} is awaiting an invoice again",
    noteReopenError: "The delivery note was not reopened",
    revClosedWithoutInvoice: "Closed without invoice",
    catalogOpen: "Full list",
    catalogTitle: "All products",
    catalogHint: "Click a row and enter the quantity and price — Enter moves to the next one, Ctrl+Enter adds them all to the delivery",
    catalogFilterPh: "Filter by name",
    catalogAllGroups: "All",
    catalogInDocument: "in the document",
    catalogPicked_one: "{{count}} product selected",
    catalogPicked_other: "{{count}} products selected",
    catalogHintToggle: "ticks",
    catalogHintAdd: "adds",
    catalogAdd: "Add selected",
    colVat: "VAT",
    deleteDraft: "Delete draft",
    deleteDraftTitle: "Delete the draft",
    deleteDraftBody: "The unfinished delivery will be deleted. Nothing has been received into stock.",
    fieldDocType: "Document type",
    fieldNoteNo: "Delivery note #",
    heldToast: "The delivery is on hold — nothing was received into stock",
    heldDraftFailed: "The delivery was saved, but the problem products were not kept as a held draft — no connection to the server or a colleague took over editing.",
    lockLost: "{{name}} took over — your changes are no longer saved",
    notesOffer_one: "The supplier has {{count}} delivery note without invoice. If this invoice covers it, pick it.",
    notesOffer_other: "The supplier has {{count}} delivery notes without invoice. If this invoice covers them, pick them.",
    notesPick: "Pick notes",
    notesLinked_one: "The invoice covers {{count}} note · {{total}}",
    notesLinked_other: "The invoice covers {{count}} notes · {{total}}",
    notesLinkedHint: "Their goods are already in stock — the invoice only links them and is paid instead of them. Add new lines below.",
    notesChange: "Change selection",
    notesModalTitle: "Which notes does the invoice cover?",
    notesModalSub: "The supplier's notes without invoice",
    notesModalHint: "Notes are not received twice. A linked note cannot be edited or voided on its own.",
    notesMoreLines: "… and {{count}} more lines",
    notesPicked_one: "{{count}} note · {{total}}",
    notesPicked_other: "{{count}} notes · {{total}}",
    notesApply: "Link to invoice",
    payMethodLong: "Payment method",
    presenceOnlyYou: "Only you are editing",
    probSaveTitle: "Some products have a problem",
    probSaveSub: "Choose how to continue",
    probSaveHold: "Hold the delivery",
    probSaveHoldHint: "Nothing is received until the problem is solved.",
    probSaveWithout: "Save without the problem ones",
    probSaveWithoutHint: "The other products are received now.",
    problemTitle: "Problem with a product",
    problemHint: "A flagged product does not block your work. On save you choose whether to hold the whole delivery or save it without the product.",
    problemNotePh: "e.g. 3 bottles broken, waiting for a credit note",
    problemDescription: "Problem description",
    problemMark: "Flag",
    problemClear: "Remove flag",
    problemFlagHint: "Flag a problem",
    problemReason_damaged: "Damaged",
    problemReason_short: "Short",
    problemReason_wrong: "Wrong product",
    problemReason_expired: "Expired",
    problemReason_other: "Other",
    productsTitle: "Products",
    saveModalTitle: "Save the delivery",
    saveModalSave: "Save",
    saveNumberRequired: "The invoice number is required",
    saveTypeHintInvoice: "Invoice — the number is required",
    saveTypeHintNote: "Delivery note — can be added to an invoice later",
    saveDelivery: "Save the delivery",
    savedTempFailed: "The draft was not saved — no connection to the server or a colleague took over editing. The sheet stays open; try again.",
    homeLockTitle: "This delivery is being edited right now",
    homeLockText: "{{name}} is working on this delivery ({{supplier}}, {{lines}}, last change {{when}}). If you take it over, {{first}}'s changes stop being saved and {{first}} will see that you took over.",
    homeTakeOver: "Take over editing",
    homeTakeOverToast: "You took over {{name}}'s delivery",
    homeTakeOverFailed: "Could not take over the delivery — try again",
    homeDeleteDraftLocked: "{{name}} is editing the draft right now — it cannot be deleted",
    homeDeleteDraftFailed: "The draft was not deleted — no connection to the server. Try again.",
    shortageSubtitle: "Uncovered sales surplus by warehouse · covered by a back-dated delivery",
    shortageSortName: "By product",
    shortageSortValue: "By value",
    shortagePickFirst: "Tick at least one product",
    shortageSelectedGross_one: "{{count}} product selected · incl. VAT",
    shortageSelectedGross_other: "{{count}} products selected · incl. VAT",
    shortageSelectedNet_one: "{{count}} product selected · excl. VAT",
    shortageSelectedNet_other: "{{count}} products selected · excl. VAT",
    shortageBackdatedTo: "delivery back-dated to {{date}}",
    shortageCompensate: "Compensate with a delivery",
    shortageReportsInfo_one: "{{count}} report · since {{date}}",
    shortageReportsInfo_other: "{{count}} reports · since {{date}}",
    shortageBreakdownOpen: "Breakdown by report",
    shortageCompensateOneHint: "A delivery for this product only",
    interchangeRulesCount_one: "{{count}} rule",
    interchangeRulesCount_other: "{{count}} rules",
    interchangeMembersCount_one: "{{count}} product in the group",
    interchangeMembersCount_other: "{{count}} products in the group",
    interchangeCatalogHint: "Tick the products that replace each other and add them at once",
    reportResult_one: "{{count}} result",
    reportResult_other: "{{count}} results",
    stockSubtitle_one: "{{count}} product · {{inStock}} in stock",
    stockSubtitle_other: "{{count}} products · {{inStock}} in stock",
    stockPickWarehouse: "Choose a warehouse to see its stock.",
    stockMinLine: "min. {{qty}}",
    stockLastPriceLine: "last {{price}}",
    soldSubtitleProduct_one: "{{count}} product in the period",
    soldSubtitleProduct_other: "{{count}} products in the period",
    soldSubtitleRecipe_one: "{{count}} recipe in the period",
    soldSubtitleRecipe_other: "{{count}} recipes in the period",
    soldAvgLine: "avg. price {{price}}",
    soldIngredientsLine_one: "{{count}} ingredient",
    soldIngredientsLine_other: "{{count}} ingredients",
    subSubtitle_one: "{{count}} substitution in the period · all warehouses",
    subSubtitle_other: "{{count}} substitutions in the period · all warehouses",
    deletedSubtitle_one: "{{count}} annulled record · the period is by annulment date",
    deletedSubtitle_other: "{{count}} annulled records · the period is by annulment date",
    newTransferPlus: "+ New transfer",
    trxHomeTodayCount_one: "{{count}} transfer",
    trxHomeTodayCount_other: "{{count}} transfers",
    trxHomeTodayNone: "No transfers yet today.",
    trxSavedToast: "Transfer No. {{number}} saved",
    trxEnteredBy: "entered by {{name}}",
    trxAnnulTitle: "Annul transfer No. {{number}}",
    trxAnnulText: "The quantities return to the source warehouse. The document stays in the report as annulled.",
    trxCatalogTitle: "In stock at {{warehouse}}",
    trxCatalogHint: "Only products in stock at the source warehouse on the date — click a row and enter the quantity",
    trxSegActive: "Active",
    transfersSearchPh: "Search by No., warehouse, product, user, comment…",
    transfersResult_one: "{{count}} transfer",
    transfersResult_other: "{{count}} transfers",
    transfersNone: "No transfers match this filter.",
    newStocktakePlus: "+ New stocktake",
    stocktakeHomeDraftHint: "The draft is saved automatically in this browser until you save or delete the stocktake.",
    stocktakeHomeCounted_one: "{{count}} counted product",
    stocktakeHomeCounted_other: "{{count}} counted products",
    stocktakeCountedShort_one: "{{count}} counted",
    stocktakeCountedShort_other: "{{count}} counted",
    stocktakeDraftModalTitle: "You have an unfinished stocktake",
    stocktakeStart: "Start",
    stkUnfinishedYours: "yours",
    stkCountingNow: "{{name}} is counting now",
    stkWhenToday: "today {{time}}",
    stkWhenYesterday: "yesterday {{time}}",
    stkRowContinue: "Continue",
    stkRowTakeOver: "Take over",
    stkColleagueAskTitle: "{{name}} is already counting {{warehouse}}",
    stkColleagueAskText: "Continue the count already started or start a separate one — the two are saved as separate stocktakes.",
    stkColleagueAskCaption: "The count in progress",
    stkColleagueContinue: "Continue {{first}}'s count",
    stkColleagueContinueHint: "Opens the sheet with everything counted so far — nothing is counted twice",
    stkColleagueSeparate: "Start a separate one",
    stkColleagueSeparateHint: "A new stocktake of {{warehouse}} alongside {{first}}'s",
    stkDraftPill: "Draft · {{who}}",
    stkDraftPillYours: "yours",
    stkNotStarted: "Not started",
    stkNeverDone: "No stocktake yet",
    stkLatestNumber: "#{{id}}",
    stkMoreCounts_one: "+{{count}} count",
    stkMoreCounts_other: "+{{count}} counts",
    stkLateHint: "Last touched before today — sales after the count will show up as a shortage. Finish it today.",
    stkLateOther: "{{name}}'s is late",
    stkLateOtherHint: "{{name}}'s count was last touched before today — sales after it will show up as a shortage. Finish it today.",
    stkStartSeparate: "Start a separate stocktake",
    stkOpenCountOf: "Open {{name}}'s count",
    stkColWarehouse: "Warehouse",
    stkColStatus: "Status",
    stkColLast: "Last completed",
    stkSaveAskTitle: "Save the stocktake of {{warehouse}}?",
    stkSaveAskTitlePlain: "Save the stocktake?",
    stkSaveAskTitleEdit: "Save the changes to stocktake #{{id}}?",
    stkSaveAskText: "Differences from the system stock become movements: the surplus is received, the shortage is written off.",
    stkSaveAskCaption: "Differences from the system",
    stkSaveEqual: "No difference",
    stkSaveRecipes: "Prepared recipes",
    stkSaveTotal: "Total difference",
    stkSaveProducts_one: "{{count}} product",
    stkSaveProducts_other: "{{count}} products",
    stkSavePortions_one: "{{count}} recipe",
    stkSavePortions_other: "{{count}} recipes",
    stkSaveUndo: "A saved stocktake is annulled from Stocktakes → Report.",
    stkSaveConfirm: "Save the stocktake",
    stkSaveConfirmEdit: "Save changes",
    stocktakeSavedToast: "Stocktake #{{number}} saved",
    stocktakeAnnulTitle: "Annul stocktake #{{number}}",
    stocktakeAnnulText: "Its surpluses and shortages are reversed — stock is recalculated without it. The document stays in the report as annulled.",
    stocktakesSearchPh: "Search by #, warehouse, user, reason, product…",
    stocktakeStatusActive: "Active",
    stocktakeStatusAnnulled: "Annulled",
    stocktakeNoGroup: "No group",
    woCatalogTitle: "Full list of products and recipes",
    woCatalogHint: "Click a row and enter the quantity — Enter moves to the next one, Ctrl+Enter adds them all at once",
    woCatalogRecipes: "Recipes",
    woLinesCount_one: "{{count}} line",
    woLinesCount_other: "{{count}} lines",
    newWriteoffPlus: "+ New write-off",
    woTodayNone: "No write-offs in this warehouse today yet.",
    woDocsCount_one: "{{count}} document",
    woDocsCount_other: "{{count}} documents",
    woNoReason: "No reason",
    woProductsCount_one: "{{count}} product",
    woProductsCount_other: "{{count}} products",
    woSavedToast: "Write-off #{{number}} saved",
    woScrapSavedToast: "Scrap #{{number}} saved",
    woAnnulTitle: "Annul write-off #{{number}}",
    woAnnulText: "The written-off quantities return to the warehouse. The document stays in the report as annulled.",
    woReportSearchPh: "Search by #, reason, comment, product, user…",
    woReportNone: "No write-offs for this filter.",
    woStatusActive: "Active",
    wsCreateNamed: "New product “{{name}}”",
    stoCatalogTitle: "Whole list — what you count",
    stoCatalogHint: "Tick the products you are about to count — they move to the top of the table in the order you picked them, and you count them one after another with ↓.",
    // "Whole list" (components/search/CatalogPickerModal) — columns, groups, selection tray
    catAllVisible: "All visible",
    catColPrice: "Price",
    catEmpty: "The catalogue is empty",
    catNoMatchInGroup: "No matches in “{{group}}”",
    catShowAllGroups_one: "Show all groups · {{count}} match",
    catShowAllGroups_other: "Show all groups · {{count}} matches",
    catCreateNamed: "New product “{{name}}”",
    catGroups: "Groups",
    catOnlyInStock: "Only in stock",
    catHideAdded: "Hide added",
    catSelected_one: "{{count}} selected",
    catSelected_other: "{{count}} selected",
    catNothingSelected: "Nothing ticked — Enter or click a row",
    catUnpick: "Remove “{{name}}” from the selection",
    catAddCount_one: "Add {{count}} selected",
    catAddCount_other: "Add {{count}} selected",
    catShownOf: "{{shown}} of {{total}}",
    catKeyRow: "row",
    catKeyGroup: "group",
    catKeyEscClear: "clears the filter",
    catKeyEscClose: "closes",
    catMore: "+{{count}} more",
    // in-row entry, "Min.", the total, the "Below minimum" / "From this supplier" chips, overwrite
    catColMin: "Min.",
    catColSum: "Amount",
    catFieldPrice: "Unit price excl. VAT",
    catFieldPriceHint: "In a ticked row — price excl. VAT per base unit, prefilled with the last delivery price; in the others — the last delivery price",
    catFieldPriceGross: "Unit price incl. VAT",
    catFieldPriceGrossHint: "In a ticked row — price incl. VAT (what you pay) per base unit, prefilled with the last delivery price; in the others — the last delivery price",
    catSelectedSum: "total",
    catBelowMinimum: "Below minimum",
    catBelowMinimumHint: "Products whose stock is below the minimum quantity — to reorder",
    catFromSupplier: "From this supplier",
    catFromSupplierHint: "Products with a delivery price from “{{name}}”",
    catKeyEdit: "tick and enter",
    catKeyPrice: "to the price",
    catOverwriteTitle_one: "Overwrite {{count}} row in the sheet?",
    catOverwriteTitle_other: "Overwrite {{count}} rows in the sheet?",
    catOverwriteText: "Rows already filled in on the sheet get what you entered here; the other picked products are added as they are.",
    catOverwriteCaption: "On the sheet → new",
    catOverwriteConfirm: "Overwrite and add",
    catOverwriteBack: "Back",
    ichGroupTitle: "Which products replace each other",
    ichGroupLead: "Enter the quantities that are worth the same. When a product is short during report posting or a stocktake, the system offers to write off another product from the group at the same ratio.",
    ichOrderLabel: "Which is written off first",
    ichOrderOldest: "Oldest delivery",
    ichOrderManual: "In this order",
    ichOrderOldestHelp: "Among the substitutes, the one with the oldest delivery is used first — older stock runs out first.",
    ichOrderManualHelp: "No. 1 is used first, then No. 2… — reorder with the arrows, Alt+↑/↓ or by dragging the number.",
    ichSlotLabel: "Product {{index}}",
    ichSlotFirstHint: "type its name or pick from the full list",
    ichSlotSecondHint: "…and a product that can replace it",
    ichSlotMoreLabel: "Another product",
    ichSlotMoreHint: "optional — every product in the group replaces the others",
    ichKeysAmounts: "between quantities",
    ichKeysMove: "moves the product",
    ichDragHandle: "Drag to reorder",
    ichPriorityAria: "Priority {{index}}",
    ichPriorityNone: "No priority — oldest delivery first",
    ichAmountAria: "Quantity of {{name}}",
    ichMoveUp: "Move up",
    ichMoveDown: "Move down",
    ichRemove: "Remove {{name}} from the group",
    ichUnitFallback: "u.",
    ichReady: "Ready to save",
    ichMissingAmount: "Enter a quantity for: {{names}}",
    ichExampleLabel: "Example",
    ichExampleA: "Coffee A",
    ichExampleB: "Coffee B",
    ichExampleUnit: "kg",
    ichExampleScenario: "If 10 {{unit}} of {{a}} are short, the system offers to write off 7 {{unit}} of {{b}} — and vice versa: when {{b}} is short, {{a}} is offered.",
    ichPreviewTitle: "How it will work",
    ichPreviewPick: "If short",
    ichPreviewShortage: "Short:",
    ichPreviewOffer: "the system offers to write off:",
    ichPreviewOr: "or",
    ichPreviewNeedAmount: "(enter a quantity)",
    ichPreviewOldestNote: "the one with the oldest delivery first",
    ichPreviewNextNote: "The next one is used if the previous is not enough.",
    ichPreviewNeedRecipient: "Enter a quantity for {{name}} to see the example.",
    ichPreviewWhere: "Applies to report posting and stocktakes. The substitution is offered — you confirm it.",
    ichPriorityOldest: "The one with the oldest delivery is written off first",
    ichPriorityOrder: "Written off first: {{names}}",
    ichPriorityRest: "the rest — by oldest delivery",
    ichEmptyTitle: "No substitution rules yet",
    ichEmptyText: "A rule says which products can replace each other and in what quantity. When one is short during report posting or a stocktake, the system offers to write off another from the same rule.",
    ichNoMatch: "No rule matches “{{query}}”",
    ichClearSearch: "Clear search",
    ichListKeys: "pick and open a rule",
    // Suggestions of the write-off "Reason" field (writeoff/components/WriteoffReasonField)
    wrPreviousReasons: "Previous reasons",
    wrReasonUsed_one: "Used {{count}} time",
    wrReasonUsed_other: "Used {{count}} times",
    wrKeyPick: "picks",
    // Change history of a document (components/revisions/)
    revTitle: "Change history",
    revNone: "No changes since it was created",
    revEdits_one: "{{count}} edit",
    revEdits_other: "{{count}} edits",
    revLast: "last {{when}} · {{name}}",
    revChanges_one: "{{count}} change",
    revChanges_other: "{{count}} changes",
    revNoValueChanges: "saved without changing any value",
    revCreated_delivery: "created",
    revCreated_writeoff: "created",
    revCreated_stocktake: "created",
    revCreated_transfer: "created",
    revAdded: "added",
    revRemoved: "removed",
    revLinkedNotes: "Summarised notes",
    revTotalNet: "Total excl. VAT",
    revTotalGross: "Total incl. VAT",
    revSubstitution: "Substitution",
    revAutoPrice: "automatic",
    revUnknownUser: "unknown user",
    revLoadError: "The change history could not be loaded",
    revShowHistory: "Show the change history",
    revHideHistory: "Hide the change history",
    // Confirmations (components/confirm): a question naming the object, what happens, a preview, a specific verb
    cfmMore: "+{{count}} more",
    cfmFinal: "This cannot be undone.",
    cfmPortions_one: "{{value}} portion",
    cfmPortions_other: "{{value}} portions",
    cfmLeaveTitle: "Leave {{document}} without saving?",
    cfmLeaveDraftTitle: "Leave {{document}}?",
    cfmLeaveText: "Unsaved changes are discarded — nothing changes in the warehouse.",
    cfmLeaveDraftText: "What you counted stays as a draft in this browser and you will be offered to continue next time. Nothing is saved to the warehouse yet.",
    cfmLeavePostingText: "The substitutions chosen for report #{{number}} are discarded. The report stays unposted in the list — nothing is written off.",
    cfmLeaveDraftNotSavedTitle: "The draft is not saved (no connection to the server). Leave without saving?",
    cfmLeaveDraftNotSavedText: "Changes in the sheet since the last successful save will be lost. Stay and try again when the connection is back.",
    cfmLeaveCaption: "Unsaved lines",
    cfmLeaveSheetCaption: "Lines in the sheet",
    cfmLeaveDraftCaption: "Kept in the draft",
    cfmLeaveFinal: "Discarded changes cannot be recovered.",
    cfmLeaveConfirm: "Leave without saving",
    cfmLeaveDraftConfirm: "Leave, keep the draft",
    cfmLeaveStay: "Stay",
    cfmDocNewWriteoff: "the new write-off",
    cfmDocNewScrap: "the new scrap",
    cfmDocEditWriteoff: "editing write-off #{{number}}",
    cfmDocEditScrap: "editing scrap #{{number}}",
    cfmDocNewTransfer: "the new transfer",
    cfmDocEditTransfer: "editing transfer #{{number}}",
    cfmDocNewStocktake: "the new stocktake",
    cfmDocEditStocktake: "editing stocktake #{{number}}",
    cfmDocEditDelivery: "editing delivery #{{number}}",
    cfmDocPosting: "posting report #{{number}}",
    cfmRemoveRowTitle: "Remove “{{name}}” from the sheet?",
    cfmRemoveRowTitlePlain: "Remove the line from the sheet?",
    cfmRemoveRowText: "The line with {{qty}} is removed from the sheet. Nothing changes in the warehouse until you save.",
    cfmRemoveRowTextEmpty: "The line is removed from the sheet. Nothing changes in the warehouse until you save.",
    cfmRemoveRowUndo: "You can add it again from the search.",
    cfmRemoveRowConfirm: "Remove the line",
    cfmSplitRecipeTitle: "Split the recipe “{{name}}”?",
    cfmSplitRecipeText_one: "The line is replaced by {{count}} product line — the recipe ingredient for {{portions}}, with a quantity and batch you can change.",
    cfmSplitRecipeText_other: "The line is replaced by {{count}} product lines — the recipe ingredients for {{portions}}, each with a quantity and batch you can change separately.",
    cfmSplitRecipeCaption: "Lines to be added",
    cfmSplitRecipeDefaultPortions: "Portions are empty — quantities are for 1 portion.",
    cfmSplitRecipeUndo: "You can merge them back with “{{action}}”.",
    cfmSplitRecipeConfirm_one: "Split into {{count}} line",
    cfmSplitRecipeConfirm_other: "Split into {{count}} lines",
    cfmRevertTitle: "Merge back into the recipe “{{name}}”?",
    cfmRevertText_one: "{{count}} product line is merged back into one “{{name}}” line for {{portions}} — changes to its quantity and batch are lost.",
    cfmRevertText_other: "{{count}} product lines are merged back into one “{{name}}” line for {{portions}} — changes to their quantities and batches are lost.",
    cfmRevertCaption: "Lines to be merged",
    cfmRevertUndo: "You can split it again with “{{action}}”.",
    cfmRevertConfirm: "Merge into the recipe",
    cfmDeleteDraftTitle: "Delete the unfinished delivery?",
    cfmDeleteDraftTitleSupplier: "Delete the unfinished delivery from “{{supplier}}”?",
    cfmDeleteDraftText: "The draft and all its lines are deleted. Nothing has been received, so the warehouse does not change.",
    cfmDeleteDraftCaption: "To be deleted",
    cfmDraftDeliveryTitle: "Continue the unfinished delivery?",
    cfmDraftDeliveryTitleBy: "Continue {{name}}'s unfinished delivery?",
    cfmDraftStocktakeTitle: "Continue the unfinished stocktake?",
    cfmDraftStocktakeTitleBy: "Continue {{name}}'s unfinished stocktake?",
    cfmDraftSavedAt: "The draft was saved automatically on {{when}}. Continue from it or start over.",
    cfmDraftCaption: "In the draft",
    cfmDraftContinue: "Continue the draft",
    cfmDraftContinueHint: "Its lines are loaded into the sheet and you carry on from there.",
    cfmDraftStocktakeContinueHint: "What you counted is loaded into the sheet and you carry on counting.",
    cfmDraftDiscard: "Delete and start over",
    cfmDraftDiscardHint: "The draft is deleted for good and the sheet starts empty.",
    cfmDraftAskDeliveryTitle: "Continue the delivery from “{{supplier}}”?",
    cfmDraftAskDeliveryTitlePlain: "Continue the unfinished delivery?",
    cfmDraftAskDeliveryText: "Each user has only one unfinished delivery — to start a new one, this one has to be saved or deleted.",
    cfmDraftAskStocktakeTitle: "Continue the stocktake of “{{warehouse}}”?",
    cfmDraftAskStocktakeTitlePlain: "Continue the unfinished stocktake?",
    cfmDraftAskStocktakeText: "This browser already has a stocktake in progress — to start a new one, this one has to be saved or deleted.",
    cfmDraftAskCaption: "Unfinished",
    cfmDraftAskContinueHint: "Opens the sheet with its lines.",
    cfmDraftAskStocktakeContinueHint: "Opens the sheet with what you counted so far.",
    cfmDraftAskDiscardHint: "The draft is deleted for good and an empty sheet opens.",
    cfmLockTitle: "Take over {{name}}'s delivery?",
    cfmLockText: "{{name}} is editing it right now. If you take it over, {{first}}'s changes stop being saved and {{first}} will see that you took over.",
    cfmLockCaption: "The draft",
    cfmLockConfirm: "Take over from {{first}}",
    cfmProblemTitle_one: "{{count}} product with a problem — how should the delivery be saved?",
    cfmProblemTitle_other: "{{count}} products with a problem — how should the delivery be saved?",
    cfmProblemText: "A product with a problem is not received until the problem is solved. Choose what happens to the rest.",
    cfmProblemCaption: "With a problem",
    cfmProblemHoldHint: "Nothing is received — the delivery stays a draft with status “On hold” until the problem is solved.",
    cfmProblemWithoutHint_one: "{{count}} product is received now, the problem ones stay in a held draft.",
    cfmProblemWithoutHint_other: "{{count}} products are received now, the problem ones stay in a held draft.",
    cfmShortageTitle_one: "Post report #{{number}} with a shortage of {{count}} product?",
    cfmShortageTitle_other: "Post report #{{number}} with a shortage of {{count}} products?",
    cfmShortageText: "These products are short in the warehouse and will be written off into negative stock until a later delivery or stocktake evens it out.",
    cfmShortageCaption: "Short",
    cfmShortageUndo: "If it turns out to be a mistake, the posting can be voided from “{{report}}”.",
    cfmShortageConfirm: "Post with shortage",
    cfmAnnulDeliveryTitle: "Void delivery #{{number}}?",
    cfmAnnulDeliveryTitleSupplier: "Void delivery #{{number}} from “{{supplier}}”?",
    cfmAnnulDeliveryCaption: "Leaving the warehouse",
    cfmAnnulDeliveryConfirm: "Void the delivery",
    cfmAnnulWriteoffTitle: "Void write-off #{{number}}?",
    cfmAnnulScrapTitle: "Void scrap #{{number}}?",
    cfmAnnulWriteoffCaption: "Returning to the warehouse",
    cfmAnnulWriteoffConfirm: "Void the write-off",
    cfmAnnulScrapConfirm: "Void the scrap",
    cfmAnnulTransferTitle: "Void transfer #{{number}}?",
    cfmAnnulTransferCaption: "Returning to “{{warehouse}}”",
    cfmAnnulTransferConfirm: "Void the transfer",
    cfmAnnulStocktakeTitle: "Void stocktake #{{number}}?",
    cfmAnnulStocktakeCaption: "Differences to be undone",
    cfmAnnulStocktakeConfirm: "Void the stocktake",
    cfmAnnulUndo: "The document can be restored from the “{{report}}” report.",
    cfmRestoreTitle: "Restore {{document}}?",
    cfmRestoreDoc_delivery: "delivery #{{number}}",
    cfmRestoreDoc_writeoff: "write-off #{{number}}",
    cfmRestoreDoc_scrap: "scrap #{{number}}",
    cfmRestoreDoc_stocktake: "stocktake #{{number}}",
    cfmRestoreDoc_transfer: "transfer #{{number}}",
    cfmRestoreDoc_substitution: "substitution #{{number}}",
    cfmRestoreDoc_report: "posting #{{number}}",
    cfmRestoreDocOther: "record #{{number}}",
    cfmRestoreCaption: "Applied again",
    cfmRestoreUndo: "The document can be voided again from its view.",
    cfmRestoreVerb_delivery: "Restore the delivery",
    cfmRestoreVerb_writeoff: "Restore the write-off",
    cfmRestoreVerb_scrap: "Restore the scrap",
    cfmRestoreVerb_stocktake: "Restore the stocktake",
    cfmRestoreVerb_transfer: "Restore the transfer",
    cfmRestoreVerb_substitution: "Restore the substitution",
    cfmRestoreVerb_report: "Restore the posting",
    cfmRestoreVerbOther: "Restore the record",
    // Column header filters of the reports (components/filters/ColumnHeader, ColumnFilterPanel, FilterSummaryLine)
    cfFilters: "Filters",
    cfSelectAll: "All",
    cfOnly: "Only this",
    cfFrom: "from",
    cfTo: "to",
    cfFilterAria: "Filter by “{{column}}”",
    cfRemove: "Remove the filter “{{filter}}”",
    cfKeyToggle: "ticks",
    cfKeyOnly: "only this",
    cfKind: "Kind",
    cfKindReason: "Kind and reason",
    cfNoMatch: "Nothing matches the search and the filters",
    cfClearFilters: "Clear the filters",
    cfWithSurplus: "With a surplus",
    cfNoSurplus: "No surplus",
    cfWithShortage: "With shortages",
    cfNoShortage: "No shortages",
    scTitle: "Stock card",
    scCardAria: "Stock card of {{name}}",
    scClose: "Close the card",
    scInStock: "In stock",
    scInStockAsOf: "In stock on {{date}}",
    scTabMovements: "Movements",
    scTabLots: "Batches",
    scTabSubstitutions: "Substitutions",
    scOpening: "Opening balance",
    scClosing: "Closing balance",
    scColIn: "In",
    scColOut: "Out",
    scColPrice: "Price",
    scColUser: "Entered by",
    scColNote: "Note",
    scTypeAll: "All",
    scTypeFilterAria: "Movement type",
    scDoc_delivery: "Delivery",
    scDoc_report: "Report",
    scDoc_writeoff: "Write-off",
    scDoc_scrap: "Scrap",
    scDoc_stocktake: "Stocktake",
    scDoc_transfer: "Transfer",
    scDoc_substitution: "Substitution",
    scDocNumber: "{{label}} #{{number}}",
    scCoveredBy: "⇄ covered by {{name}}",
    scBatchNote: "batch {{batch}}",
    scExpiryNote: "until {{date}}",
    scEmptyPeriod: "No movements in this period.",
    scEmptyFiltered: "No movements of this type in the period.",
    scExcel: "Excel",
    scInconsistent: "The movements do not add up to the closing balance — check the product history.",
    scNegativeHint: "Below zero — more was written off than was in stock",
    scSoldWithoutStock: "⚠ {{qty}} sold without stock",
    scWrittenOffWithoutStock: "⚠ {{qty}} written off without stock",
    scCoveredNegative: "({{qty}} cover what was sold without stock)",
    scColReceived: "Received",
    scColRemaining: "Remaining",
    scLotsHint: "Open batches right now — in the order they will be written off (oldest first).",
    scLotsEmpty: "No open batches.",
    scColSubstitution: "Substitution",
    scSubsEmpty: "No substitutions in this period.",
    scSubsCovered: "covered {{qty}}",
    scSubsTaken: "written off {{qty}}",
    scSubsCountReplaced_one: "replaced another product once",
    scSubsCountReplaced_other: "replaced another product {{count}} times",
    scSubsCountReplacedBy_one: "was replaced once",
    scSubsCountReplacedBy_other: "was replaced {{count}} times",
    scBadgeReplaced: "⇄ replaced {{name}}",
    scBadgeReplacedBy: "⇄ replaced by {{name}}",
    scBadgeTipTitle: "Substitutions for {{period}}",
    scBadgeTipReplaced_one: "Replaced {{name}} — once, {{qty}}",
    scBadgeTipReplaced_other: "Replaced {{name}} — {{count}} times, {{qty}}",
    scBadgeTipReplacedBy_one: "Replaced by {{name}} — once, {{qty}}",
    scBadgeTipReplacedBy_other: "Replaced by {{name}} — {{count}} times, {{qty}}",
    scMasterListAria: "Products in the warehouse",
    scKeysSwitch: "product",
    scKeysClose: "closes",
    ichDocNewRule: "the new rule",
    ichDocEditRule: "the rule “{{name}}”",
    ichDeleteTitle: "Delete the rule “{{name}}”?",
    ichDeleteText: "Its products will no longer be offered as interchangeable when posting reports or counting stock. Stock levels and documents already saved do not change.",
    ichDeleteCaption: "Products in the rule",
    ichDeleteFinal: "This cannot be undone — the rule has to be created again.",
    ichDeleteConfirm: "Delete the rule",
    cfmPostingAnnulTitle: "Void the posting of Report #{{report}}?",
    cfmPostingRepostTitle: "Correct the posting of Report #{{report}}?",
    cfmPostingAnnulNote: "The voided posting stays in this report, struck through, with the reason; the report can be posted again.",
    cfmPostingAnnulConfirm: "Void the posting",
    cfmPostingRepostConfirm: "Void and post again",
    upPriceFromSum: "Calculated from the entered line total — the total is saved exactly",
    // Shared report toolbar and period picker (round 4)
    tbPeriodPickRange: "pick the first and the last day",
    tbPeriodPickEnd: "pick the last day",
    tbToolbarAria: "Report toolbar",
    tbColAnnulReason: "Void reason",
    tbColAgeDays: "Days since delivery",
    tbColReports: "Reports",
    tbColOldestReport: "Oldest report",
    stkStatusAll: "All",
    stkStatusCounted: "Counted",
    stkStatusUncounted: "Not counted",
    stkStatusDiff: "With difference",
    stkAllGroups: "All groups",
    stkGroupsAria: "Product groups",
    stkBandProgress: "{{counted}} of {{total}}",
    stkBandPinned_one: "{{count}} picked to count",
    stkBandPinned_other: "{{count}} picked to count",
    stkBandRestHint: "they stay unchanged; type 0 if the product is missing",
    stkEmptyCounted: "Nothing counted yet.",
    stkEmptyUncounted: "Every product is counted.",
    stkEmptyDiff: "No differences — the count matches the expected stock.",
    stkTotalsSurplus: "Surpluses",
    stkTotalsShortage: "Shortages",
    stkTotalsNetCaption: "Net result",
    stkIssuePrice: "Enter a unit price for: {{products}}",
    stkIssueMore_one: "and {{count}} more",
    stkIssueMore_other: "and {{count}} more",
    stkPricePh: "price *",
    stkPriceRequiredHint: "No delivery price in this warehouse — enter the surplus unit price, otherwise it enters at 0.",
    stkPriceAdminOnly: "Only the UnrealSoft service account can change the unit price.",
    sdGroupRecipe: "By recipe",
    sdGroupRecipeTip: "Current recipes × what was sold on bills closed in the period, at the current average cost.",
    sdGroupPosted: "Posted",
    sdGroupPostedTip: "What posting the reports dated in the period wrote off, at FIFO cost — including what was written off instead of another product (substitution).",
    sdGroupDiff: "Difference",
    sdGroupDiffTip: "Posted − by recipe. “+” means more was written off than the recipes say; “−” means less.",
    sdColQty: "Qty",
    sdDatesHint: "By recipe counts bills by the time they were closed; Posted counts reports by their date.",
    sdDatesHintTip: "The two differ when a recipe changed after posting, the price differs (current average vs FIFO), a product was substituted, a quantity was corrected by hand while posting, or the report is dated outside the period (for example, run after midnight).",
    sdDonorLine: "{{qty}} written off instead of another product",
    sdCoveredLine: "{{qty}} covered by another product",
    sdKeysSwitch: "row",
    sdPostedByWarehouse: "Posted by warehouse: {{list}}",
    sdCardPeriodNote: "The card uses the report period ({{period}}) and opens on the postings — the “All” chip shows every movement.",
    sdRecipeKicker: "Sold recipe",
    sdRecipePanelAria: "Sold recipe {{name}}",
    sdClosePanel: "Close the panel",
    sdSoldLabel: "Sold",
    sdTabDays: "By day",
    sdTabReports: "Reports",
    sdTabIngredients: "Ingredients",
    sdDaysHint: "Sales by the day the bill was closed — only the days with sales.",
    sdDaysHoursHint: "By hour — in Reports → “Custom reports” (the “Items” filter).",
    sdReportsHint: "The period's bills by the posted report they belong to. Click or press Enter to open the posting.",
    sdReportsEmpty: "No posted reports with this recipe in the period.",
    sdIngredientsNote: "By the CURRENT recipe × {{sold}} sold — theoretical usage, not what was written off. The write-off is in “Sold products” → “Posted”.",
    sdIngredientsEmpty: "The recipe has no ingredients.",
    sdColPerPortion: "Per portion",
    sdColTotalQty: "Total",
    sdPerPortionLine: "{{qty}} per portion",
    sdIngredientsTotal: "Cost by recipe",
    sdXlsDonor: "Posted · incl. instead of another product",
    sdXlsCovered: "Covered by another product",
    stqStatusPinned: "To count",
    stqBandPinnedHint: "(picked from the list)",
    stqBandRestHint: "they stay as they are; type 0 if missing",
    stqEmptyPinned: "Nothing to count — pick products from “Full list”.",
    stqBatchLink: "+ batch / expiry",
    // "Counted" in several passes (StocktakeCountExtras)
    stqCountAdd: "Add more",
    stqCountAddHint: "Add more to the counted amount — or type “+5” in the field (“-2” corrects)",
    stqCountHistory: "Counting history",
    stqCountClear: "Clear",
    stqCountClearHint: "Clear this row's count and start over",
    stqTotalsWithVat: "(incl. VAT {{value}})",
    // “Zero the warehouse” on the new stocktake sheet (useStocktakeZeroing)
    stqZeroButton: "Zero the warehouse",
    stqZeroNeedsWarehouse: "Choose a warehouse first",
    stqZeroNothing: "No products with stock in “{{warehouse}}” as of {{date}}.",
    stqZeroTitle: "Zero the “{{warehouse}}” warehouse as of {{date}}?",
    stqZeroText_one: "{{count}} product gets “Counted” 0.",
    stqZeroText_other: "{{count}} products get “Counted” 0.",
    stqZeroTextNegative_one: "{{count}} of them is below zero and also becomes 0.",
    stqZeroTextNegative_other: "{{count}} of them are below zero and also become 0.",
    stqZeroTextCounted_one: "The {{count}} product already counted stays as it is.",
    stqZeroTextCounted_other: "The {{count}} products already counted stay as they are.",
    stqZeroTextWhole: "Applies to the whole warehouse — the filters above the table do not limit it.",
    stqZeroCaption: "Stock as of {{date}}",
    stqZeroNegativeTag: "below zero",
    stqZeroAll: "Zero all ({{count}})",
    stqZeroAllHint: "Everything becomes 0. Shortage of about {{value}} (at the average price; the exact one is calculated on save).",
    stqZeroAllHintNoPrice: "Everything becomes 0. The shortage value is calculated on save.",
    stqZeroNegative: "Only below zero ({{count}})",
    stqZeroNegativeHint: "Only the products below zero become 0, with no value. The rest wait to be counted.",
    stqZeroNegativeNone: "No products below zero.",
    stqZeroNoteDraft: "Nothing is saved until you confirm the stocktake — you can fix single rows.",
    stqZeroNoteUndo: "A saved stocktake is annulled from Stocktakes → Report and the stock comes back.",
    stqZeroReason: "Warehouse zeroing",
    stqZeroApplied_one: "“Counted” 0 for {{count}} product — review and confirm the stocktake.",
    stqZeroApplied_other: "“Counted” 0 for {{count}} products — review and confirm the stocktake.",
    dashNav: "Dashboard",
    dashTitle: "Dashboard",
    dashLocalOff: "Local mode — the dashboard does not show the warehouse state until the cloud is reachable again.",
    dashQueueAria: "Warehouse tasks",
    dashQuickAria: "Quick actions",
    dashQuickDelivery: "New delivery",
    dashQuickWriteoff: "New write-off",
    dashQuickTransfer: "Transfer",
    dashQuickStocktake: "Stocktake",
    dashKeysQuick: "quick actions",
    dashKeysRows: "move between tasks",
    dashKeysEnter: "runs the row's action",
    dashGroupBlocking: "Blocking",
    dashGroupShortage: "Sales surplus",
    dashGroupDrafts: "Your drafts",
    dashGroupWaiting: "Waiting",
    dashAllClear: "Nothing to do",
    dashAllClearHint: "Overdue reports, unfinished drafts, unpaid deliveries and delivery notes without an invoice will show up here.",
    dashSourceFailed: "{{source}} — could not load",
    dashRetry: "Try again",
    dashSourcePosting: "Reports to post",
    dashSourceDelivery: "Delivery drafts",
    dashSourceWriteoff: "Write-off drafts",
    dashSourceStocktake: "Unfinished stocktakes",
    dashSourceTransfer: "Transfer drafts",
    dashSourceShortage: "Sales surplus",
    dashSourcePayable: "Unpaid deliveries",
    dashSourceNotes: "Delivery notes without an invoice",
    dashContinue: "Continue",
    dashTakeOver: "Take over",
    dashPost: "Post",
    dashPay: "Pay",
    dashCompensate: "Compensate",
    dashEditingNow: "{{name}} is editing it now",
    dashDraftTitleDelivery: "Delivery draft · {{label}}",
    dashDraftTitleWriteoff: "Write-off draft · {{label}}",
    dashDraftTitleStocktake: "Stocktake draft · {{label}}",
    dashDraftTitleTransfer: "Transfer draft · {{label}}",
    dashTaskStaleStocktake: "Unfinished stocktake · {{label}}",
    dashTaskHeldDelivery: "Delivery on hold · {{label}}",
    dashTaskReport: "Report No. {{number}} · {{date}}",
    dashTaskReportContext_one: "{{count}} bill · turnover",
    dashTaskReportContext_other: "{{count}} bills · turnover",
    dashTaskReportLate_one: "{{count}} day late",
    dashTaskReportLate_other: "{{count}} days late",
    dashTaskReportLateHint: "An unposted report keeps the stock levels wrong — post it.",
    dashTaskReportOne: "No. {{number}} of {{date}}",
    dashOldestReport: "oldest — No. {{number}} of {{date}}",
    dashTaskPendingReports_one: "{{count}} report to post",
    dashTaskPendingReports_other: "{{count}} reports to post",
    dashTaskShortageHint: "More was sold than the warehouse holds — compensate with a delivery.",
    dashShortageProducts_one: "{{count}} product",
    dashShortageProducts_other: "{{count}} products",
    dashTaskUnpaid_one: "{{count}} unpaid delivery",
    dashTaskUnpaid_other: "{{count}} unpaid deliveries",
    dashOldestWaits_one: "the oldest has waited {{count}} day",
    dashOldestWaits_other: "the oldest has waited {{count}} days",
    dashTaskNotes: "Delivery notes without an invoice · {{supplier}}",
    // Invoice over delivery notes: the notes' lines in the worksheet and the document
    notesRowsBand: "Delivery note {{number}} · {{date}} · internal #{{id}}",
    notesRowsHint: "already in stock — the invoice only covers it",
    notesRowsCount_one: "{{count}} product from the note",
    notesRowsCount_other: "{{count}} products from the notes",
    docNoteSection: "From delivery note {{number}} · {{date}} · internal #{{id}}",
    docNotesLoading: "Loading the delivery notes' lines…",
    // New delivery without a supplier
    issueSupplier: "Select a supplier",
    saveDateFuture: "A delivery cannot be in the future — pick an earlier day or time",
    saveNegTitle: "Sold without stock",
    saveNegHead: "{{name}}: {{qty}} sold without stock on {{days}}.",
    saveNegWrittenOff: "Dated {{date}}, they are written off and all {{incoming}} come in.",
    saveNegCovered: "Dated {{date}}, the delivery will cover {{covered}} and {{rest}} will come in.",
    saveNegMixed: "Dated {{date}}, {{writtenOff}} are written off, the delivery will cover {{covered}} and {{rest}} will come in.",
    saveNegMoveDate: "If the goods arrived on {{day}}, set that date — then the delivery covers what was sold.",
    postingZeroHint: "Nothing is written off. Post it to take it off the pending list — it stays in the report at 0.00.",
    sdDaysEmpty: "The recipe has no sales in the period",
    // "Correct the total" in the delivery
    tadjNetLabel: "Total excl. VAT — type the document's amount; the difference goes to the line with the largest sum",
    tadjGrossLabel: "Total incl. VAT — type the document's amount; the difference goes to the line with the largest sum",
    tadjNoRows: "No line with a quantity and a sum can take the difference",
    tadjTooLarge: "The difference {{delta}} is larger than the line \"{{product}}\" — check the lines",
    tadjApplied: "{{delta}} of the total went to \"{{product}}\"",
    tadjConfirmTitle: "A difference of {{delta}} from the lines?",
    tadjConfirmText: "That is more than rounding. The sum of \"{{product}}\" becomes {{value}} and its price is derived from it.",
    tadjConfirmUndo: "You can fix it by typing the line's sum by hand",
    tadjConfirmOk: "Put it into \"{{product}}\"",
    tadjRowNote: "{{delta}} from correcting the total",
    // Write-off: the recipe's value and the ingredient shortage (wr6)
    wr6IssueShort: "Insufficient stock: {{items}}",
    wr6ShortItem: "{{name}} — needed {{needed}}, available {{available}}",
    wr6ShortItemFrom: "{{name}} — needed {{needed}}, available {{available}} (for {{recipes}})",
    wr6ShortMore_one: "and {{count}} more",
    wr6ShortMore_other: "and {{count}} more",
    wr6Needed: "needed",
    wr6Available: "available",
    wr6OwnShare: "here {{value}}",
    wr6NeededTotal: "needed in total {{value}}",
    wr6FitsLabel: "Enough for",
    wr6BandTitle: "Not enough for {{portions}}:",
    wr6FitAction: "Reduce to {{portions}}",
    wr6DialogTitle: "Not enough stock for “{{name}}”",
    wr6DialogText_one: "For {{portions}}, {{count}} ingredient is short — stock as of {{date}} in warehouse “{{warehouse}}”.",
    wr6DialogText_other: "For {{portions}}, {{count}} ingredients are short — stock as of {{date}} in warehouse “{{warehouse}}”.",
    wr6DialogCaption: "Short",
    wr6DialogFitHint: "That is what the stock covers alongside the other lines. Then save again.",
    wr6DialogSplitHint_one: "The recipe becomes {{count}} product line — correct its quantity or remove it.",
    wr6DialogSplitHint_other: "The recipe becomes {{count}} product lines — correct the short ones or remove them.",
    wr6DialogNoFit: "The ingredients do not cover even 1 portion alongside the other lines.",
    // Prepared recipes in a stocktake (sr6)
    sr6SectionTitle: "Prepared recipes",
    sr6SectionHint: "their ingredients are added to the count",
    sr6PortionsUnit: "port.",
    sr6PerPortion: "per portion:",
    sr6ContentsLoading: "Loading ingredients…",
    sr6ContentsError: "Ingredients failed to load",
    sr6ContentsEmpty: "No stock ingredients — remove the recipe",
    sr6RemoveRecipe: "Remove “{{name}}”",
    sr6RemoveCaption: "Leave the count",
    sr6InRecipesChip: "+{{qty}} in recipes",
    sr6InRecipesTitle: "In prepared recipes",
    sr6InRecipesPart: "{{recipe}} × {{portions}} — {{qty}}",
    sr6InRecipesTotal: "total found {{total}}",
    sr6CountLooseHint: "count it outside the recipe too (or 0)",
    sr6IssueCountLoose: "Count outside the recipe too (or 0): {{products}}",
    sr6IssuePortions: "Enter portions for: {{recipes}}",
    sr6IssueEmptyRecipe: "Recipe “{{name}}” has no stock ingredients — remove it",
    sr6BandPinnedRecipes: "(ingredients of prepared recipes)",
    sr6BandPinnedBoth: "(picked from the list and recipe ingredients)",
    sr6SearchPh: "Search a product or recipe to count…",
    sr6SearchAdded: "added",
    sr6CatalogHint: "Tick the products you will count now — they go to the top of the table in the order picked. Click a row to enter “Counted” right here — Enter moves to the next one. The “Recipes” group are prepared dishes — they go to “Prepared recipes” and their ingredients are added to the count.",
    sr6DocRecipes: "Prepared recipes",
    sr6DocInRecipes: "incl. {{qty}} in recipes",
    // Drafts of write-off, transfer and stocktake (round 6)
    dr6ContinueDraftNeut: "Continue the unfinished one",
    dr6DiscardAndNewNeut: "Delete and start new",
    dr6AskCaptionNeut: "Unfinished",
    dr6AskContinueHintNeut: "Opens the sheet with its lines.",
    dr6LockTextNeut: "{{name}} is editing it right now. If you take it over, {{first}}'s changes stop being saved and {{first}} will see that you took over.",
    dr6TakeOverFailed: "Could not take over the draft — try again",
    dr6DeleteDraftText: "The draft and all its lines are deleted. Nothing has been recorded in stock yet, so quantities do not change.",
    dr6NoWarehouse: "No warehouse",
    dr6WoAskTitle: "Continue the unfinished write-off?",
    dr6WoAskText: "Each user has only one unfinished write-off — to start a new one, this one has to be saved or deleted.",
    dr6WoLockTitle: "Take over {{name}}'s write-off?",
    dr6WoTakeOverToast: "You took over {{name}}'s write-off",
    dr6WoDeleteTitle: "Delete the unfinished write-off?",
    dr6WoPromptTitleBy: "Continue {{name}}'s unfinished write-off?",
    dr6TrxAskTitle: "Continue the unfinished transfer?",
    dr6TrxAskText: "Each user has only one unfinished transfer — to start a new one, this one has to be saved or deleted.",
    dr6TrxLockTitle: "Take over {{name}}'s transfer?",
    dr6TrxTakeOverToast: "You took over {{name}}'s transfer",
    dr6TrxDeleteTitle: "Delete the unfinished transfer?",
    dr6TrxPromptTitleBy: "Continue {{name}}'s unfinished transfer?",
    dr6StkAskText: "Each user has only one unfinished stocktake — to start a new one, this one has to be saved or deleted.",
    dr6StkLockTitle: "Take over {{name}}'s stocktake?",
    dr6StkTakeOverToast: "You took over {{name}}'s stocktake",
    dr6StkDeleteTitle: "Delete the unfinished stocktake?",
    dr6StkDeleteTitleLabel: "Delete the unfinished stocktake of \"{{label}}\"?",
    // Stocktake draft: products + prepared recipes
    dr6StkCountedLines_one: "{{count}} counted line",
    dr6StkCountedLines_other: "{{count}} counted lines",
    sd6DeletedChip: "deleted",
    sd6DeletedHint: "The product was deleted in the organisation. Its history stays, but it is not offered for new documents.",
  },

  // Shared reports table (src/components/dataTable) — column menu, columns
  // panel, pagination and the totals row.
  dataTable: {
    hourShort: "h",
    minuteShort: "min",
    sortAsc: "Sort ascending",
    sortDesc: "Sort descending",
    hideColumn: "Hide column",
    clearColumnFilter: "Clear filter",
    columnMenuFor: "Column menu for {{column}}",
    filterTextHint: "Contains…",
    filterFrom: "from",
    filterTo: "to",
    filterSearch: "Search value",
    filterSelectAll: "All",
    filterClear: "Clear",
    filterNoValues: "No values",
    columns: "Columns",
    density: "Density",
    density_compact: "Compact",
    density_normal: "Normal",
    density_roomy: "Roomy",
    resetLayout: "Reset to default layout",
    pageRange: "{{from}}–{{to}} of {{total}}",
    goToPage: "Go to page {{page}}",
    perPage: "{{count}} per page",
    prev: "Back",
    next: "Next",
    emptyTitle: "No rows",
    emptyHint: "Clear the filters or widen the period.",
    totalsLabel: "Total · {{count}}",
    mobileControls: "Sort",
    mobileSortSection: "Sort by",
    mobileFilterSection: "Filters",
    clearAllFilters: "Clear all filters",
    columnMenuHint: "Sort, filter, hide the column",
    resizeColumn: "Resize column {{column}} (double-click resets the width)",
    moveColumn: "Move column “{{column}}” — drag or ↑/↓",
    columnRequired: "This column can't be hidden",
  },

  reportsPage: {
    depositsReport: "Deposits",
    paidWithDepositShort: "deposit No. {{id}} · {{amount}}",
    // A bill fully covered by a deposit — the method already says so, this is the number
    paidWithDepositNumber: "deposit No. {{id}}",
    depositColumn: "Deposit",
    dpStatus_active: "Active",
    dpStatus_used: "Used",
    dpStatus_expired: "Expired",
    dpStatus_attached: "Attached to a table",
    dpStatus_cancelled: "Cancelled",
    dpKpiTaken: "Taken",
    dpKpiTakenSub: "Count: {{count}} · cash {{cash}}",
    dpKpiTakenSubCancelled: "Count: {{count}} · cash {{cash}} · cancelled {{cancelledCount}} ({{cancelled}})",
    dpKpiUsed: "Used",
    dpKpiUsedSub: "Count: {{count}} · {{percent}} % of taken",
    dpKpiUsedSubEmpty: "Count: {{count}}",
    dpKpiBurned: "Forfeited",
    dpKpiBurnedSub: "Unused parts and expired",
    dpKpiActive: "Active",
    dpKpiExpired: "Expired",
    dpKpiCountSub: "Count: {{count}}",
    dpKpiOutstanding: "Active right now",
    dpKpiOutstandingSub: "Count: {{count}} · regardless of period",
    dpKicker_created: "Taken in the period",
    dpKicker_used: "Used in the period",
    dpKicker_valid_until: "Due in the period",
    dpDepositOne: "{{count}} deposit",
    dpDeposits: "{{count}} deposits",
    dpSearchPlaceholder: "Search by No., name, description, bill, receipt…",
    dpNoData: "No deposits",
    dpEmptyHint: "No deposits for the selected period by this date.",
    dpBasisLabel: "Period by date of",
    dpBasisCreated: "Taking",
    dpBasisUsed: "Use",
    dpBasisValidUntil: "Due date",
    dpColumnNumber: "No.",
    dpColumnCreatedAt: "Taken on",
    dpColumnName: "Name",
    dpColumnDescription: "Description",
    dpColumnAmount: "Amount",
    dpColumnMethod: "Method",
    dpColumnValidUntil: "Due date",
    dpColumnStatus: "Status",
    dpColumnUsedAt: "Used on",
    dpColumnBill: "Table",
    dpColumnUsedAmount: "Paid with it",
    dpColumnBurned: "Forfeited",
    dpColumnCreatedBy: "Taken by",
    dpColumnUsedBy: "Bill waiter",
    dpColumnInvoice: "Invoice No.",
    dpInvoiceAction: "Invoice",
    // “Receipt No.” — the deposit's receipt or why there is none
    dpColumnReceipt: "Receipt No.",
    dpReceiptNotRequired: "not required",
    dpReceiptUnknown: "unclear receipt",
    dpReceiptCardCharged: "card — no receipt",
    dpReceiptMissing: "no receipt",
    dpColumnReported: "Reported",
    dpReportedYes: "Yes",
    dpReportedNo: "No",
    // Cancellation — the reason (or “Cancel”), when and who
    dpColumnCancel: "Cancellation",
    dpColumnCancelledAt: "Cancelled at",
    dpColumnCancelledBy: "Cancelled by",
    dpNoExpiry: "no expiry",
    kitchenMonitors: "Monitors",
    kmListKicker: "Rows by statuses",
    kmAvgKicker: "Average time",
    kmTable: "Table",
    kmStation: "Station",
    kmQty: "Qty",
    kmEnteredAt: "Received",
    kmStatusInProgress: "In progress",
    kmStatusCooked: "Cooked",
    kmStatusIssued: "Issued",
    kmStatusServed: "Served",
    kmType: "Type",
    kmStartedBy: "Started by",
    kmCookedBy: "Cooked by",
    kmIssuedBy: "Issued by",
    kmServedBy: "Served by",
    kmResultOne: "{{count}} row",
    kmResults: "{{count}} rows",
    kmNoData: "No status transitions in the selected period",
    // Sidebar
    reports: "Reports",
    exportExcel: "Export to Excel",
    exportPreparing: "Preparing file...",
    exportFailed: "Export failed",
    exportTooManyBills:
      "There are {{bills}} bills, but one Excel file holds up to {{limit}}. No file was created — narrow the period (or the filters) and try again.",
    tableReports: "Control",
    generalReports: "Sales",
    reportsManagement: "Reports",
    fiscalReports: "Fiscal",
    categoriesReport: "By group",
    collapse: "Collapse",
    billsHistory: "Bills report",
    billsSplits: "Split bills",
    cancellations: "Cancellations",
    invoices: "Invoices",
    personalReports: "Personal reports",
    establishmentReports: "Establishment reports",
    assortmentReport: "By item",
    // In warehouse mode the sellable is a recipe → same report, different name.
    recipesReport: "By recipe",
    discountsReport: "Discounts",
    // The "Clients" report (client discounts and surcharges) — sidebar and title
    clientsReport: "Clients",
    priceChanges: "Price changes",
    priceChangesTitle: "Price changes",
    cardPayments: "Card payments",
    cardPaymentsTitle: "Card payments",
    cardPaymentsConsumedKicker: "WITH RECEIPT",
    cardPaymentsUnresolvedKicker: "PENDING",
    cardPaymentsVoidedKicker: "REVERSED",
    cardPaymentsResults: "{{count}} charges",
    cardPaymentsResultOne: "1 charge",
    cardPaymentsNoData: "No card charges for the period",
    cardPaymentsListKicker: "TERMINAL MOVEMENTS",
    cardPaymentsColTime: "Time",
    cardPaymentsColBill: "Bill",
    cardPaymentsColAmount: "Amount",
    cardPaymentsColStatus: "Status",
    cardPaymentsColPan: "Card",
    cardPaymentsColAuth: "Auth",
    cardPaymentsColReceipt: "Receipt #",
    cardPaymentsColMount: "Terminal",
    cardPaymentsColRefund: "Refunded (void)",
    cardPaymentsTerminal: "Till",
    cardPaymentsStatus_consumed: "With receipt",
    cardPaymentsStatus_voided: "Reversed",
    cardPaymentsStatus_unknown: "Unclear",
    cardPaymentsStatus_void_failed: "No receipt!",
    cardPaymentsMount_pc: "terminal at the till",
    cardPaymentsMount_fiscal: "terminal on the printer",
    fiscalCardPending: "Paid by card — do not charge again",
    priceChangesColKind: "Kind",
    priceChangesKindProduct: "Product",
    priceChangesKindAddition: "Addition",
    priceChangesOldPrice: "Old price",
    priceChangesNewPrice: "New price",
    priceChangesDelta: "Difference",
    priceChangesLineEffect: "Total difference",
    priceChangesActive: "Active",
    priceChangesReverted: "Reverted",
    priceChangesDirection: "Change type",
    priceChangesDown: "Lowered price",
    priceChangesUp: "Raised price",
    priceChangesCountKicker: "Changes in period",
    priceChangesCountTitle: "Changed prices",
    priceChangesDownTitle: "Total reduced",
    priceChangesUpTitle: "Total added",
    priceChangesListKicker: "Price changes list",
    priceChangesResults: "{{count}} changes",
    priceChangesResultOne: "{{count}} change",
    priceChangesNoData: "No price changes for the selected period.",
    waitersReport: "By waiter",
    stationsReport: "By station",
    dashboard: "Dashboard",
    dashboardSection: "Dashboard",
    dashboardTitle: "Dashboard",
    dashboardOpenTables: "Open Tables",
    dashboardOpenCount: "Table count",
    dashboardOpenAmount: "Total amount",
    dashboardClosedToday: "Closed Bills Today",
    dashboardClosedCount: "Bill count",
    dashboardCash: "Cash",
    dashboardCard: "Card",
    dashboardMixed: "Mixed",
    dashboardBills: "bills",
    dashboardDailyReports: "Daily Revenue (last 7 days)",
    dashboardNoReport: "No report",
    dashboardToday: "Today",
    expandMenu: "Expand menu",
    collapseMenu: "Collapse menu",
    dashboardBackToModules: "Back to modules",
    dashboardModuleLabel: "Reports module",
    dashboardAdminRole: "Administrator",
    dashboardRevenueByDays: "Revenue by day",
    dashboardLast7Days: "Last 7 days",
    dashboardDailyRevenue: "Daily revenue",
    dashboardAverage: "Average",
    dashboardNoRevenue: "No revenue",
    dashboardOpen: "Open",
    dashboardClosedTodayShort: "Closed today",
    dashboardTotalAmountLabel: "Total amount",
    dashboardBill: "bill",
    dashboardBillsMany: "bills",
    dashboardTableOne: "table",
    dashboardTableMany: "tables",
    dashboardTablesOpenNow: "Open now",
    dashboardTablesClosedToday: "Closed today",
    dashboardTablesOccupancy: "Open share",
    dashboardTablesNone: "No activity today",
    dashboardTotalShort: "Total",
    dashboardLogout: "Logout",
    dashboardAvgCheck: "Average check",
    dashboardAvgTableTime: "Avg. table time",
    dashboardVoidsToday: "Voids today",
    dashboardDiscountsToday: "Discounts today",
    dashboardMinShort: "min",
    dashboardHourShort: "h",
    dashboardPcs: "pcs",
    dashboardTopKicker: "Top 5 today",
    dashboardTopSubtitle: "Best sellers by revenue",
    dashboardTopEmpty: "No sales today",
    dashboardHeatKicker: "Busy hours",
    dashboardHeatSubtitle: "Closed bills by hour — last 8 days",
    dashboardHeatEmpty: "No data for the period",
    dashboardVoidsHint:
      "Total value of voided products today (by time of void)",
    dashboardDiscountsHint:
      "Total value of all discounts — whole-bill and per-item — from today's closed bills",
    dashboardBillWithDiscount: "on 1 bill",
    dashboardBillsWithDiscount: "on {{count}} bills",
    dashboardStationsKicker: "Revenue by station",
    dashboardStationsSubtitle: "Today's split",

    // Common
    refresh: "Refresh",
    filters: "Filters",
    clear: "Clear",
    apply: "Apply",
    fromDate: "From date",
    toDate: "To date",
    totalRecords: "Total {{count}} records",
    totalReports: "Total {{count}} reports",
    totalLabel: "Total:",
    totalAmount: "Total amount",
    view: "View",
    noData: "No data",
    waiter: "Waiter",
    date: "Date",
    reportDate: "Report date",
    createdAt: "Creation date",
    amount: "Amount",
    fiscalAmount: "Amount FP",
    baseAmount: "Base amount",
    productName: "Product",
    category: "Category",
    station: "Station",
    quantity: "Quantity",
    averagePrice: "Average Price",
    totalProducts: "Total Products",
    totalQuantity: "Total Quantity",
    dateRange: "Date Range",
    total: "Total",

    // BillsHistory
    billsHistoryTitle: "Bills report",
    tables: "Tables",
    table: "Table",
    status: "Status",
    statusOpen: "Open",
    // Singular — the status of ONE bill (row dot, mobile card).
    statusOpenOne: "Open",
    statusClosedOne: "Closed",
    statusClosed: "Closed",
    opened: "Open",
    closed: "Closed",
    duration: "Duration",
    orders: "Orders",
    cancelledItems: "Cancelled",
    cancelledLabel: "Cancelled: {{count}}",
    payment: "Payment",
    paymentMethod: "Payment method",
    noTableData: "No table data",
    hours: "h",
    minutes: "min",
    cash: "Cash",
    card: "Card",
    billsHistoryActivityEmpty: "No activity in selected period",
    billsHistoryResults: "{{count}} results",
    billsHistoryResultOne: "{{count}} result",
    billsHistoryNoResults: "No bills match the selected filters",
    billsHistoryReceiptNumber: "Fiscal receipt",
    billsHistoryClearFilters: "Clear filters",

    // Redesign 0.0.218 — bento layout + shared data table.
    openMenu: "Open menu",
    durationShort: "Time",
    itemsShort: "Items",
    guestsCount: "Guests",
    billIdShort: "Bill ID",
    ordersShort: "Orders",
    cashShort: "Cash",
    cardShort: "Card",
    cancelledCountShort: "Voided qty",
    discount: "Discount",
    receiptNumber: "Receipt #",
    restored: "Restored",
    billsHistoryKpiBills: "Bills",
    billsHistoryKpiBillsUnit: "bills",
    billsHistoryKpiScopeNote:
      "The figures above cover the whole period — the status, payment and search filters change only the list and its Total row.",
    billsHistoryIsolateMarked: "Show only the bills carrying this mark",
    billsHistoryKpiRevenue: "Revenue",
    billsHistoryKpiRevenueSub: "closed bills",
    billsHistoryKpiAvgTicket: "Average bill",
    billsHistoryKpiAvgTime: "Average time at table",
    billsHistoryKpiAvgTimeSub: "per closed bill",
    billsHistoryHourlyTitle: "Bills per hour",
    billsHistoryDailyTitle: "Bills by day",
    billsHistoryDailyPeak: "peak {{day}} · {{count}} bills",
    billsHistoryByHour: "by hour",
    billsHistoryByDay: "by day",
    billsHistoryHourlyHint: "by closing hour",
    billsHistoryHourlyPeak: "by closing hour · peak {{from}}–{{to}} h",
    billsHistoryHourlyBar: "{{hour}}:00 · {{count}} pcs",
    billsHistoryInvoicedOnly: "Invoiced only",
    billsHistoryTableKicker: "Bills",
    billsHistoryTableCount: "{{count}} bills",
    billsHistoryTableCountOne: "{{count}} bill",
    billsHistoryTableHint: "Click a row to open its operations history",
    billsHistorySearchPlaceholder: "Search table",
    billsHistoryEmptyHint:
      "Clear the filters, widen the period or search for another table.",

    // Dashboard 0.0.219 — period-aware bento layout.
    // Voids 0.0.220 — three views over the same rows.
    cardPaymentsRowOne: "{{count}} transaction",
    cardPaymentsRowMany: "{{count}} transactions",
    cardPaymentsListHint: "Click a row to open the bill",
    cardPaymentsEmptyHint: "No card terminal activity for this period.",
    cardPaymentsReconcileHint:
      "End-of-day reconciliation: \"With receipt\" must match the card line in the establishment report, \"With receipt + Pending\" must match the terminal's own end-of-day, and the bank sees only \"With receipt\" (voided ones cancel out).",
    cardPaymentsTerminalsKicker: "Per terminal",
    cardPaymentsTerminalsTitle: "{{count}} terminals",
    voidsViewList: "List",
    voidsByWaiter: "By server",
    voidsByProduct: "By product",
    voidsRowsKicker: "Voided rows",
    voidsInBills: "in {{count}} bills",
    voidsInBillsOne: "in {{count}} bill",
    voidsValue: "Value",
    voidsShareOfGross: "{{share}} % of closed revenue",
    voidsValueSub: "voided value",
    voidsAfterKitchen: "After kitchen",
    voidsRealLoss: "real product loss",
    voidsPerRow: "Average per row",
    voidsPerRowSub: "per voided row",
    voidsRows: "Voided rows",
    voidsTime: "Time",
    voidsCancelledBy: "Voided by",
    voidsScrap: "Scrap",
    voidsScrapShort: "Scrap",
    voidsBillOpenFailed: "The bill could not be opened",
    voidsScrapCount: "{{count}} scrap",
    voidsPlain: "Void",
    voidsSearchPlaceholder: "Table, product, waiter, reason…",
    voidsShareOfRevenue: "% of their turnover",
    voidsRowOne: "{{count}} row",
    voidsRowMany: "{{count}} rows",
    voidsListHint: "Click a row to open the bill",
    voidsWaiterHint: "Who voids how much against their own turnover",
    voidsProductHint: "What gets dropped most often",
    voidsEmptyTitle: "No voids for this period",
    voidsEmptyHint: "That is good news. Change the period or check the filters.",
    voidsBatchHint: "Voided together — {{count}} rows in one action",
    voidsBatchColumn: "Together",
    dashClosed: "Closed",
    dashBillsCount: "{{count}} bills",
    dashBillsCountOne: "{{count}} bill",
    dashAvgCheck: "Average bill",
    dashItemsCount: "{{count}} items",
    dashItemsCountOne: "{{count}} item",
    dashCashCard: "Cash / card",
    dashVoids: "Voided",
    dashVoidsCount: "{{count}} pcs",
    dashDiscounts: "Discounts",
    dashDiscountsSub: "{{count}} bills · {{share}} % of gross",
    dashDiscountsSubOne: "{{count}} bill · {{share}} % of gross",
    dashOpen: "Open",
    dashNoOpenTables: "no open tables",
    dashWaitersTitle: "Revenue by server",
    dashStationsTitle: "By station",
    dashTopTitle: "Top 5 by revenue",
    dashFullReport: "Full report",
    dashAllItems: "All items",
    dashPieces: "{{count}} pcs",
    dashNoData: "No data for this period",
    dashAlertsTitle: "Needs attention",
    dashAlertsNone: "nothing to check in this period",
    invoice: "Invoice",
    priceChangesSearchPlaceholder: "Search by product, table or user",
    priceChangesTableHint: "Click a row to open the operation history",
    priceChangesEmptyHint: "Change the period or clear the column filters.",
    kmRowsKpi: "Rows",
    kmRowsKpiSub: "with tracked transitions",
    kmTableHint: "the period is by “Received”, durations are up to the first transition to the status",
    kmSearchPlaceholder: "Search table, item, operator…",
    kmEmptyHint:
      "Widen the period or check whether the monitors report transitions.",
    billsSplitsWaitersSub: "involved in the splits",
    billsSplitsProductsSub: "{{count}} transferred products",
    billsSplitsAvgSub: "value per split",
    billsSplitsRowOne: "{{count}} split",
    billsSplitsRowMany: "{{count}} splits",
    billsSplitsTableHint: "click a row to see the transferred products",
    billsSplitsSearchPlaceholder: "Search by table, waiter or comment",
    billsSplitsEmptyHint: "Change the period or clear the column filters.",
    billsSplitsComment: "Comment",
    billsSplitsNoItems: "No products recorded for this split",
    categoriesOpenItems: "Open the items in {{name}}",
    itemDaysSubtitle: "Revenue by day",
    itemDaysQuantity: "Quantity",
    itemDaysAvgPrice: "Average price",
    itemDaysDayOne: "{{count}} day with sales",
    itemDaysDaysMany: "{{count}} days with sales",
    itemDaysEmpty: "No sales in the selected period",
    itemDaysFailed: "The daily breakdown failed to load.",
    itemDaysClose: "Close",
    dashLoadFailed: "Could not load the data.",
    dashPaymentMixTitle: "How it was paid",
    dashPaymentMixEmpty: "No closed bills in this period.",
    dashPaymentOther: "Other method",
    dashAlertOpenLong: "Table {{table}} has been open {{time}}",
    dashAlertBigDiscount: "Discount {{percent}} % · table {{table}}",
    dashDaysTitle: "Revenue by working day",
    dashDaysHint: "{{range}} · the selected period is highlighted",
    dashDaysAverage: "average {{amount}}",
    dashThousandShort: "k",
    dashDaysEmpty: "No closed bills for {{range}}",
    periodYesterday: "Yesterday",
    periodToday: "Today",
    period7d: "7 days",
    periodThisMonth: "This month",
    periodPrev: "Previous period",
    periodNext: "Next period",
    shift: "Shift",
    allShifts: "All shifts",
    shiftNumber: "Shift {{number}}",
    shiftsDiffer: "The selected days have different working hours",

    // FiscalIssues
    fiscalIssues: "Fiscal issues",
    fiscalIssuesTitle: "Fiscal issues",
    fiscalIssuesSubtitle: "Bills and stornos without a confirmed fiscal receipt",
    fiscalIssuesListTitle: "Bills without a fiscal receipt",
    fiscalIssuesListSubtitle:
      "Closed bills whose fiscal receipt was not confirmed",
    fiscalIssuesTotal: "Total issues",
    fiscalIssuesFailed: "Rejected by printer",
    fiscalIssuesUnknown: "Unknown outcome",
    fiscalIssuesClosedAt: "Closed at",
    fiscalIssuesStatus: "Status",
    fiscalIssuesError: "Printer error",
    fiscalIssuesRetry: "Retry",
    fiscalIssuesRetrySuccess: "Fiscal receipt issued",
    fiscalIssuesStornoListTitle: "Storno without a document",
    fiscalIssuesStornoListSubtitle:
      "Bills with a fiscal receipt whose storno was not confirmed on paper",
    fiscalIssuesStornoRetrySuccess: "Storno document issued",
    fiscalIssuesReceiptNo: "Receipt no.",
    fiscalIssuesRetryTimeout:
      "The printer did not respond in time — check again in a minute",
    fiscalIssuesRetryError: "The printer returned an error",
    fiscalIssuesEmpty: "No fiscal issues for the selected period.",
    fiscalIssuesDays1: "Last 24 hours",
    fiscalIssuesDays7: "Last 7 days",
    fiscalIssuesDays30: "Last 30 days",
    fiscalStatus_failed: "Rejected",
    fiscalStatus_timeout: "Unknown",
    fiscalStatus_pending: "Waiting",
    fiscalStatusFailedHint:
      "The printer rejected the print — no receipt was issued. Fix the cause and retry.",
    fiscalStatusUnknownHint:
      "The result never reached the server — the document may already have printed. Check the printer's tape before retrying; a retry is safe only from the till the bill was closed on.",
    fiscalIssuesRetryUnknownTitle: "The document may already have printed",
    fiscalIssuesRetryUnknownBody:
      "The outcome is unknown. First check the printer's tape (journal) and \"Receipt no.\" in the bills report — if the document printed, do not retry. \"Retry\" here prints on YOUR session's printer; if that is a different device, a second document will be issued.",
    fiscalIssuesStornoRetryUnknownBody:
      "The outcome is unknown — the storno may have printed or may be paid and waiting on the device. First check the printer's tape (journal): if the storno document printed, do not retry. \"Retry\" sends the storno to the printer it started on, which finishes or prints it.",

    // BillsSplits
    billsSplitsTitle: "Split bills",
    totalSplits: "Total splits",
    transferredProducts: "Transferred products",
    fromTable: "From table",
    toTable: "To table",
    fromTableId: "From table (ID)",
    toTableId: "To table (ID)",
    products: "Products",
    noSplitsData: "No split bills data",
    billsSplitsSummaryKicker: "Splits in period",
    billsSplitsSummaryTitle: "Total count",
    billsSplitsWaiters: "Waiters",
    billsSplitsAmountTitle: "Total amount",
    billsSplitsAvg: "Avg per split",
    billsSplitsActivityKicker: "Activity",
    billsSplitsActivityHourly: "Splits by hour",
    billsSplitsActivityDaily: "Splits by day",
    billsSplitsActivityEmpty: "No activity in the selected period",
    billsSplitsListKicker: "Splits list",

    // Cancellations
    cancellationsTitle: "Cancellations",
    totalCancelled: "Total cancelled",
    defectiveItems: "Defective items",
    billId: "Bill ID",
    selectUsers: "Select users",
    defectiveOnly: "Defective items only",
    noCancellations: "No cancelled products",
    cancellationsSummaryKicker: "Cancellations in period",
    cancellationsSummaryTitle: "Total count",
    cancellationsAmountKicker: "Amount in period",
    cancellationsAmountTitle: "Cancelled value",
    cancellationsAvgLabel: "Avg per item",
    cancellationsActivityKicker: "Activity",
    cancellationsActivityHourly: "Cancellations per hour",
    cancellationsActivityDaily: "Cancellations per day",
    cancellationsActivityEmpty: "No cancellations in the selected period",
    cancellationsListKicker: "Cancellations list",
    cancellationsResults: "{{count}} records",
    cancellationsResultOne: "{{count}} record",
    cancellationsClearFilters: "Clear filters",
    cancellationsDefectiveLabel: "Defective",
    cancellationsRegularLabel: "Cancelled",

    // AssortmentReport
    assortmentProductsKicker: "Items in period",
    assortmentProductsTitle: "Unique products",
    assortmentQuantityKicker: "Sold quantity",
    assortmentQuantityTitle: "Sum across all items",
    assortmentAmountKicker: "Revenue in period",
    assortmentAmountTitle: "Total sales amount",
    assortmentAvgLabel: "Avg per item",
    assortmentListKicker: "Assortment list",
    assortmentResults: "{{count}} items",
    assortmentResultOne: "{{count}} item",
    assortmentClearFilters: "Clear filters",
    assortmentSearchPlaceholder: "Search item",
    assortmentNoItems: "No items in the selected period",
    // Row kind: standalone sale or sold addon
    assortmentColKind: "Type",
    assortmentKindAssortment: "Assortment",
    assortmentKindRecipe: "Recipe",
    assortmentKindAddon: "Addon",
    assortmentKindLunch: "Lunch menu",
    lunchTag: "Lunch",
    lunchScopeAll: "All",
    lunchScopeRegular: "Without lunch",
    lunchScopeLunch: "Lunch only",
    lunchScopeLabel: "Lunch menu",
    categoriesKindRegular: "Regular menu",
    bill: "Bill",
    product: "Product",
    qty: "Qty",
    unitPrice: "Unit price",
    comment: "Comment",
    defective: "✓ Defective",
    cancelled: "Cancelled",

    // PersonalReportsPage
    allUsers: "All users",
    noReportsFound: "No reports found",
    reportNumber: "No.",
    user: "User",
    bills: "Bills",
    cashAmount: "Cash",
    cardAmount: "Card",
    details: "Details",

    // DiscountsReport
    clientsTitle: "Clients",
    discountsBillId: "Bill",
    discountsClient: "Client",
    discountsColType: "Type",
    discountTag: "Discount",
    discountsType: "Discount type",
    discountsPercent: "Percent",
    discountsAmount: "Adjustment",
    discountsPaid: "Paid",
    discountsBillsCount: "Bills with discount",
    discountsTotalDiscount: "Total discount",
    discountsTotalPaid: "Total paid",
    discountsTotalOriginal: "Total amount",
    discountsNoData: "No discount data",
    discountTypeFixedPercent: "Fixed percent",
    discountTypeCashback: "Cashback",
    discountTypeBillAmount: "Whole bill",
    surchargeTag: "Surcharge",
    discountsBillsKicker: "Bills in period",
    discountsBillsTitle: "With discount or surcharge",
    discountsAmountKicker: "Discounts",
    discountsAmountTitle: "Total discounts given",
    discountsAmountAvgLabel: "Avg per bill",
    surchargesKicker: "Surcharges",
    surchargesTitle: "Total surcharges added",
    surchargesCountLabel: "Bills count",
    discountsPaidKicker: "Paid amount",
    discountsPaidTitle: "After adjustments",
    discountsPaidSharedLabel: "Share of total",
    discountsListKicker: "Discounts & surcharges list",
    discountsResults: "{{count}} bills",
    discountsResultOne: "{{count}} bill",
    discountsClearFilters: "Clear filters",

    // WaitersReport - Waiters report
    waitersTitle: "Waiters Report",
    methodCountColumn: "{{method}} · count",
    waitersCashCount: "Cash Count",
    waitersCardCount: "Card Count",
    waitersMixedCount: "Mixed Count",
    waitersTotalCash: "Total Cash",
    waitersTotalCard: "Total Card",
    waitersNoData: "No waiter data",
    waitersDetailTitle: "Details by date",
    waitersRevenueKicker: "Revenue in period",
    waitersRevenueTitle: "Total amount",
    waitersRevenueAvgLabel: "Avg per waiter",
    waitersSplitKicker: "Split",
    waitersSplitTitle: "By payment method",
    waitersListKicker: "Waiters list",
    waitersResults: "{{count}} waiters",
    waitersResultOne: "{{count}} waiter",
    waitersClearFilters: "Clear filters",

    // StationsReport - Stations report
    stationsTitle: "Stations Report",
    stationsName: "Station",
    stationsCashCount: "Cash Count",
    stationsCardCount: "Card Count",
    stationsMixedCount: "Mixed Count",
    stationsTotalCash: "Total Cash",
    stationsTotalCard: "Total Card",
    stationsRevenueKicker: "Revenue in period",
    stationsRevenueTitle: "Total amount",
    stationsRevenueAvgLabel: "Avg per station",
    stationsSplitKicker: "Split",
    stationsSplitTitle: "By payment method",
    stationsListKicker: "Stations list",
    stationsResults: "{{count}} stations",
    stationsResultOne: "{{count}} station",
    stationsClearFilters: "Clear filters",

    // Personal reports list
    personalTitle: "Personal reports",
    personalRevenueKicker: "Revenue in period",
    personalRevenueTitle: "Total amount",
    personalRevenueAvgLabel: "Avg per report",
    personalSplitKicker: "Split",
    personalSplitTitle: "By payment method",
    personalListKicker: "Reports list",
    personalResults: "{{count}} reports",
    personalResultOne: "{{count}} report",
    personalClearFilters: "Clear filters",
    personalBillsLabel: "Bills",

    // EstablishmentReportsPage
    personalReportsCount: "Personal reports",
    establishmentTitle: "Establishment report",
    establishmentRevenueKicker: "Revenue in period",
    establishmentRevenueTitle: "Total amount",
    establishmentRevenueAvgLabel: "Avg per report",
    establishmentSplitKicker: "Split",
    establishmentSplitTitle: "By payment method",
    establishmentListKicker: "Reports list",
    establishmentResults: "{{count}} reports",
    establishmentResultOne: "{{count}} report",
    billOpsBillKicker: "Bill",
    billOpsCancelStockFailedTitle: "The cancellation was not made",
    billOpsCancelStockFailedText:
      "The warehouse could not return the goods ({{reason}}). Nothing was printed — try again or contact support.",
    billOpsCardNoReceipt: "NO RECEIPT — do not charge again",
    billOpsCardRefunded: "refunded on void:",
    billOpsCardSection: "Card captures",
    billOpsCardVoided: "refunded to the card",
    billOpsCardWithReceipt: "with receipt",
    billOpsClient: "Customer: {{name}}",
    billOpsClose: "Close",
    billOpsDefective: "waste",
    billOpsDuration: "Duration {{value}}",
    billOpsEmpty: "No operations",
    billOpsEventAddItems: "Items added",
    billOpsEventCancel: "Void",
    billOpsEventClose: "Closed",
    billOpsEventPreliminaryBill: "Pre-bill printed",
    billOpsEventDiscountBill: "Bill discount",
    billOpsEventDiscountBillRemoved: "Bill discount removed",
    billOpsEventAmountDiscount: "Whole-bill discount",
    billOpsEventAmountDiscountRemoved: "Whole-bill discount removed",
    billOpsEventDiscountItem: "Item discount",
    billOpsEventDiscountItemRemoved: "Item discount removed",
    billOpsEventOpen: "Opened",
    billOpsEventPayment: "Payment",
    billOpsEventPriceChange: "Price changed",
    billOpsEventPriceRestored: "Menu price restored",
    billOpsEventRestore: "Bill restored",
    billOpsKitchenHeldMinutes: "Held for the kitchen: {{minutes}} min",
    billOpsKitchenHeldManual: "Held for the kitchen: manual",
    billOpsKitchenStillHeld: "not released yet",
    billOpsKitchenReleasedManual: "released manually",
    billOpsKitchenReleasedManualBy: "released manually by {{name}}",
    billOpsKitchenReleasedTimer: "released automatically",
    billOpsKitchenReleasedClose: "released automatically when the bill was closed",
    billOpsKitchenReleasedEdge: "released automatically by the cash register — offline",
    billOpsRestoreReceipt: "Receipt #{{number}} reversed",
    billOpsRestoreReport: "Excluded from personal report #{{number}}",
    billOpsRestoreReportBoth:
      "Excluded from personal report #{{number}} and establishment report #{{establishment}}",
    billOpsRestoredTimes_one: "restored {{count}} time",
    billOpsRestoredTimes_other: "restored {{count}} times",
    billOpsEventSplitIn: "Received from another bill",
    billOpsEventSplitOut: "Bill split",
    billOpsEventSurchargeBill: "Bill surcharge",
    billOpsEventSurchargeBillRemoved: "Bill surcharge removed",
    billOpsEventTransfer: "Guests moved",
    billOpsFullTransfer: "The whole bill #{{id}} was transferred",
    billOpsPaidCard: "Card",
    billOpsPaidCash: "Cash",
    billOpsReason: "Reason: {{reason}}",
    billOpsSectionOperations: "Operations",
    billOpsSector: "Sector: {{name}}",
    billOpsStatusClosed: "closed",
    billOpsStatusOpen: "open",
    billOpsTotalCancelled: "Voided",
    billOpsTotalDiscount: "Discount",
    billOpsTotalItems: "Items",
    billOpsTotalPaid: "Paid",
    billOpsTotalSurcharge: "Surcharge",
    billOpsTotalTransferred: "Transferred",
    categoriesCountKicker: "Groups",
    categoriesItemsSub: "units sold",
    categoriesTableCount: "{{count}} groups",
    categoriesTableCountOne: "{{count}} group",
    categoriesGroupColumn: "Group",
    categoriesTableHint: "click the name to see its items",
    dailyBillsMany: "{{count}} bills",
    dailyBillsOne: "{{count}} bill",
    dailyColTotal: "Total",
    dailyEmptyHint: "Change the period or check whether the reports have been issued.",
    dailyKpiReports: "Reports",
    dailyKpiReportsSub: "personal",
    dailyKpiRevenue: "Revenue",
    dailyListHint: "click a row to open the report",
    dailyOther: "Other",
    dailyReportsTitle: "Daily reports",
    dailySearchPlaceholder: "Search number or waiter",
    dailyTabEstablishment: "Establishment",
    dailyTabPersonal: "Personal",
    dailyWorkDay: "Working day",
    discountsBefore: "Before",
    discountsBillOpenFailed: "The bill could not be opened",
    discountsEmptyHint: "Change the period or clear the column filters.",
    discountsGiven: "Discount",
    discountsAdjustmentColumn: "Discount / surcharge",
    discountsKind: "Kind",
    discountsKpiAboveApproval: "Over {{percent}} %",
    discountsKpiAboveApprovalIsolated: "showing only these",
    discountsKpiAboveApprovalSub: "bills · need approval",
    discountsKpiAverage: "Average discount",
    discountsKpiAverageSub: "per bill",
    discountsKpiBills: "Bills with a discount",
    discountsKpiBillsShare: "{{share}} % of all bills",
    discountsKpiBillsUnit: "bills in the period",
    discountsKpiGiven: "Discounts given",
    discountsKpiGivenShare: "{{share}} % of gross",
    discountsKpiGivenSub: "for the period",
    discountsKpiSurcharges: "Surcharges",
    discountsKpiSurchargesNone: "none in this period",
    discountsSearchPlaceholder: "Search by bill, waiter or client",
    discountsTableHint: "Click a row to open the operation history",
    discountsTime: "Time",
    itemsAvgKicker: "Average per item",
    itemsAvgSub: "revenue / units",
    itemsCategoryFilterChip: "Category: {{name}}",
    itemsCategoryFilterClear: "Clear the category filter",
    itemsColCount: "Units",
    itemsColPrice: "Price",
    itemsColRevenue: "Revenue",
    itemsColShare: "Share",
    itemsEmptyHint: "Change the period or clear the column filters.",
    itemsRevenueKicker: "Item revenue",
    itemsRevenueSub: "as actually paid",
    itemsSoldKicker: "Units sold",
    itemsTableHint: "click a row to see its days",
    itemsTableKicker: "Items",
    itemsUniqueKicker: "Unique items",
    itemsUniqueSub: "with sales",
    stationsBillsColumn: "Bills",
    stationsCountKpi: "Stations",
    stationsCountKpiSub: "with revenue in the period",
    stationsDetailClose: "Close",
    stationsEmptyHint: "No closed bills with station items in the selected period.",
    stationsEmptyTitle: "No station revenue",
    stationsPageTitle: "Stations report",
    stationsRevenueColumn: "Revenue",
    stationsRevenueKpi: "Item revenue",
    stationsRevenueKpiSub: "from closed bills, after discounts",
    stationsRowMany: "{{count}} stations",
    stationsRowOne: "{{count}} station",
    stationsSearchPlaceholder: "Search station…",
    stationsShareColumn: "Share",
    stationsWaiters: "Waiters",
    stationsAllWaiters: "All waiters",
    stationsVoidsColumn: "Voids",
    stationsVoidsCount: "{{count}} pcs",
    stationsVoidsKpi: "Voids by station",
    voidsBatchChip: "×{{count}} together",
    waitersAvgBill: "Avg bill",
    waitersBillsMany: "{{count}} bills",
    waitersBillsOne: "{{count}} bill",
    waitersDayMany: "{{count}} days",
    waitersDayOne: "{{count}} day",
    waitersDaysHint: "Split by payment method",
    waitersDaysKicker: "By day",
    waitersDetailSubtitle: "Revenue by day for the selected period",
    waitersEmptyHint: "Change the period or check whether the bills are closed.",
    waitersEmptyTitle: "No closed bills in this period",
    waitersKicker: "Waiters",
    waitersOnShift: "{{count}} on shift",
    waitersRevenue: "Revenue",
    waitersRowHint: "Click opens the day-by-day detail",
    waitersSearchPlaceholder: "Search waiter…",
    waitersShare: "Share",
    dailyKpiReportsEstablishmentSub: "establishment",
    establishmentPreparedBy: "Prepared by",
    establishmentSearchPlaceholder: "Search number or author",
    invoicesColStatus: "Status",
    invoicesColType: "Type",
    invoicesEmptyHint: "Change the period or clear the search.",
    invoicesGrandKpi: "Total",
    invoicesGrandKpiSub: "incl. VAT",
    invoicesStatusCancelled: "Cancelled",
    invoicesStatusIssued: "Issued",
    invoicesTableHint: "click opens the invoice",
    invoicesTaxableKpi: "Taxable base",
    invoicesTaxableKpiSub: "excl. VAT",
    invoicesVatKpi: "VAT",
    invoicesVatKpiSub: "charged tax",
    salesPeriodsReport: "Custom reports",
    salesPeriodsPageTitle: "Custom reports",
    salesPeriodsMode_grouped: "Grouped",
    salesPeriodsMode_timeline: "Over time",
    salesPeriodsUnit_hour: "Hours",
    salesPeriodsUnit_weekday: "Weekdays",
    salesPeriodsUnit_month: "Months",
    salesPeriodsUnit_date: "Dates",
    salesPeriodsUnit_year: "Years",
    salesPeriodsAllUsers: "All users",
    salesPeriodsUsers: "Users",
    salesPeriodsAllClients: "All clients",
    salesPeriodsClients: "Clients",
    salesPeriodsWithoutClient: "No client",
    salesPeriodsAllItems: "All items",
    salesPeriodsItems: "Items",
    salesPeriodsClearFilters: "Clear filters",
    salesPeriodsChartHint: "one bar = one table row",
    salesPeriodsTableKicker: "Periods",
    salesPeriodsRowOne: "{{count}} period",
    salesPeriodsRowMany: "{{count}} periods",
    salesPeriodsTableHint: "click a row to open its days",
    salesPeriodsSearchPlaceholder: "Search period…",
    salesPeriodsEmptyTitle: "No sales in this period",
    salesPeriodsEmptyHint: "Change the period or remove the filters.",
    salesPeriodsPeriodColumn: "Period",
    salesPeriodsBillsColumn: "Bills",
    salesPeriodsQuantityColumn: "Assortment count",
    salesPeriodsQuantityShort: "items",
    salesPeriodsRevenueColumn: "Amount",
    salesPeriodsVatColumn: "VAT",
    salesPeriodsNetColumn: "Net",
    salesPeriodsAvgPriceColumn: "Avg. price",
    salesPeriodsAvgBillColumn: "Average bill",
    salesPeriodsAvgBillShort: "avg.",
    salesPeriodsClientsColumn: "Clients count",
    salesPeriodsClientRevenueColumn: "Clients amount",
    salesPeriodsClientAvgColumn: "Clients average amount",
    salesPeriodsFiscalizedColumn: "Amount FP",
    salesPeriodsBaseColumn: "At base prices",
    salesPeriodsDiscountColumn: "Discounts",
    salesPeriodsMarkupColumn: "Surcharges",
    salesPeriodsDetailSubtitle: "{{bills}} bills · {{amount}}",
    // Custom reports templates (salesByPeriods/templates/)
    salesPeriodsTemplates: "Templates",
    salesPeriodsTemplateNew: "Save as template",
    salesPeriodsTemplateDiscard: "Discard",
    salesPeriodsTemplateModified: "Modified",
    salesPeriodsTemplateRename: "Rename",
    salesPeriodsTemplateDelete: "Delete",
    salesPeriodsTemplateDeleteTitle: "Delete template",
    salesPeriodsTemplateDeleteDescription:
      "The template \"{{name}}\" will disappear for everyone in the restaurant. The report keeps its current settings.",
    salesPeriodsTemplateCreateTitle: "New template",
    salesPeriodsTemplateCreateSubtitle:
      "Shared by the restaurant — everyone with access to the report sees it.",
    salesPeriodsTemplateRenameTitle: "Rename template",
    salesPeriodsTemplateRenameSubtitle: "Everyone in the restaurant sees the new name.",
    salesPeriodsTemplateNameLabel: "Name",
    salesPeriodsTemplateNamePlaceholder: "e.g. Coffee by hour",
    salesPeriodsTemplateRemembers: "The template will remember",
    salesPeriodsTemplateGrouping: "Grouping",
    salesPeriodsTemplateFilters: "Filters",
    salesPeriodsTemplateNoFilters: "no filters",
    salesPeriodsTemplateColumns: "Columns",
    salesPeriodsTemplateColumnsValue: "{{visible}} of {{total}}",
    salesPeriodsTemplateColumnFiltersOne: "{{count}} column filter",
    salesPeriodsTemplateColumnFiltersMany: "{{count}} column filters",
    salesPeriodsTemplatePeriod: "Period",
    salesPeriodsTemplatePeriodNone: "not saved — no button selected",
    salesPeriodsTemplateFiltersOne: "{{count}} filter",
    salesPeriodsTemplateFiltersMany: "{{count}} filters",
    salesPeriodsTemplateSaved: "Template \"{{name}}\" saved",
    salesPeriodsTemplateUpdated: "Changes saved to \"{{name}}\"",
    salesPeriodsTemplateRenamed: "Template renamed",
    salesPeriodsTemplateDeleted: "Template deleted",
    salesPeriodsTemplateSaveError: "The template was not saved. Try again.",
    methodAmountColumn: "{{method}} (as paid)",

    // --- Reservation reports (reservations/) ---
    reservationsReports: "Reservations",
    rvJournal: "Reservations log",
    rvOccupancy: "Occupancy",
    rvGuests: "Guests",
    rvStatus_upcoming: "Upcoming",
    rvStatus_seated: "Seated",
    rvStatus_completed: "Completed",
    rvStatus_cancelled: "Cancelled",
    rvStatus_expired: "No-show",
    rvKpiReservations: "Reservations",
    rvKpiReservationsSub: "upcoming {{upcoming}} · seated {{seated}}",
    rvKpiGuests: "Guests",
    rvKpiGuestsSub: "excluding cancelled and no-shows",
    rvKpiAverageParty: "{{value}} per reservation on average",
    rvKpiCompletedSub: "guests came and left",
    rvKpiCancelledSub: "cancelled before the time",
    rvKpiNoShowNone: "no no-shows",
    rvKpiNoShowRate: "{{value}} % no-show rate",
    rvKpiRevenue: "Revenue",
    rvKpiRevenueSub: "from the bills of seated guests",
    rvColumnTime: "Time",
    rvColumnPhone: "Phone",
    rvColumnGuests: "Guests",
    rvColumnTables: "Tables",
    rvColumnStatus: "Status",
    rvColumnName: "Name",
    rvColumnDuration: "Duration",
    rvColumnSeatedAt: "Seated at",
    rvColumnReleasedAt: "Left at",
    rvColumnAvgDuration: "Average duration",
    rvExportDurationMinutes: "Duration (min)",
    rvExportAvgDurationMinutes: "Average duration (min)",
    rvAllStatuses: "All statuses",
    rvStatuses: "Statuses",
    rvAllTables: "All tables",
    rvChartTitle: "Reservations by period",
    rvChartAverage: "average {{value}}",
    rvChartEmpty: "No reservations for the selected period and filters.",
    rvKpiPerPeriod: "Average per {{unit}}",
    rvKpiPerPeriodSub: "{{count}} periods in the table",
    rvPerUnit_hour: "hour",
    rvPerUnit_weekday: "weekday",
    rvPerUnit_month: "month",
    rvPerUnit_date: "day",
    rvPerUnit_year: "year",
    rvColumnStay: "Actual stay",
    rvColumnBill: "Bill",
    rvColumnCreatedBy: "Created by",
    rvColumnSeatedBy: "Seated by",
    rvSeatedByNoOrder: "No order yet",
    rvSeatedByNoOrderClosed: "No order",
    rvColumnNote: "Note",
    rvColumnCreatedAt: "Created at",
    rvColumnAvgStay: "Average actual stay",
    rvColumnNoShowRate: "No-show rate",
    rvColumnLastVisit: "Last visit",
    rvColumnLastReservation: "Last reservation",
    rvColumnFirstReservation: "First reservation",
    rvExportEnd: "End",
    rvExportStayMinutes: "Actual stay (min)",
    rvExportBillName: "Bill table",
    rvExportAvgStayMinutes: "Average actual stay (min)",
    rvJournalKicker: "Reservations by time",
    rvReservationOne: "{{count}} reservation",
    rvReservations: "{{count}} reservations",
    rvJournalHint: "the period is by reservation time",
    rvJournalSearchPlaceholder: "Search guest, phone, table…",
    rvNoData: "No reservations in the selected period",
    rvEmptyHint: "Widen the period or check whether the Reservations module is in use.",
    rvKpiGuestsCount: "Guests",
    rvKpiGuestsCountSub: "distinct phone numbers",
    rvKpiReservationsOfGuestsSub: "total in the period",
    rvKpiReturning: "Returning",
    rvKpiReturningSub: "with more than one reservation",
    rvKpiWithNoShow: "With no-shows",
    rvKpiWithNoShowSub: "at least one no-show",
    rvGuestsKicker: "Guests by phone",
    rvGuestOne: "{{count}} guest",
    rvGuestsMany: "{{count}} guests",
    rvGuestsHint: "the name comes from the guest's latest reservation",
    rvGuestsSearchPlaceholder: "Search name or phone…",
    rvGuestVisitsPeriod: "period {{period}}",
    rvGuestVisitsKpiVisits: "Visits",
    rvGuestVisitsOfReservationOne: "of {{count}} reservation",
    rvGuestVisitsOfReservationsMany: "of {{count}} reservations",
    rvGuestVisitsKpiAverageBill: "Average bill",
    rvGuestVisitsKpiAverageStay: "Average stay",
    rvGuestVisitsKpiCompletedSub: "over completed visits",
    rvGuestVisitsKpiRevenue: "Total spent",
    rvGuestVisitsKpiRevenueSub: "all bills in the period",
    rvGuestVisitsKpiCancelled: "Cancelled",
    rvGuestVisitsKpiNoShow: "No-shows",
    rvGuestVisitsKpiNoShowSub: "booked but did not come",
    rvGuestVisitsListTitle: "Reservations in the period",
    rvGuestVisitsEmpty: "This guest has no reservations in the selected period.",
    rvGuestVisitsFailed: "The guest history did not load. Try again.",
  },

  restoreBill: {
    title: "Restore bill",
    total: "Total: {{count}}",
    range30: "30 min",
    range60: "60 min",
    range120: "2 hours",
    noBills: "No closed bills in the selected period",
    restore: "Restore",
    fiscalize: "Fiscalize",
    changePaymentMethod: "Change payment",
    success: "Bill restored successfully",
    error: "Error restoring bill",
    cardWarning:
      "This bill was paid by card (RRN {{rrn}}). The storno prints as CASH — hand the amount back to the guest in cash from the till, not through the terminal.",
    cardWarningNoRrn:
      "This bill was paid by card. The storno prints as CASH — hand the amount back to the guest in cash from the till, not through the terminal.",
    cardRefundPlaceholder: "RRN, only if exceptionally refunded through the terminal (optional)",
  },

  language: {
    bg: "Български",
    en: "English",
  },

  changelog: {
    kicker: "VERSIONS",
    title: "Changelog",
    subtitle:
      "All versions, new features, fixes and improvements to the system.",
    currentVersion: "Current version",
    latest: "Latest",
    legend: "Change types",
    typeFeature: "New feature",
    typeFix: "Fix",
    typeImprovement: "Improvement",
    typeBreaking: "Breaking change",
    typeSecurity: "Security",
  },

  monitoring: {
    title: "Monitoring",
    subtitle: "Service access — audit, fiscal issues and AI assistant",
    auditLog: "Action audit log",
    auditLogDesc:
      "Records of every operation in the system — who changed what and when.",
    fiscalIssues: "Fiscal issues",
    fiscalIssuesDesc:
      "Bills and stornos without a confirmed fiscal receipt — review and retry.",
    changelog: "Changelog",
    changelogDesc:
      "All versions, new features, improvements and fixes to the system — test organization only.",
    assistant: "AI assistant",
    assistantDesc:
      "Questions about setup and problems — answered from the knowledge base and this restaurant's data.",
    assistantSubtitle: "Knowledge base and live restaurant data",
    assistantPlaceholder: "How do I… / Why doesn't…  (Enter — ask, Shift+Enter — new line)",
    assistantPlaceholderShort: "How do I… / Why doesn't…",
    assistantSend: "Ask",
    assistantNewConversation: "New conversation",
    assistantThinking: "Searching the knowledge base and data… {{seconds}} s",
    assistantEmptyTitle: "What should I check?",
    assistantEmpty:
      "Ask about a setting, a symptom, or a specific person, product, station or device.",
    assistantExample1: "Why can't Ivan see “Void”?",
    assistantExample2: "Why doesn't station Bar print?",
    assistantExample3: "Which bills today have no fiscal receipt?",
    confidenceConfirmed: "Confirmed",
    confidenceLikely: "Likely, unconfirmed",
    confidenceUnknown: "Don't know",
    confidenceClarify: "Needs clarification",
    assistantStatsSummary: "{{seconds}} s · {{rounds}} steps",
    assistantStatsCost: "~${{cost}}",
    assistantStatsTokens:
      "Input {{tokensIn}} (cache {{cacheRead}}) · output {{tokensOut}} · {{model}}",
    assistantStatsTools: "Tools and sections",
    assistantHelpful: "Was this helpful?",
    assistantVoteUp: "Helpful",
    assistantVoteDown: "Not helpful",
    assistantCommentPlaceholder:
      "What is wrong / what is the correct answer? (optional)",
    assistantCommentSend: "Send",
    assistantFeedbackSaved: "Feedback saved.",
    assistantFeedbackError: "Feedback was not saved.",
    assistantNotRecorded: "The answer was not recorded — feedback is not possible.",
    assistantErrNotConfigured:
      "The AI assistant is not configured on the server (OpenAI key missing).",
    assistantErrExpired: "The conversation expired — starting a new one. Please ask again.",
    assistantErrTimeout: "The answer took too long — ask a more specific question.",
    assistantErrUpstream: "OpenAI did not respond — try again in a moment.",
    assistantErrForbidden: "Only the UnrealSoft service account has access.",
    assistantErrGeneric: "The question failed — please try again.",
  },

  auditLog: {
    title: "Activity log",
    subtitle: "Who did what, when — across every module.",
    searchPlaceholder: "Search across all fields…",
    noResults: "No records found",
    emptyHint:
      "Widen the period or drop one of the filters. Reads (GET) stay hidden until you turn them on.",
    tableKicker: "LOG",
    tableCount: "{{count}} records",
    tableCountOne: "{{count}} record",
    tableHint: "click opens the full record",
    statusClass: {
      "2xx": "Successful (2xx)",
      "4xx": "Client error (4xx)",
      "5xx": "Server error (5xx)",
    },
    col: {
      timestamp: "Time",
      outcome: "Outcome",
      operation: "Action",
      module: "Module",
      impact: "Kind",
      table: "Table",
      billId: "Bill",
      user: "User",
      userId: "User id",
      method: "Method",
      status: "Status",
      duration: "Duration",
      path: "URL",
      entryId: "Record id",
    },
    module: {
      ordering: "Marking",
      cash: "Cash register",
      reports: "Reports",
      invoicing: "Invoices",
      inventory: "Inventory",
      configuration: "Configuration",
      access: "Access",
      system: "System",
    },
    impact: {
      money: "Money",
      settings: "Settings",
      data: "Data",
      read: "Read",
    },
    outcome: {
      ok: "Successful",
      failed: "Receipt was not printed",
      rejected: "Rejected",
      error: "Server error",
    },
    quick: {
      problems: "Problems only",
      money: "Money",
      settings: "Settings",
      slow: "Slower than {{ms}} ms",
      reads: "Include reads (GET)",
      live: "Live",
    },
    stats: {
      actions: "ACTIONS",
      actionsSub: "changes: {{writes}} · reads: {{reads}}",
      problems: "PROBLEMS",
      problemsSub: "failed or rejected",
      fiscalFailedSub: "incl. {{count}} unprinted receipts",
      fiscalFailedSubOne: "incl. 1 unprinted receipt",
      money: "MONEY",
      moneySub: "payment, void, discount, cash",
      settings: "SETTINGS",
      settingsSub: "configuration and devices",
      people: "PEOPLE",
      lastAt: "last at {{when}}",
      slowest: "SLOWEST",
      avg: "avg {{value}}",
      byHour: "BY HOUR",
      byDay: "BY DAY",
      barsHint: "click narrows the period",
      barTitle: "{{when}} · actions: {{count}} · problems: {{problems}}",
    },
    breakdown: {
      byModule: "BY MODULE",
      byUser: "BY USER",
      clickHint: "click narrows the list",
      anonymous: "No user",
    },
    trace: {
      kicker: "TRACE",
      billTitle: "Bill #{{id}} · table {{table}}",
      tableTitle: "Table {{name}}",
      billHint: "Trace bill #{{id}}",
      tableHint: "Trace table {{name}}",
      count: "{{count}} records",
      countOne: "{{count}} record",
      periodOff: "period is off — the whole life is shown",
      exit: "Exit",
    },
  },

  auditLogDetail: {
    request: "Request",
    response: "Response",
    headers: "Headers",
    query: "Query string",
    body: "Body",
    empty: "None",
    getNoBody: "GET requests have no body — data lives in the query string",
    copy: "Copy",
    copied: "Copied!",
    togglePretty: "Pretty-print",
    toggleRaw: "Single line",
    loadError: "Failed to load details",
    truncatedTitle: "Payload was too large",
    truncatedBody:
      "Body was {{size}} KB. We didn't store the whole thing to avoid bloating the database (limit: 10 KB).",
    truncatedBodyUnknown:
      "Body exceeded the limit (10 KB) and wasn't stored in full.",
    human: {
      deposit: "Deposit",
      rawTitle: "Technical details (raw record)",
      summaryKicker: "What happened",
      who: "User",
      when: "Time",
      table: "Table",
      noDetails:
        "This operation has no detailed human view — see the technical details below.",
      total: "Total",
      product: "Product",
      products: "Products",
      unknownProduct: "Unknown product",
      method: "Method",
      modifier: "Modifier",
      discount: "Discount",
      percent: "Percent",
      client: "Client",
      discountType: "Discount type",
      removedDiscountNote: "The discount was removed from the bill.",
      cancelled: "Cancelled",
      reason: "Reason",
      defective: "Defective",
      yes: "Yes",
      no: "No",
      cancelledAllNote: "The entire bill was voided.",
      paymentMethod: "Payment method",
      cash: "Cash",
      card: "Card",
      paidTotal: "Amount paid",
      addedAmount: "Amount added",
      restoredNote: "The closed bill was reopened (same id).",
      depositAmount: "Deposit amount",
      depositAttachedNote: "The deposit was attached to the bill — it is deducted when the bill is closed.",
      depositDetachedNote: "The deposit was removed from the bill — it is free again.",
      kitchenHold: "On hold",
      kitchenHoldMinutes: "{{count}} min",
      kitchenHoldManual: "until released",
      kitchenHoldRows_one: "{{count}} of {{total}} items",
      kitchenHoldRows_other: "{{count}} of {{total}} items",
      kitchenOrder: "Order",
      kitchenHeldAt: "Held at",
      kitchenReleasedAt: "Released at",
      kitchenReleasedNote:
        "The held order was released — it went to the kitchen (monitors and kitchen ticket).",
      kitchenNewTime: "New time",
      kitchenEntersAt: "Enters at",
      kitchenHoldChangedNote:
        "The time of the held order was changed — the new one counts from the change; it has not reached the kitchen yet.",
      targetTable: "To table",
      movedItems: "Items moved",
      movedProducts: "Moved products",
      newName: "New name",
      waiter: "Waiter",
      cashIn: "Deposited",
      cashOut: "Withdrawn",
      successLabel: "Success",
      failedLabel: "Failed",
      prevOp: "Previous operation on this bill",
      nextOp: "Next operation on this bill",
      timelineTitle: "Bill timeline",
      billLabel: "Bill",
      operationsWord: "operations",
      flagDiscountReverted: "Discount applied and then removed",
      flagCancelAfterClose: "Void after the bill was closed",
      flagFailed: "The operation failed",
      // Bill: removed line discount and price changes
      removedItemDiscountNote: "The discount on this product was removed.",
      newPrices: "New prices",
      itemDiscounts: "Item discounts",
      additionLine: "addon",
      // Warehouse
      recipe: "Recipe",
      documentNo: "Document",
      warehouse: "Warehouse",
      fromWarehouse: "From warehouse",
      toWarehouse: "To warehouse",
      supplier: "Supplier",
      invoiceNumber: "Invoice no.",
      deliveryValue: "Delivery value",
      writeoffKind: "Type",
      writeoffKindScrap: "Scrap",
      writeoffKindNormal: "Write-off",
      substitutions: "Substitutions",
      countedProducts: "Counted products",
      postedProducts: "Posted products",
      reportNo: "Report",
      hint: "Hint",
      coefficient: "Coefficient",
      groupMembers: "Products in the group",
      vatRegistered: "VAT registered",
      defaultWarehouse: "Default warehouse",
      productWarehouse: "The product's own warehouse",
      productWarehouseRemovedNote:
        "The product's own warehouse is removed — from now on the station's warehouse decides.",
      restoredDocument: "Restored document",
      deliveryNotes: "Delivery notes",
      closeReason: "Reason",
      markedPaid: "Marked as paid",
      detachedNotesNote: "The delivery notes were taken out of their invoice; their stock stays received.",
      closedNotesNote: "The delivery notes were closed without an invoice — no invoice is awaited for them any more.",
      reopenedNotesNote: "The delivery notes are awaiting an invoice again.",
      restoredDocumentNote: "A cancelled warehouse document was restored.",
      cancelledDocumentNote: "The document was cancelled.",
      deletedGroupNote: "The interchangeable products group was deleted permanently.",
      truncatedBodyNote:
        "The request body was too large to store — open the document in Warehouse.",
      // Configuration, printing, devices, voice
      newValue: "New value",
      turnedOn: "On",
      turnedOff: "Off",
      invoiceNo: "Invoice",
      result: "Result",
      errorLabel: "Error",
      requestId: "Request id",
      device: "Device",
      deviceUpdateNote:
        "A command was sent for the register to update to the latest FiscalAPI version.",
      fiscalRetryNote: "Retry of the fiscal receipt.",
      stornoRetryNote: "Retry of the credit note.",
      recognizedProducts: "Recognised products",
      voiceAmbiguous: "ambiguous, candidates",
      voiceNoMatch: "no match in the menu",
      // Reservations
      reservationGuest: "Guest",
      reservationTables: "Tables",
      reservationTime: "Time",
      reservationGuests: "Guests",
      reservationDuration: "Duration",
      reservationBill: "Bill",
      reservationBillNew: "No. {{id}} — new empty bill",
      reservationBillAttached: "No. {{id}} — attached to an open bill",
      reservationBills: "Bills",
      reservationBillsSeparate: "{{ids}} — a separate bill for each table",
      reservationReleasedNote:
        "The guests left — the reservation is completed; the bill stays open until it is closed in Ordering.",
      reservationUnseatedNote:
        "Seating undone — the reservation is upcoming again; the bill was not touched.",
      reservationUnseatedBillClosedNote:
        "Seating undone — the reservation is upcoming again; the empty bill opened by the seating (no orders) was removed.",
      reservationCancelledNote: "The reservation was cancelled — the tables are free.",
      reservationRestoredNote: "The reservation is back in upcoming.",
      reservationOrigin: "Source",
      reservationOriginEdge: "The till, without internet — uploaded at sync",
    },
  },

  push: {
    menuLabel: "Notifications",
    settingsTitle: "Notifications",
    settingsSubtitle:
      "Kitchen notifications when orders are cooked or served out.",
    toggleLabel: "Receive notifications on this device",
    unsupportedHint:
      "This browser does not support notifications. On iPhone the app must first be installed on the home screen (Share → “Add to Home Screen”).",
    permissionDeniedHint:
      "Notifications are blocked by the browser for this site. Allow them in the browser's site settings and try again.",
    enableError: "Could not enable notifications. Please try again.",
    disableError: "Could not disable notifications. Please try again.",
    deviceTitle: "Device notifications",
    deviceSubtitle: "Who receives notifications on this terminal.",
    deviceEmpty: "No one is subscribed to notifications on this device.",
    remove: "Remove",
    removeError: "Removal failed. Please try again.",
  },
  // Changing your own code and password from the profile menu
  // (components/profile/ChangeCredentialsModal.tsx, BE router_credentials.py)
  credentials: {
    menuLabel: "Change code or password",
    menuLabelCode: "Change code",
    menuLabelPassword: "Change password",
    modal: {
      title: "Change code or password",
      titleCode: "Change code",
      titlePassword: "Change password",
      subtitleCode: "The code you use to sign in at the till.",
      subtitlePassword: "The password you use to sign in to the system.",
      tabCode: "Code",
      tabPassword: "Password",
      currentCode: "Current code",
      newCode: "New code",
      confirmCode: "Confirm the new code",
      currentPassword: "Current password",
      newPassword: "New password",
      confirmPassword: "Confirm the new password",
      currentCodeRequired: "Enter your current code",
      newCodeRequired: "Enter a new code",
      confirmCodeRequired: "Repeat the new code",
      currentPasswordRequired: "Enter your current password",
      newPasswordRequired: "Enter a new password",
      confirmPasswordRequired: "Repeat the new password",
      passwordMin: "At least 3 characters",
      passwordLatin: "Use Latin characters only",
      codeMismatch: "The codes do not match",
      passwordMismatch: "The passwords do not match",
      codeSaved: "Your code has been changed",
      passwordSaved: "Your password has been changed",
      saveError: "The change failed. Please try again.",
    },
  },
  // Local fallback — working against the main till without internet (docs/edge.md)
  edge: {
    bannerTitle: "Local mode",
    bannerText: "no internet, you are working against the till",
    bannerSyncing:
      "internet is back — waiting for the till to send what was done offline",
    bannerRestricted: "log in with a code or a password",
    bannerUnresolved_one:
      "{{count}} action has not reached the cloud — check the till",
    bannerUnresolved_other:
      "{{count}} actions have not reached the cloud — check the till",
    restrictedRequest: "Local mode — log in with a code or a password",
    returnedToPin:
      "Internet is back and work continues against the cloud — log in again with your code.",
    loginWithPassword: "Log in with a username and password",
    unavailableOffline: "Unavailable without internet",
    panelKicker: "Offline fallback",
    panelTitle: "Local fallback",
    stateReady: "Ready",
    stateUnreachable: "No access",
    stateDisabled: "Disabled",
    stateUnknown: "Not checked",
    stateNoMaster: "Till not connected",
    stateLocalNow: "In local mode",
    // The "Offline fallback" panel has two parts (Configuration → Devices):
    // the switch is for the WHOLE site, preparation is per device.
    sectionSite: "The site",
    sectionDevice: "This device",
    stateEnabled: "Enabled",
    siteEnabledHint:
      "If the internet drops, every prepared terminal and monitor continues against the main till.",
    siteOnceHint:
      "There is one fallback for the whole site — it is switched on once, from any terminal. Switching it off also applies to all.",
    deviceNeedsSite:
      "First enable the fallback for the site — then prepare each device separately.",
    disableTitle: "Switch off the offline fallback",
    disableQuestion:
      "The fallback is switched off for the WHOLE site — if the internet drops, every terminal stops, not just this one. Switch it off?",
    disableConfirm: "Yes, switch off for the site",
    compactSummary: "Enabled for the site · this device is ready to work without internet",
    showDetails: "Details",
    hideDetails: "Hide",
    hintReady:
      "The browser reaches the main till at {{address}}. If the internet drops, ordering, kitchen tickets and closing continue against it.",
    hintUnreachable:
      "The browser cannot reach the till at {{address}}. Allow local network access (Chrome/Edge), make sure FiscalAPI on the main till has the fallback enabled, and press “Check”.",
    hintPublicAddress:
      "The till has announced a public address ({{address}}) — the browser does not allow a connection to such an address from another device. Without internet only the browser on the till's own computer will work.",
    thisDeviceIsTill: "This device is the till — connected via {{address}}",
    hintNoMaster:
      "No FiscalAPI installation announces a fallback. Enable it in the main till's portal and wait for it to announce itself.",
    hintDisabled:
      "If the internet drops, the terminals stop. Enable the fallback so ordering continues against the main till.",
    hintUnknown:
      "Not yet checked whether the browser reaches the till — press “Check”.",
    hintLocalNow:
      "This terminal is currently working against the till. It returns to the cloud automatically once the internet is back and the till has sent its journal.",
    hintUnresolved_one:
      "The till is holding {{count}} action the cloud refused — it goes up only after a person acts at the till (“Try again” or “Write off” in its portal). Until then the terminals stay in local mode so that nothing done offline is lost.",
    hintUnresolved_other:
      "The till is holding {{count}} actions the cloud refused — they go up only after a person acts at the till (“Try again” or “Write off” in its portal). Until then the terminals stay in local mode so that nothing done offline is lost.",
    cloudModeLocal:
      "The cloud considers the restaurant “in local mode” — closing is possible only from the till until it sends its journal.",
    forceCloudHint:
      "“Return to cloud” only if the till is down and has nothing to send — otherwise what was done offline will arrive late.",
    // Readiness of the SITE, not of the till: the fallback is switched on once,
    // but local network access is granted on every device separately.
    clientsSeen_one: "{{count}} device has used it in the last 24 h",
    clientsSeen_other: "{{count}} devices have used it in the last 24 h",
    clientsSeenNone:
      "No device has used it in the last 24 h — if the internet drops, all of them go quiet. Prepare every terminal and monitor from the button on its own screen.",
    presenceStale: "The till is silent — last seen at {{time}}.",
    pendingEvents_one:
      "The till is holding {{count}} event it has not sent yet — it goes up on its own.",
    pendingEvents_other:
      "The till is holding {{count}} events it has not sent yet — they go up on their own.",
    // The button that raises the browser prompt (it only comes on a human
    // action) and pulls in one go what the screen will need without internet.
    // Shown next to the browser prompt when it comes up on its own at the
    // first touch (`lib/edge/edgePermission.ts`): a refusal is final, and
    // Chrome's own text does not say why this device needs the till.
    lnaPrompt:
      "The browser is asking for access to the till on the local network — press “Allow” so this device keeps working without internet.",
    lnaDenied:
      "Access to the till on the local network is denied on this device — allow it from the icon to the left of the address, otherwise the screen goes quiet without internet.",
    prepareDevice: "Prepare this device",
    prepareDeviceHint:
      "Asks for local network access and downloads the data this screen will use without internet.",
    preparing: "Preparing…",
    prepareReady: "Done — this device will work offline.",
    prepareBlocked:
      "The browser refused local network access — allow it from the icon to the left of the address and press again.",
    prepareUnreachable:
      "The till does not respond at {{address}} — check that FiscalAPI is running and that its address is correct (the till's portal → Local mode), then press again.",
    preparePublicAddress:
      "The till has announced a public address ({{address}}) — the browser does not allow a connection to such an address. Without internet it will only work on the till's computer: press “Prepare this device” there.",
    prepareDisabled:
      "The local fallback is switched off for this site — enable it in Configuration → Devices.",
    prepareNoMaster:
      "The till has not announced an address yet — enable the fallback in its portal and try again.",
    prepareLocalNow:
      "This screen is already working against the till — there is nothing to prepare.",
    masterClientId: "Main till",
    address: "Address",
    check: "Check",
    checking: "Checking…",
    enable: "Enable",
    disable: "Disable",
    forceCloud: "Return to cloud",
    updateError: "The setting was not saved",
    // Titles ("kicker") of the banner — one per state. They live here too, not
    // only as a `defaultValue` in the component: otherwise an English screen
    // shows Bulgarian in the middle of the sentence.
    linkTitleSlow: "Slow connection",
    linkTitleSuspect: "Checking the connection",
    linkTitleDeviceOffline: "Only this device",
    linkTitleAppDown: "The cloud is updating",
    linkSlow: "The connection is slow — hold on, the request is still running.",
    linkSuspect: "Checking the connection to the cloud…",
    linkDeviceOffline:
      "Only this device is offline — the till is online. Check its Wi-Fi; the others are working normally.",
    linkAppDown:
      "The cloud is not answering, but the network is fine — it is most likely updating. Please wait.",
    linkLocal: "Local mode — working against the till.",
    tillBackTerminalBlind:
      "The till is back on the cloud, but this device still has no internet — new actions will be refused. Check the Wi-Fi on this terminal.",
    linkLostNoFallback:
      "No connection to the cloud, and the local fallback is off for this venue — turn it on from Configuration → Devices.",
    forceLocal: "Switch to local mode",
    forceLocalHint:
      "Freezes the WHOLE venue against the till — every terminal starts working against it. Use it only when the venue is stuck and the automatics have not switched.",
    forceLocalQuestion:
      "The whole venue switches to working against the till. Switch now?",
    forceLocalConfirm: "Yes, switch to local mode",
    forceLocalEntered: "Done — the terminals are working against the till.",
    forceLocalNoPermission:
      "You are not allowed to switch the mode — call the person responsible for the till.",
    forceLocalDisabled:
      "The local fallback is off for this venue — turn it on from Configuration → Devices.",
    forceLocalNoTill: "The till has not announced an address — there is nothing to switch to.",
    forceLocalFailed:
      "The till did not take over — check that FiscalAPI on the main till is running and try again.",
    // The till is HELD in local mode by a manual decision (§19.5).
    bannerManualHold: "the till is in local mode by a manual decision",
    bannerManualHoldCloudBack:
      "internet is back, but the till is held in local mode by hand — return it to the cloud",
    releaseLocal: "Return to the cloud",
    releaseLocalHint:
      "The till sends what was done offline and the whole venue returns to the cloud. Do it only if the internet works on the terminals.",
    releaseLocalQuestion: "Return the venue to the cloud now?",
    releaseLocalConfirm: "Yes, return to the cloud",
    releaseLocalFailed:
      "Returning did not work. Try again or do it from the till.",
  },
  lunchMenu: {
    title: "Lunch menu",
    tag: "Lunch",
    backToTables: "Back to tables",
    kickerToday: "Today",
    kickerUpcoming: "Upcoming",
    kickerPast: "Past",
    statusLive: "Active now",
    statusToday: "Later today",
    statusTomorrow: "Tomorrow",
    statusUpcoming: "Upcoming",
    statusRecurring: "Recurring",
    statusPast: "Expired",
    newMenu: "New lunch menu",
    edit: "Edit",
    copy: "Copy",
    print: "Print",
    printSetup: {
      title: "Print lunch menu",
      settings: "Print settings",
      hideSettings: "Hide settings",
      fontSize: "Font size",
      subtitle: "Shared restaurant template · changes appear immediately in the A4 preview",
      close: "Close",
      content: "Content",
      showVenue: "Restaurant name",
      showTitle: "Heading",
      showDate: "Date and days",
      showHours: "Hours",
      titleText: "Heading text",
      typography: "Fonts and sizes",
      headerAlignment: "Alignment",
      alignLeft: "Left",
      alignCenter: "Center",
      alignRight: "Right",
      titleFont: "Heading font",
      venueFont: "Restaurant font",
      bodyFont: "Product font",
      fontSans: "Modern",
      fontSerif: "Classic",
      fontMono: "Monospaced",
      titleSize: "Heading",
      venueSize: "Restaurant",
      categorySize: "Categories",
      itemSize: "Products and prices",
      colors: "Colors",
      venueColor: "Restaurant",
      titleColor: "Heading",
      metaColor: "Date and time",
      categoryColor: "Categories",
      itemColor: "Products",
      priceColor: "Prices",
      spacing: "Spacing",
      topMargin: "Top and bottom",
      sideMargin: "Left and right",
      sectionSpacing: "Between sections",
      rowSpacing: "Within product rows",
      preview: "Preview · A4",
      pageNavigation: "Menu pages",
      previousPage: "Previous page",
      nextPage: "Next page",
      pageSelector: "Page",
      pageProducts: "Page {{page}} · products: {{count}} of {{total}}",
      reset: "Default look",
      unsaved: "Unsaved changes",
      saveDefault: "Save for restaurant",
      saving: "Saving…",
      saved: "Print template saved",
      saveError: "Could not save the print template",
      serverUnavailable: "This server version does not support the shared print template yet. You can adjust and print this menu, but saving for the restaurant is unavailable.",
      settingsUnavailable: "The shared print template could not be loaded. You can adjust and print this menu, but saving for the restaurant is temporarily unavailable.",
      retry: "Try again",
      printNow: "Print this layout",
    },
    finishNow: "End now",
    delete: "Delete",
    colProduct: "Product",
    colSize: "Size",
    colPortions: "Portions/day",
    colMain: "Regular",
    colLunch: "Lunch",
    changedSizeHint: "Struck through is the product value, next to it — the lunch one. The product itself is unchanged.",
    createdBy: "Created by {{name}}",
    productsCount_one: "{{count}} product",
    productsCount_other: "{{count}} products",
    pickMenu: "Pick a menu from the list.",
    missingProduct: "The product is no longer on the menu",
    everyWeekdays: "every {{days}}",
    everyDay: "every day",
    periodOpenEnded: "from {{from}}",
    defaultName: "Lunch · {{day}} · {{date}}",
    creatingTitle: "Creating a lunch menu",
    editingTitle: "Editing a lunch menu",
    emptyTitle: "No lunch menus yet",
    emptyText: "Create the first one — pick products from the catalog, give them a lunch price and choose when it shows in Ordering.",
    weekdayShort0: "Sun",
    weekdayShort1: "Mon",
    weekdayShort2: "Tue",
    weekdayShort3: "Wed",
    weekdayShort4: "Thu",
    weekdayShort5: "Fri",
    weekdayShort6: "Sat",
    weekdayLong0: "Sunday",
    weekdayLong1: "Monday",
    weekdayLong2: "Tuesday",
    weekdayLong3: "Wednesday",
    weekdayLong4: "Thursday",
    weekdayLong5: "Friday",
    weekdayLong6: "Saturday",
    dayButton0: "Su",
    dayButton1: "Mo",
    dayButton2: "Tu",
    dayButton3: "We",
    dayButton4: "Th",
    dayButton5: "Fr",
    dayButton6: "Sa",
    confirmDeleteTitle: "Delete lunch menu",
    confirmDeleteMessage: "“{{name}}” will disappear from the list. Its sales stay in the reports.",
    confirmFinishTitle: "End lunch menu",
    confirmFinishMessage: "“{{name}}” stops right away and moves to “Past”.",
    confirmFinish: "End",
    saved: "Lunch menu saved",
    deleted: "Lunch menu deleted",
    finished: "Lunch menu ended",
    loadError: "The lunch menu could not be loaded",
    editorKicker: "Lunch menu",
    products: "Products",
    priceColumnsHint: "lunch / regular price",
    emptySelection: "Pick products from the catalog on the left.",
    emptySelectionNarrow: "Pick products from the catalog.",
    save: "Save",
    reviewAndSave: "Review and save",
    scheduleChange: "Change",
    scheduleSingleDate: "single date",
    scheduleTitle: "When it is active",
    scheduleSubtitle: "Outside this period the menu is hidden in Ordering.",
    name: "Name",
    modeSingle: "Single date",
    modeRecurring: "Recurring",
    date: "Date",
    today: "Today",
    tomorrow: "Tomorrow",
    weekdays: "Days of the week",
    period: "Period",
    periodFrom: "from",
    periodTo: "to",
    noEndDate: "no end date",
    hours: "Hours",
    done: "Done",
    errorName: "Enter a menu name.",
    errorItems: "Pick at least one product.",
    errorTime: "The start time must be before the end time.",
    errorWeekdays: "Pick at least one day of the week.",
    errorPeriod: "The end date is before the start date.",
    errorPastDate: "The date must be today or later.",
    errorFarDate: "The date can be at most 2 years ahead.",
    priceTooHigh: "At most {{max}}",
    reorderEnded: "The lunch menu has ended — this product can't be reordered from it.",
    orderEnded: "The lunch menu has ended — its products switched to the regular price. Check the order and send it again.",
    recipeSizeFixed: "You work with a warehouse — the quantity is the recipe's and cannot change. The lunch menu changes only the price.",
    cartSwitchedToRegular_one: "The lunch menu has ended — 1 product in the order now has the regular price.",
    cartSwitchedToRegular_other: "The lunch menu has ended — {{count}} products in the order now have the regular price.",
    modalClosedEnded: "The lunch menu has ended — the lunch product window was closed.",
    cartRemoved_one: "1 lunch product is no longer in the menu and was removed from the order.",
    cartRemoved_other: "{{count}} lunch products are no longer in the menu and were removed from the order.",
    errorPrice: "Check the prices — one is empty or invalid.",
    dailyLimitTitle: "Portions per day",
    dailyLimitInvalid: "A whole number from 0 to {{max}}",
    dailyLimitHint: "0 = no limit · e.g. 20 → after 20 sold in a day it shows “Sold out”",
    dailyLimitShort: "{{count}}/day",
    dailyLimitUnlimited: "No limit",
    errorDailyLimit: "Check the portions per day — a whole number from 0 to 9,999.",
    countLeft: "{{count}} left",
    tagSoldOut: "Sold out",
    remainingTitle_one: "{{count}} portion left today",
    remainingTitle_other: "{{count}} portions left today",
    limitSoldOut: "“{{name}}” from the lunch menu is sold out for today.",
    limitOnlyLeft_one: "“{{name}}” — only {{count}} portion left today from the lunch menu.",
    limitOnlyLeft_other: "“{{name}}” — only {{count}} portions left today from the lunch menu.",
    limitReachedOrder: "Not enough lunch portions: {{items}}. The count is refreshed — lower the quantity.",
    limitReachedItem: "“{{name}}” ({{count}} left)",
    lunchPrice: "Lunch price",
    mainPriceSubtitle: "regular price {{price}}",
    resetPrice: "Restore the regular price",
    mainPriceShort: "regular",
    sizeTitle: "Unit and size",
    byLanguage: "per language",
    inAssortment: "product: {{value}}",
    fromAssortment: "empty → from the product",
    sizeScopeHint: "The change applies to this lunch menu only — the product is not touched.",
    remove: "Remove",
    apply: "Apply",
    overlapTitle: "Another lunch menu covers this period",
    overlapMessage: "“{{name}}” ({{schedule}}) overlaps with the new one. Only one lunch menu can be active at a time.",
    finishOld: "End the old one",
    finishOldHint: "The old one stays valid until {{endDate}} inclusive, because the new one starts on {{startDate}} and the two can’t be active at the same time. It then moves to “Past”.",
    finishOldNowHint: "It stops right away and stays under “Past” — the new one applies from now.",
    deleteOld: "Delete the old one",
    deleteOldHint: "It disappears from the list. Its sales stay in the reports.",
    finishAndSave: "End and save",
    deleteAndSave: "Delete and save",
    printPricesNote: "Prices are in euro, VAT included.",
    printAllergensTitle: "Allergens",
  },
  // "Seat" from Ordering (src/pages/ordering/components/tables/seat/).
  seatReservation: {
    action: "Seat",
    title: "Seat a reservation",
    lateShort: "−{{duration}}",
    soonShort: "in {{duration}}",
    now: "now",
    tables_one: "Table {{tables}}",
    tables_other: "Tables {{tables}}",
    emptyHint: "Today's reservations will appear here.",
    noResults: "No reservations match the search",
    loadError: "The reservations could not be loaded",
    retry: "Try again",
    noTable: "The reservation's tables were removed from the layout",
    expired: "The reservation's time is over",
    notUpcoming: "The reservation is no longer upcoming",
    seat: "Seat",
    takenBy: "Table {{table}} is taken by {{waiter}}",
    takenByOther: "Table {{table}} is taken by another waiter",
    // The "where to seat" question — one template for every case (useSeatChoiceText.ts).
    choice: {
      title: "Seat {{guest}}?",
      pickBill: "Which bill",
      pickBillOnTable: "Which bill on table {{table}}",
      outcome: "What happens",
      opens: "Will open",
      open: "Open",
      newTable: "New table {{table}}",
      // "One bill" in schema mode — before the tables "14 | 13 | 12" (JoinedTableNames).
      newTableJoined: "New table",
      tableJoined: "Table",
      onWaiter: "by {{waiter}}",
      // The disabled "Seat" while none of several options is chosen.
      pickTable: "Choose a bill",
    },
    // The bills of a reservation with several tables (SeatBillsSection.tsx); the
    // other texts are shared with "Reservations" (reservations.seatBills).
    bills: {
      label: "How to keep the bills",
      singleSummary: "All tables on one bill",
    },
    success: "{{guest}} — seated at table {{table}}",
    successSeparate: "{{guest}} — seated at tables {{tables}}, a separate bill for each table",
  },
  // "Reservations" module (src/pages/reservations/).
  reservations: {
    title: "Reservations",
    counts: {
      reservations_one: "{{count}} reservation",
      reservations_other: "{{count}} reservations",
      guests_one: "{{count}} guest",
      guests_other: "{{count}} guests",
      seats_one: "{{count}} seat",
      seats_other: "{{count}} seats",
    },
    duration: {
      minutes: "{{minutes}} min",
      hours: "{{hours}} h",
      hoursMinutes: "{{hours}} h {{minutes}} min",
    },
    day: {
      today: "Today, {{date}}",
      tomorrow: "Tomorrow, {{date}}",
      yesterday: "Yesterday, {{date}}",
      previous: "Previous day",
      next: "Next day",
      pick: "Pick a date",
    },
    newReservation: "New reservation",
    timeline: {
      now: "Now",
      preview: "Preview at",
      earlier: "Earlier",
      backToNow: "Now",
      slider: "Time of day",
      outside: "Outside {{from}}–{{to}}",
      inFuture: "in {{duration}}",
      inPast: "{{duration}} ago",
    },
    legend: {
      free: "Free",
      reserved: "Reserved",
      occupied: "Occupied",
      nextReservation: "Time of the next reservation",
      fits: "Fits the party",
      selected: "Selected",
      blocked: "Taken at this time",
    },
    table: {
      name: "Table {{name}}",
      hover: "Table {{name}} · {{seats}}",
      hoverReserved: "reserved {{time}} · {{guest}}",
      hoverOccupied: "occupied {{duration}}",
      hoverNext: "next reservation {{time}}",
    },
    popover: {
      close: "Close",
      status: {
        free: "Free",
        reserved: "Reserved",
        occupied: "Occupied",
      },
      late: "Late by {{duration}}",
      arriving: "Arrives in {{duration}}",
      seatedAt: "Seated at {{time}} · {{duration}}",
      finished: "Finished reservation",
      reservedMeanwhile: "Reservation at this time",
      occupiedSince: "Occupied · since {{time}} · {{duration}}",
      freeUntil: "Free until {{time}}",
      freeAllDay: "Free until the end of the day",
      next: "Next reservation",
      nextDetails_one: "{{time}} · {{guest}}, {{count}} guest",
      nextDetails_other: "{{time}} · {{guest}}, {{count}} guests",
      seat: "Seat the guests",
      cancel: "Cancel reservation",
      release: "Guests have left",
      reserveTable: "Reserve this table",
      seatBlockedOccupied_one: "Table {{tables}} is occupied by a seated reservation",
      seatBlockedOccupied_other: "Tables {{tables}} are occupied by a seated reservation",
      seatBlockedEarly: "Can be seated after {{time}}",
      seatBlockedPreviousBill: "The bill of the previous reservation on table {{table}} ({{waiter}}) is not closed yet",
      seatBlockedPreviousBillNoWaiter: "The bill of the previous reservation on table {{table}} is not closed yet",
      seatAttachHint: "Will be attached to {{waiter}}'s open bill",
      seatAttachHintNoWaiter: "Will be attached to the table's open bill",
      seatPickHint: "The table has several open bills — you will choose the waiter",
      edit: "Edit",
      unseat: "Undo seating",
      restore: "Restore to upcoming",
    },
    confirmCancel: {
      title: "Cancel this reservation?",
      content_one: "{{guest}} · {{time}} · Table {{tables}}. The table becomes free for this time.",
      content_other: "{{guest}} · {{time}} · Tables {{tables}}. The tables become free for this time.",
      ok: "Cancel reservation",
      back: "Back",
    },
    confirmUnseat: {
      title: "Undo the seating?",
      content_one: "{{guest}} · Table {{tables}}. The reservation goes back to “Upcoming”. The bill stays in Ordering.",
      content_other: "{{guest}} · Tables {{tables}}. The reservation goes back to “Upcoming”. The bill stays in Ordering.",
      contentBillCloses_one: "{{guest}} · Table {{tables}}. The reservation goes back to “Upcoming”. The empty bill on table {{billTable}} will be removed — nobody has ordered yet.",
      contentBillCloses_other: "{{guest}} · Tables {{tables}}. The reservation goes back to “Upcoming”. The empty bill on table {{billTable}} will be removed — nobody has ordered yet.",
      contentBillsClose_one: "{{guest}} · Table {{tables}}. The reservation goes back to “Upcoming”. The empty bills on tables {{billTables}} will be removed — nobody has ordered yet.",
      contentBillsClose_other: "{{guest}} · Tables {{tables}}. The reservation goes back to “Upcoming”. The empty bills on tables {{billTables}} will be removed — nobody has ordered yet.",
      ok: "Undo seating",
      back: "Back",
    },
    seatBills: {
      group: "The reservation's bills",
      single: "One bill",
      singlePick: "The tables have several open bills — choose which one",
      separate: "A separate bill for each table",
      separateSummary_one: "{{count}} bill of the reservation",
      separateSummary_other: "{{count}} bills of the reservation — one per table",
      separateLocalMode: "Online only — the till opens one bill",
      // "Separate…" with several bills on a table (seatBills.ts `separatePlanOf`, orderingSeatBills.ts).
      pickOnTable: "Table {{table}} has several open bills — choose which one",
      noBillOfWaiter: "Table {{table}} has several open bills and none of them is {{waiter}}'s",
      noOwnBill: "Table {{table}} has several open bills and none of them is yours",
      confirm: "Seat",
      back: "Back",
    },
    seatPicker: {
      title: "Which waiter should seat them?",
      subtitle_one: "{{guest}} · Table {{tables}}. The table has several open bills — the reservation will be attached to the chosen one.",
      subtitle_other: "{{guest}} · Tables {{tables}}. The tables have several open bills — the reservation will be attached to the chosen one.",
      unknownWaiter: "Unknown waiter",
      table: "Table {{name}}",
      openedAgo: "opened {{duration}} ago",
      openedJustNow: "opened just now",
      close: "Back",
    },
    messages: {
      seated: "Guests seated — the table's bill is open",
      seatedNewBill: "Guests seated. The table will belong to the waiter who places the first order",
      seatedAttached: "Guests seated with {{waiter}}",
      seatedAttachedNoWaiter: "Guests seated — the reservation is attached to the open bill",
      seatedSeparate: "Guests seated — a separate bill for each table ({{tables}})",
      unseated: "Seating undone",
      unseatedBillClosed: "Seating undone. The empty bill on table {{table}} was removed",
      unseatedBillsClosed: "Seating undone. The empty bills on tables {{tables}} were removed",
      unseatedBillClosedNoTable: "Seating undone. The empty bill was removed",
      restored: "The reservation is back in “Upcoming”",
      restoredExpired: "Its time has passed — the reservation stays in “Past” as “Not fulfilled”",
      released: "The reservation is completed",
      releasedBillOpen: "The reservation is completed. The table stays occupied until the bill is closed in Ordering",
      cancelled: "Reservation cancelled",
      seatUnavailable: "The reservation can no longer be seated",
      actionFailed: "The action could not be completed",
    },
    offlineHint: "Reservations work only online — the till has no layout or reservations.",
    offlineStaleHint: "The till has no fresh data for this day — pick a day near today or wait for the internet.",
    dataAsOf: "data as of {{time}}",
    loadError: "Reservations could not be loaded",
    loadErrorHint: "Check the connection and try again in a moment.",
    noSchema: {
      title: "No restaurant layout yet",
      description: "Draw the rooms and tables in Reservations → Floor plan — reservations are made on it.",
    },
    noTables: {
      title: "No tables yet",
      description:
        "Reservations are made on the tables of the floor plan. Arrange the rooms and tables there — then you will see them here too.",
      action: "Go to the floor plan",
    },
    noTablesInRegion: {
      title: "This area has no tables yet",
      description: "Arrange its tables on the floor plan — then you will see them here too.",
    },
    regionAll: "All",
    emptyRegion: "This area has no tables yet.",
    list: {
      tabs: {
        upcoming: "Upcoming",
        seated: "Seated",
        past: "Past",
      },
      search: "Name, phone or table",
      clearSearch: "Clear search",
      seat: "Seat",
      release: "Guests left",
      tableInRegion: "{{table}} · {{region}}",
      late: "Late {{duration}}",
      soon: "In {{duration}}",
      seatedUntil: "Seated {{time}} – {{until}}",
      completed: "Completed",
      cancelled: "Cancelled",
      expired: "Not fulfilled",
      empty: {
        upcoming: "No upcoming reservations for the day",
        seated: "No seated guests",
        past: "No past reservations yet",
      },
      noMatches: "No reservations match the search",
      noTable: "No table",
      cancel: "Cancel",
      restore: "Restore",
      edit: "Edit reservation",
      unseat: "Undo seating",
      moment: {
        tabHasActive: "Has reservations at the tables at {{time}}",
      },
    },
    form: {
      title: "New reservation",
      editTitle: "Edit reservation",
      close: "Close",
      when: "When",
      today: "Today",
      tomorrow: "Tomorrow",
      otherDate: "Other date…",
      guests: "Guests",
      fewerGuests: "Fewer guests",
      moreGuests: "More guests",
      duration: "Duration",
      tables: "Tables",
      suggest: "Suggest a table",
      noSuggestion_one: "No free table for {{count}} guest at this time",
      noSuggestion_other: "No free table for {{count}} guests at this time",
      tablesEmpty: "Click a free table on the layout. You can pick several for a larger group.",
      tableChip: "Table {{name}} · {{seats}}",
      removeTable: "Remove table {{name}}",
      seatsEnough: "{{seats}} for {{guests}}",
      seatsShort: "Not enough seats: {{seats}} for {{guests}}",
      freeInRegion: "Free tables for {{guests}} in “{{region}}” · {{time}}. Pick one or more.",
      noFreeInRegion: "No free table for {{guests}} here — try another area or show all tables.",
      showAllTables: "Show all tables ({{count}})",
      showOfferedOnly: "Show free tables only",
      regionHasSelected: "Has a selected table",
      region: "Area",
      freeCount_one: "{{count}} free",
      freeCount_other: "{{count}} free",
      tablesBusy: "Taken at this time: {{names}} — pick another table",
      guest: "Guest",
      phone: "Phone",
      name: "Name",
      note: "Note — occasion, allergies, high chair…",
      cancel: "Cancel",
      create: "Create reservation",
      created: "The reservation was created",
      createFailed: "The reservation was not created",
      save: "Save changes",
      saved: "Changes saved",
      saveFailed: "The changes were not saved",
      dayLoading: "Loading table availability for the day…",
      dayError: "Table availability for the day could not be loaded",
      retry: "Try again",
      slotPast: "The selected time has passed — pick another",
      dayClosed: "Day off — no reservations",
      slotOutsideHours: "The time is outside working hours — pick another",
      afterMidnightHint: "The hours after midnight ({{from}}–{{to}}) are on {{day}}",
      errors: {
        startAt: "Invalid reservation date or time",
        durationMinutes: "The duration must be between 30 and 480 minutes",
        guests: "The number of guests must be between 1 and 500",
        guestName: "Enter a name (up to 100 characters)",
        phone: "Invalid phone number",
        nameRequired: "Enter a name",
        phoneInvalid: "Invalid phone number",
        note: "Invalid note",
        tableIds: "Select at least one table",
      },
      // Known phone: the chip in the "Phone" field and the history card.
      guestHistory: {
        title: "History",
        show: "Show the guest's history",
        hide: "Hide the guest's history",
        misses: "Has cancelled or no-show reservations",
        reservations_one: "{{count}} reservation",
        reservations_other: "{{count}} reservations",
        visits_one: "{{count}} visit",
        visits_other: "{{count}} visits",
        lastVisit: "Last visit",
        averageBill: "Average bill",
        expired_one: "{{count}} no-show",
        expired_other: "{{count}} no-shows",
        cancelled_one: "{{count}} cancelled",
        cancelled_other: "{{count}} cancelled",
        upcoming_one: "{{count}} upcoming",
        upcoming_other: "{{count}} upcoming",
        seated_one: "{{count}} seated now",
        seated_other: "{{count}} seated now",
        lastNote: "Last note",
      },
    },
    picker: {
      badgeSelected: "Selected",
      badgeBusy: "Taken",
      hoverBusy: "taken at this time",
      hoverSelected: "selected",
      hoverFits: "fits the guests",
    },
  },
  reservationsTable: {
    title: "Reservations table",
    table: "Table",
    search: "Search by table or guest…",
    noMatch: "No tables match this search.",
    hideUntil: "Hide until the 1st reservation",
    hideUntilHint: "Hides the hours before the first upcoming or seated reservation",
    showWholeDay: "Show the whole day",
    hourLoadHint: "Reservations | guests in this hour",
    newAt: "New reservation — {{table}}, {{time}}",
    now: "now {{time}}",
    onlyUpcomingEditable: "Only an upcoming reservation can be edited",
    legend: {
      expired: "No-show",
      completed: "Completed",
    },
  },
  // Sections of the „Инфо" window (ordering/components/menu/ItemDetailModal)
  itemInfo: {
    allergens: "Allergens",
  },
  // The countdown in the license modal (components/license)
  licenseCountdown: {
    hours: "{{value}} h",
    minutes: "{{value}} min",
  },
};

export default en;
